import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';

import { IconButton, Stack, TextField, Tooltip, Chip, Switch, FormControlLabel, Slider, Divider, Popover, ListItemButton, ListItemText, Checkbox, InputAdornment, Snackbar, Alert, CircularProgress, Skeleton, LinearProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import WifiIcon from "@mui/icons-material/Wifi";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import TimelineIcon from "@mui/icons-material/Timeline";
import GestureIcon from "@mui/icons-material/Gesture";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import PanToolIcon from "@mui/icons-material/PanTool";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import NumbersIcon from "@mui/icons-material/Numbers";

import DeleteIcon from '@mui/icons-material/Delete';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import NearMeIcon from '@mui/icons-material/NearMe';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';

import axios from '../axios';
import cameraModelComboClientConfig from '../config/cameraModelComboClientConfig.json';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTheme, alpha } from '@mui/material/styles';

const API_BASE = (process.env.REACT_APP_API || '').replace(/\/$/, '');
const apiUrl = (path) => `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

// Detection defaults for every newly configured model and combo class. Also the
// fallbacks when a saved config predates one of these fields.
const DEFAULT_CONF = 0.2;
const DEFAULT_IOU = 0.45;
const DEFAULT_DETECT_FPS = 2;
const DEFAULT_MOTION_SENSITIVITY = 1;

function TimeInput24({ value, onChange, borderColor, bgColor, textColor }) {
  const parts = (value || "").split(":");
  const h = parts[0] ?? "";
  const m = parts[1] ?? "";
  const baseStyle = {
    width: 24, textAlign: "center", border: "none", outline: "none",
    background: "transparent", fontSize: 11, color: textColor || "inherit",
    padding: 0, fontFamily: "inherit",
  };
  const clamp = (v, min, max) => Math.max(min, Math.min(max, parseInt(v) || 0));
  return (
    <Box sx={{ display: "flex", alignItems: "center", px: "6px", py: "3px", border: `1px solid ${borderColor}`, borderRadius: "4px", background: bgColor, flex: 1 }}>
      <input
        type="text" inputMode="numeric" maxLength={2} value={h}
        onChange={e => onChange(`${e.target.value}:${m}`)}
        onBlur={() => onChange(`${String(clamp(h, 0, 23)).padStart(2, "0")}:${m}`)}
        style={baseStyle}
      />
      <span style={{ fontSize: 11, color: textColor || "inherit", userSelect: "none", padding: "0 1px" }}>:</span>
      <input
        type="text" inputMode="numeric" maxLength={2} value={m}
        onChange={e => onChange(`${h}:${e.target.value}`)}
        onBlur={() => onChange(`${h}:${String(clamp(m, 0, 59)).padStart(2, "0")}`)}
        style={baseStyle}
      />
    </Box>
  );
}

export default function CameraModelComboConfig() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const UI = {
    bg: theme.palette.background.default,
    panel: theme.palette.background.paper,
    panelSolid: theme.palette.background.paper,
    surface: theme.palette.background.neutral,
    border: alpha(theme.palette.text.primary, isDark ? 0.16 : 0.2),
    textMuted: theme.palette.text.secondary,
    textMain: theme.palette.text.primary,
    accent: theme.palette.primary.main,
    accentSoft: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.12),
    dark: theme.palette.primary.main,
    darkContrast: theme.palette.primary.contrastText,
    darkSoft: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08),
    green: theme.palette.success.main,
    greenSoft: alpha(theme.palette.success.main, isDark ? 0.16 : 0.1),
    shadowSm: "none",
    shadowMd: "none",
    inputBg: alpha(theme.palette.text.primary, isDark ? 0.14 : 0.04),
    hoverBg: alpha(theme.palette.text.primary, isDark ? 0.22 : 0.08),
    subtleIcon: isDark ? "#D1D5DB" : "#6B7280",
    scrollThumb: alpha(theme.palette.text.primary, isDark ? 0.18 : 0.3),
    scrollThumbHover: alpha(theme.palette.text.primary, isDark ? 0.28 : 0.45),
    gridBg: isDark ? alpha(theme.palette.common.black, 0.08) : alpha(theme.palette.common.black, 0.01),
    gridLine: isDark ? alpha(theme.palette.common.white, 0.08) : alpha(theme.palette.common.black, 0.08),
    overlayBg: alpha(theme.palette.background.default, 0.72),
    darkIconMuted: alpha(theme.palette.common.white, 0.55),
    darkHover: alpha(theme.palette.common.white, 0.12),
    softOverlay: alpha(theme.palette.text.primary, isDark ? 0.08 : 0.06),
    popTextMuted: alpha(theme.palette.text.primary, 0.6),
    popTextSubtle: alpha(theme.palette.text.primary, 0.48),
    popIconMuted: alpha(theme.palette.text.primary, 0.38),
    popSelectedBg: alpha(theme.palette.text.primary, isDark ? 0.12 : 0.08),
  };
  const navigate = useNavigate();
  const canvasRef = React.useRef(null);

  const [selectedImg, setSelectedImg] = React.useState(null);
  const [selectedDevice, setSelectedDevice] = React.useState(null);
  const [zoom, setZoom] = React.useState(1);
  const [mode, setMode] = React.useState("ignore_rect"); // "line" | "rect" | "poly" | "ignore_rect" | "ignore_poly"
  const [history, setHistory] = React.useState([]);
  const [future, setFuture] = React.useState([]);

  const [drawing, setDrawing] = React.useState(false);
  const [start, setStart] = React.useState(null);
  const [polyPoints, setPolyPoints] = React.useState([]);
  const [imgSize, setImgSize] = React.useState({ width: 0, height: 0 });
  const [imgBroken, setImgBroken] = React.useState(false);
  // How far down the snapshot candidate chain the selected camera has fallen: 0 is
  // the first configured storage url, the last entry is always the api snapshot.
  const [snapshotCandidateIndex, setSnapshotCandidateIndex] = React.useState(0);
  // latest.jpg is a fixed url, so the browser would keep serving the first frame
  // it ever saw. These stamps make each request unique. They deliberately change
  // only when there is a reason to refetch -- a new device list, or a camera
  // being selected -- never on a re-render, which would refetch continuously.
  const [listSnapshotNonce, setListSnapshotNonce] = React.useState(() => Date.now());
  const [deviceSnapshotNonce, setDeviceSnapshotNonce] = React.useState(() => Date.now());

  const [shapesByImage, setShapesByImage] = React.useState({});
  const [selectedShapeId, setSelectedShapeId] = React.useState(null);
  const [hoveredZoneId, setHoveredZoneId] = React.useState(null);

  const [draggingShape, setDraggingShape] = React.useState(null);
const [dragOffset, setDragOffset] = React.useState(null);

// Pan state
const [isPanning, setIsPanning] = React.useState(false);
const [panStart, setPanStart] = React.useState(null);
const scrollContainerRef = React.useRef(null);

// Filter & search state
const [searchText, setSearchText] = React.useState("");
const [filterBy, setFilterBy] = React.useState(""); // "server" | "client" | ""
const [selectedServer, setSelectedServer] = React.useState("");
const [selectedClient, setSelectedClient] = React.useState("");
const currentUserType = Number(localStorage.getItem("user_type"));
const currentClientId = localStorage.getItem("client_id");
const isClientRestrictedUser = currentUserType !== 9;
const isReseller = localStorage.getItem("user_type") === "40";
const clientModelComboConfig = React.useMemo(
  () => cameraModelComboClientConfig.find(config => Number(config.client_id) === Number(currentClientId)),
  [currentClientId]
);
// /api/v1/devices names a camera's server by its hash ServerId, while the config
// file and the rest of the ui use the numeric server Id. /servers carries both, so
// a configured client fetches it purely to translate one into the other.
const [serverIdAliases, setServerIdAliases] = React.useState({});
const [serverAliasesLoaded, setServerAliasesLoaded] = React.useState(false);

React.useEffect(() => {
  if (!clientModelComboConfig) { setServerAliasesLoaded(true); return; }
  axios.get(apiUrl('/servers'))
    .then(res => {
      const aliases = {};
      (Array.isArray(res.data) ? res.data : []).forEach(server => {
        if (server?.ServerId && server?.Id !== undefined && server?.Id !== null) {
          aliases[String(server.ServerId)] = String(server.Id);
        }
      });
      setServerIdAliases(aliases);
    })
    .catch(() => setServerIdAliases({}))
    .finally(() => setServerAliasesLoaded(true));
}, [clientModelComboConfig]);

// The keys a camera's server answers to, best first: the numeric server Id, then
// the hash ServerId it is known by in /api/v1/devices.
const serverKeysOf = React.useCallback((device) => {
  const hash = device?.ServerId ? String(device.ServerId) : "";
  if (!hash) return [];
  const numeric = serverIdAliases[hash];
  return numeric ? [numeric, hash] : [hash];
}, [serverIdAliases]);

// A per-server setting is written either as one plain value used by every server,
// or as a map keyed by server id: { "1169": ..., "default": ... }. "default"
// covers every server the map does not name.
const perServerValue = React.useCallback((configured, device) => {
  if (configured === null || configured === undefined) return undefined;
  if (Array.isArray(configured) || typeof configured !== "object") return configured;
  const key = serverKeysOf(device).find(candidate => configured[candidate] !== undefined);
  return key ? configured[key] : configured.default;
}, [serverKeysOf]);

// A camera only offers the model versions its own server can run.
const modelVersionIdsForDevice = React.useCallback((device) => {
  const forServer = perServerValue(clientModelComboConfig?.display_model_version_ids, device);
  return (Array.isArray(forServer) ? forServer : []).map(Number);
}, [clientModelComboConfig, perServerValue]);

// gRPC ip a newly configured model starts on, likewise per server. An empty
// string is a deliberate setting -- that server's cameras each carry their own
// ip, so the field starts blank and the operator fills it in. Only a server the
// config does not name at all falls back to the stock local ip.
const commIpForDevice = React.useCallback(
  (device) => {
    const configured = perServerValue(clientModelComboConfig?.default_comm_ip, device);
    return configured === undefined || configured === null ? "127.0.0.1" : configured;
  },
  [clientModelComboConfig, perServerValue]
);
// the camera being configured decides the list, so it changes as the selection does
const allowedModelVersionIds = React.useMemo(
  () => modelVersionIdsForDevice(selectedDevice),
  [modelVersionIdsForDevice, selectedDevice]
);
const allowedClassIds = React.useMemo(
  () => (clientModelComboConfig?.display_class_ids || []).map(Number),
  [clientModelComboConfig]
);
const allowedComboIds = React.useMemo(
  () => (clientModelComboConfig?.display_combo_ids || []).map(Number),
  [clientModelComboConfig]
);
// A configured client with no display_combo_ids runs no combos at all, so it is not
// offered the combo tab. Clients absent from the config file, and the internal admin,
// keep it.
const showComboTab = !(isClientRestrictedUser && clientModelComboConfig && allowedComboIds.length === 0);
// Per-client defaults for newly configured models. A client absent from the
// config file gets no entry here, so it keeps the stock defaults below.
const defaultCommIp = commIpForDevice(selectedDevice);
// default_model_format resolves per server first, then per model version: a server
// entry is either one format for every version it runs, or a map keyed by
// model_version_id carrying its own "default".
const modelFormatConfigFor = React.useCallback((device) => {
  const forServer = perServerValue(clientModelComboConfig?.default_model_format, device);
  if (forServer && typeof forServer === "object") {
    return { byVersion: forServer, fallback: forServer.default || "pt" };
  }
  return { byVersion: {}, fallback: forServer || "pt" };
}, [clientModelComboConfig, perServerValue]);
// the format used where no model version is in hand yet
const defaultModelFormat = modelFormatConfigFor(selectedDevice).fallback;
const hideLicensePlate = Boolean(clientModelComboConfig?.hide_license_plate);
const pytorchModelIds = React.useMemo(
  () => (clientModelComboConfig?.pytorch_model_ids || []).map(Number),
  [clientModelComboConfig]
);
// Tracking defaults, applied to both the per-class panel and the combo panel.
// The two panels name the same three settings differently on the wire
// (min_duration_sec / min_track_duration_sec and so on), so each value lands twice.
const defaultMinDurationSec = clientModelComboConfig?.default_min_duration_sec ?? 2;
const defaultMaxDisappeared = clientModelComboConfig?.default_max_disappeared ?? 30;
// default_min_distance is either one number covering every class, or a map keyed by
// class id with "default" covering the classes it does not name. The combo panel
// tracks a whole combo rather than one class, so it always takes the fallback.
const minDistanceConfig = clientModelComboConfig?.default_min_distance;
const minDistanceByClassId = (minDistanceConfig && typeof minDistanceConfig === "object") ? minDistanceConfig : null;
const defaultMinDistance = (minDistanceByClassId ? minDistanceByClassId.default : minDistanceConfig) ?? 0;
// The distance a class starts on: its own entry when the client pins one, else the fallback.
const minDistanceForClass = (classKey) =>
  (classKey != null && minDistanceByClassId?.[String(classKey)] != null)
    ? minDistanceByClassId[String(classKey)]
    : defaultMinDistance;
// Model-tab tracking FPS. The combo tab has its own, defaultComboTrackingFps.
const defaultTrackingFps = clientModelComboConfig?.default_tracking_fps ?? 1;
// "Ignore Motion" after tracking, and for how many seconds, on a new class.
const defaultIgnoreMotionAfterTracking = clientModelComboConfig?.default_ignore_motion_after_tracking ?? false;
const defaultIgnoreMotionAfterTrackingTime = clientModelComboConfig?.default_ignore_motion_after_tracking_time ?? 5;
// Model versions whose classes start with tracking switched off. Keyed on
// model_version_id (the exact version), not model_id like modelFormatFor.
const trackingDisabledModelVersionIds = React.useMemo(
  () => (clientModelComboConfig?.tracking_disabled_model_version_ids || []).map(Number),
  [clientModelComboConfig]
);
const trackingEnabledByDefault = (modelVersionKey) =>
  !trackingDisabledModelVersionIds.includes(Number(modelVersionKey));
// Combo-panel counterparts. The combo runs longer, coarser tracking than the
// per-class panel, so it does not share those three numbers.
const defaultComboTrackingEnabled = clientModelComboConfig?.default_combo_tracking_enabled ?? false;
const defaultComboDetectFps = clientModelComboConfig?.default_combo_detect_fps ?? DEFAULT_DETECT_FPS;
const defaultComboTrackingFps = clientModelComboConfig?.default_combo_tracking_fps ?? 1;
const defaultComboMinDurationSec = clientModelComboConfig?.default_combo_min_duration_sec ?? 3;
const defaultComboMaxDisappeared = clientModelComboConfig?.default_combo_max_disappeared ?? 30;
// Live snapshot store. Configured clients read the current frame straight from
// storage; anyone else, and any camera with no frame there, keeps the snapshot
// the devices api hands back.
// snapshot_base_url is one url, a list of urls, or a map keyed by server id whose
// entries are either of those. Every configured url is tried in order and the first
// one that serves a frame wins.
const snapshotBaseUrlsFor = (device) => {
  const configured = perServerValue(clientModelComboConfig?.snapshot_base_url, device);
  return (Array.isArray(configured) ? configured : [configured]).filter(Boolean);
};
const storageSnapshotsFor = (device, nonce) => (
  device?.deviceid
    ? snapshotBaseUrlsFor(device).map(base => `${base}/${device.deviceid}/latest.jpg?t=${nonce}`)
    : []
);
// The urls to try for one camera, storage first and the api snapshot as the last resort.
const snapshotCandidatesFor = (device, nonce) => (
  [...storageSnapshotsFor(device, nonce), device?.snapshot || ""].filter(Boolean)
);
const preferredSnapshotFor = (device, nonce) => snapshotCandidatesFor(device, nonce)[0] || "";
const activeSnapshotCandidates = snapshotCandidatesFor(selectedDevice, deviceSnapshotNonce);
const activeSnapshot = activeSnapshotCandidates[snapshotCandidateIndex] || "";
// Model version a combo class starts on before the user picks one. "" means no
// preselection, which is the stock behaviour.
const defaultComboModelVersionId = clientModelComboConfig?.default_combo_model_version_id
  ? String(clientModelComboConfig.default_combo_model_version_id)
  : "";
const getComboId = React.useCallback((combo) => String(
  combo?.id ?? combo?.combo_id ?? combo?.combo_rule_id ?? combo?.ComboId ?? combo?.ID ?? ""
), []);

// Fetched server/client data
const [servers, setServers] = React.useState([]);
const [clients, setClients] = React.useState([]);
const [resellerClientIds, setResellerClientIds] = React.useState("");
const [resellerClientsLoaded, setResellerClientsLoaded] = React.useState(false);

React.useEffect(() => {
  if (isClientRestrictedUser) {
    setServers([]);
    setClients([]);
    setResellerClientIds("");
    setResellerClientsLoaded(true);
    return;
  }

  axios.get(apiUrl('/servers/servers-info'))
    .then(res => setServers(Array.isArray(res.data) ? res.data : []))
    .catch(() => setServers([]));
  axios.get(apiUrl('/client-info/get-clients'))
    .then(res => {
      const list = Array.isArray(res.data) ? res.data : [];
      setClients(list);
      if (isReseller) {
        setResellerClientIds(list.map(client => client?.ID).filter(id => id !== undefined && id !== null).join(","));
      }
    })
    .catch(() => {
      setClients([]);
      if (isReseller) setResellerClientIds("");
    })
    .finally(() => {
      if (isReseller) setResellerClientsLoaded(true);
    });
}, [isClientRestrictedUser, isReseller]);

// Device list state
const [devices, setDevices] = React.useState([]);
const [devicesLoading, setDevicesLoading] = React.useState(false);
const [hasMoreDevices, setHasMoreDevices] = React.useState(true);
const deviceListRef = React.useRef(null);
const DEVICE_LIMIT = 50;
const [saving, setSaving] = React.useState(false);
const [snack, setSnack] = React.useState({ open: false, severity: "success", message: "" });

const fetchDevices = React.useCallback((params = {}, append = false) => {
  if (isClientRestrictedUser && !currentClientId) {
    setDevices([]);
    setHasMoreDevices(false);
    return;
  }
  setDevicesLoading(true);
  const query = new URLSearchParams();
  query.set("limit", String(DEVICE_LIMIT));
  if (isClientRestrictedUser) {
    query.set("client_id", String(currentClientId));
  } else {
    if (params.server_id) query.set("server_id", params.server_id);
    if (params.client_id) query.set("client_id", String(params.client_id));
    if (isReseller) query.set("client_id", resellerClientIds);
  }
  if (params.device_id) query.set("device_id", String(params.device_id));
  if (params.device_name) query.set("device_name", params.device_name);
  if (params.last_device_id) query.set("last_device_id", String(params.last_device_id));
  fetch(`${apiUrl('/api/v1/devices')}?${query.toString()}`).then(r => r.json())
    .then(data => {
      const list = Array.isArray(data) ? data : [];
      if (!append) setListSnapshotNonce(Date.now());
      setDevices(prev => append ? [...prev, ...list] : list);
      setHasMoreDevices(list.length >= DEVICE_LIMIT);
    })
    .catch(() => { if (!append) setDevices([]); setHasMoreDevices(false); })
    .finally(() => setDevicesLoading(false));
}, [currentClientId, isClientRestrictedUser, isReseller, resellerClientIds]);

// Initial load
const isFirstDeviceFetch = React.useRef(true);
React.useEffect(() => {
  if (isReseller && !resellerClientsLoaded) return;
  fetchDevices();
}, [fetchDevices, isReseller, resellerClientsLoaded]);

// Refetch on filter/search change (debounced for search)
const searchTimerRef = React.useRef(null);
React.useEffect(() => {
  if (isFirstDeviceFetch.current) { isFirstDeviceFetch.current = false; return; }
  clearTimeout(searchTimerRef.current);
  const params = {};
  if (!isClientRestrictedUser) {
    if (filterBy === "server" && selectedServer) params.server_id = selectedServer;
    if (filterBy === "client" && selectedClient) params.client_id = selectedClient;
  }
  const trimmed = searchText.trim();

  // Require at least 3 characters to search, or empty to show all
  if (trimmed.length > 0 && trimmed.length < 3) return;
  if (trimmed.length >= 3) {
    params.device_name = trimmed;
  }

  if (trimmed.length >= 3) {
    searchTimerRef.current = setTimeout(() => fetchDevices(params), 400);
  } else {
    fetchDevices(params);
  }
  return () => clearTimeout(searchTimerRef.current);
}, [searchText, filterBy, selectedServer, selectedClient, isClientRestrictedUser]);

// Infinite scroll handler
const handleDeviceScroll = React.useCallback((e) => {
  const el = e.target;
  if (devicesLoading || !hasMoreDevices) return;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
    const lastDevice = devices[devices.length - 1];
    if (!lastDevice) return;
    const params = {};
    if (!isClientRestrictedUser) {
      if (filterBy === "server" && selectedServer) params.server_id = selectedServer;
      if (filterBy === "client" && selectedClient) params.client_id = selectedClient;
    }
    if (searchText.trim().length >= 3) {
      params.device_name = searchText.trim();
    }
    params.last_device_id = lastDevice.uid;
    fetchDevices(params, true);
  }
}, [devices, devicesLoading, hasMoreDevices, filterBy, selectedServer, selectedClient, searchText, fetchDevices, isClientRestrictedUser]);

// Fetch camera detection config when device selected
const [configLoading, setConfigLoading] = React.useState(false);
const loadRequestSeqRef = React.useRef(0);

// Parse area — accepts string "[[(0.3, 0.05), ...]]" or already-parsed array
const parseAreaString = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try {
    const json = val.replace(/\(/g, "[").replace(/\)/g, "]");
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch { return []; }
};

// Convert parsed polygon to shape — detect rect (4 points, axis-aligned) vs poly
const polyToShape = (poly, category) => {
  const pts = poly.map(p => ({ x: p[0], y: p[1] }));
  if (pts.length === 4) {
    const xs = pts.map(p => p.x);
    const ys = pts.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    // Check if all points sit on the bounding box edges (axis-aligned rectangle)
    const isRect = pts.every(p =>
      (Math.abs(p.x - minX) < 0.001 || Math.abs(p.x - maxX) < 0.001) &&
      (Math.abs(p.y - minY) < 0.001 || Math.abs(p.y - maxY) < 0.001)
    );
    if (isRect) {
      return { id: Date.now() + Math.random(), type: "rect", category, x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }
  }
  return { id: Date.now() + Math.random(), type: "poly", category, points: pts };
};

const loadDeviceConfig = (deviceId, device, imgKey) => {
  if (!deviceId) return;
  const loadSeq = ++loadRequestSeqRef.current;
  const isCurrentLoad = () => loadRequestSeqRef.current === loadSeq;
  // resolved against the camera being loaded rather than the current selection
  const deviceCommIp = commIpForDevice(device);
  setConfigLoading(true);

  // Reset combo state
  setSelectedCombos([]);
  setActiveComboId("");
  setActiveComboClassId("");
  setComboSettings({});

  // Shared between parallel requests — populated by each, consumed after both settle
  let _comboIds = [];
  let _comboSettings = {};
  let _comboTrackLineShapes = [];
  let _loadedShapes = [];

  // Moved to loadDeviceConfig scope so it's reusable in post-settlement reconstruction
  const polyMatchesShape = (daPoly, shape) => {
    let shapeCoords;
    if (shape.type === "line" && shape.x1 != null) {
      shapeCoords = [[shape.x1, shape.y1], [shape.x2, shape.y2]];
    } else if (shape.type === "rect") {
      shapeCoords = [[shape.x, shape.y], [shape.x + shape.w, shape.y], [shape.x + shape.w, shape.y + shape.h], [shape.x, shape.y + shape.h]];
    } else {
      shapeCoords = (shape.points || []).map(p => [p.x, p.y]);
    }
    if (daPoly.length !== shapeCoords.length) return false;
    const maxCoord = Math.max(
      ...daPoly.flatMap(([x, y]) => [Math.abs(x), Math.abs(y)]),
      ...shapeCoords.flatMap(([x, y]) => [Math.abs(x), Math.abs(y)])
    );
    const EPS = maxCoord <= 2 ? 1e-4 : 1;
    const ptEq = (a, b) => Math.abs(a[0] - b[0]) <= EPS && Math.abs(a[1] - b[1]) <= EPS;
    if (daPoly.length === 2) {
      return (ptEq(daPoly[0], shapeCoords[0]) && ptEq(daPoly[1], shapeCoords[1])) ||
             (ptEq(daPoly[0], shapeCoords[1]) && ptEq(daPoly[1], shapeCoords[0]));
    }
    const n = daPoly.length;
    for (let start = 0; start < n; start += 1) {
      let forwardOk = true;
      for (let i = 0; i < n; i += 1) {
        if (!ptEq(daPoly[i], shapeCoords[(start + i) % n])) { forwardOk = false; break; }
      }
      if (forwardOk) return true;
      let reverseOk = true;
      for (let i = 0; i < n; i += 1) {
        const idx = (start - i + n) % n;
        if (!ptEq(daPoly[i], shapeCoords[idx])) { reverseOk = false; break; }
      }
      if (reverseOk) return true;
    }
    return false;
  };

  // Fetch combo config in parallel
  const comboRequest = fetch(`${apiUrl('/api/v1/servers/combo-config')}?camera_id=${deviceId}`, { cache: "no-store" })
    .then(r => r.ok ? r.json() : { cameras: {} })
    .then(comboData => {
      const camCombo = comboData?.cameras?.[String(deviceId)];
      if (!camCombo || !camCombo.combos) return;
      const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const comboIds = [];
      const settings = {};
      const canvasShapesToAdd = []; // shapes to merge into canvas for all combos

      Object.entries(camCombo.combos).forEach(([comboKey, combo]) => {
        // Find combo rule ID from comboList — try combo_name, object key, display_name, id
        const nameToMatch = combo.combo_name || comboKey;
        const comboRule = comboList.find(c =>
          c.combo_name === nameToMatch ||
          c.name === nameToMatch ||
          c.combo_name === comboKey ||
          (combo.display_name && c.display_name === combo.display_name) ||
          (combo.combo_id && getComboId(c) === String(combo.combo_id))
        );
        // Fall back to combo.combo_id from the API response if comboList lookup fails
        const resolvedRuleId = getComboId(comboRule) || combo.combo_id;
        if (!resolvedRuleId) return;
        if (isClientRestrictedUser && !allowedComboIds.includes(Number(resolvedRuleId))) return;
        const comboId = String(resolvedRuleId);
        comboIds.push(comboId);

        // Build classModels from combo.classes + combo.models
        const classModels = {};
        // Also build a class-name→classId map from the comboRule items for fallback
        const ruleClassNameMap = {};
        (comboRule?.items || []).forEach(item => {
          if (item.class_name) ruleClassNameMap[item.class_name.toLowerCase()] = String(item.class_id);
          if (item.class_display_name) ruleClassNameMap[item.class_display_name.toLowerCase()] = String(item.class_id);
        });
        Object.entries(combo.classes || {}).forEach(([className, cls]) => {
          const lowerName = className?.toLowerCase();
          // Prefer comboRule item class_id (consistent with chip keys), fall back to the class list
          let classObj = ALL_MODEL_CLASSES.find(c => c.label?.toLowerCase() === lowerName);
          let classId = ruleClassNameMap[lowerName] || classObj?.key;
          if (!classId) return;
          const model = combo.models?.[cls.model_name];
          if (!model) {
            classModels[classId] = { ...DEFAULT_COMBO_CLASS_MODEL };
            return;
          }
          const modelVersionKey = model.model_version_id
            ? String(model.model_version_id)
            : (ALL_MODEL_OPTIONS.find(o =>
                o.label === model.version_display_name ||
                o.label === model.model_name ||
                o.label?.toLowerCase() === model.version_display_name?.toLowerCase() ||
                o.label?.toLowerCase() === model.model_name?.toLowerCase()
              )?.key || "");
          const comm = model.communication || {};
          classModels[classId] = {
            model_config_id: model.model_config_id || 0,
            model_version_id: modelVersionKey,
            conf: model.conf ?? DEFAULT_CONF,
            iou: model.iou ?? DEFAULT_IOU,
            detect_fps: model.detect_fps ?? defaultComboDetectFps,
            motion_sensitivity: model.motion_sensitivity ?? DEFAULT_MOTION_SENSITIVITY,
            augment: model.augment ?? false,
            agnostic_nms: model.agnostic_nms ?? false,
            is_zoom_motion_area: model.is_zoom_motion_area ?? model.zoom_motion_area ?? false,
            enable_detect_always: model.enable_detect_always ?? false,
            enable_motion: model.enable_motion ?? true,
            model_format: model.model_format || modelFormatForDevice(device, modelVersionKey),
            zoneIds: [],
            rawDetectionArea: cls.detection_area || null,
            comm_type: comm.type || "grpc",
            comm_ip: comm.grpc?.ip || deviceCommIp,
            comm_port: String(comm.grpc?.port || "50051"),
            comm_url: comm.http?.public_base_url || comm.http?.private_base_url || "",
            comm_path: comm.http?.path || "",
          };
        });

        // Build tracking — handle both nested (t.config.*) and flat (t.*) API shapes
        const t = combo.tracking;
        const tc = t?.config || t || null;
        const trackingEnabled = t ? (t.enabled ?? true) : false;
        const trackingCfg = tc ? {
          tracking_config_id: tc.tracking_config_id ?? 0,
          track_iou: tc.track_iou ?? 0.3,
          track_max_disappeared: tc.track_max_disappeared ?? defaultComboMaxDisappeared,
          tracking_fps: tc.tracking_fps ?? defaultComboTrackingFps,
          min_track_distance: tc.min_track_distance ?? defaultMinDistance,
          min_track_duration_sec: tc.min_track_duration_sec ?? defaultComboMinDurationSec,
          enable_object_count: tc.enable_object_count ?? false,
          track_direction: tc.track_direction || null,
          track_line: null,
          comboTrackLineIds: [],
          rawTrackLine: null,
        } : { ...DEFAULT_COMBO_TRACKING_CFG };

        // --- Combo boundary — accept both string and array format from API
        const cb = combo.combo_boundary;
        // Store as-is: parseAreaString handles both string (Python-style) and array
        const rawComboBoundary = (cb !== null && cb !== undefined && cb !== '') ? cb : null;

        // --- Convert track_line array → canvas line shapes ---
        const rawTL = tc?.track_line;
        if (rawTL) {
          if (typeof rawTL === 'string') {
            trackingCfg.rawTrackLine = rawTL;
          } else if (Array.isArray(rawTL) && rawTL.length > 0) {
            const trackLineShapeIds = [];
            rawTL.forEach(segment => {
              if (!Array.isArray(segment) || segment.length < 2) return;
              const [p1, p2] = segment;
              const shape = {
                id: Date.now() + Math.random(),
                type: "line",
                category: "zone",
                x1: p1[0], y1: p1[1],
                x2: p2[0], y2: p2[1],
                label: "Track Line",
                _comboShape: true,
              };
              canvasShapesToAdd.push(shape);
              trackLineShapeIds.push(String(shape.id));
            });
            if (trackLineShapeIds.length > 0) {
              trackingCfg.comboTrackLineIds = trackLineShapeIds;
            }
          }
        }

        // Build schedule from monitoring times
        const merged = [];
        (combo.combo_monitoring_times || []).forEach(mt => {
          const st = (mt.start_time || "00:00").slice(0, 5);
          const et = (mt.end_time || "23:59").slice(0, 5);
          const dayName = DAY_NAMES[mt.day_of_week] || String(mt.day_of_week);
          const existing = merged.find(r => r.startTime === st && r.endTime === et);
          if (existing) {
            existing.days.push(dayName);
            existing.dayIds[dayName] = mt.id || 0;
          } else {
            merged.push({ startTime: st, endTime: et, days: [dayName], dayIds: { [dayName]: mt.id || 0 } });
          }
        });

        settings[comboId] = {
          comboConfigId: 0,
          combo_rule_id: parseInt(comboId) || 0,
          classModels,
          trackingEnabled,
          tracking: trackingCfg,
          schedule: merged,
          rawComboBoundary: rawComboBoundary,
          boundaryZoneIds: [],
          syncDetectionConfig: combo.sync_detection_config ?? false,
        };
      });

      // Defer state-setting to post-settlement so zone reconstruction can use loaded shapes
      _comboIds = comboIds;
      _comboSettings = settings;
      _comboTrackLineShapes = canvasShapesToAdd.filter(s => s._comboShape && s.type === "line");
    })
    .catch(() => {});

  const detectionRequest = fetch(`${apiUrl('/api/v1/camera-detection-config')}?device_id=${deviceId}`, { cache: "no-store" })
    .then(r => r.json())
    .then(data => {
      if (!isCurrentLoad()) return;
      const cam = data?.cameras?.[String(deviceId)] || data?.[String(deviceId)];
      if (!cam || !cam.models || cam.models.length === 0) {
        // No model config — reset model state but still load device areas for combo zone matching
        setSelectedModels([]);
        setActiveModel("");
        setModelSettings({});
        setActiveClassName("");
        const deviceShapes = [];
        if (device) {
          parseAreaString(device.Monarea).forEach(poly => {
            if (Array.isArray(poly) && poly.length >= 3)
              deviceShapes.push(polyToShape(poly, "zone"));
          });
          parseAreaString(device.Ignorearea).forEach(poly => {
            if (Array.isArray(poly) && poly.length >= 3)
              deviceShapes.push(polyToShape(poly, "ignore"));
          });
        }
        // Capture shapes so post-settlement handler can match combo boundaries
        _loadedShapes = deviceShapes;
        if (!isCurrentLoad()) return;
        setShapesByImage(prev => ({
          ...prev,
          [imgKey]: [...deviceShapes, ...(prev[imgKey] || []).filter(s => s._comboShape)],
        }));
        setSelectedShapeId(null);
        tabZoneMemory.current = { model: undefined, combo: undefined };
        return;
      }

      const deviceModelVersionIds = modelVersionIdsForDevice(device);
      if (isClientRestrictedUser) {
        console.log(`[LOAD] camera ${deviceId} server keys ${serverKeysOf(device).join(" / ") || "unknown"} → model versions`, deviceModelVersionIds);
      }
      const models = cam.models
        .filter(m => !isClientRestrictedUser || deviceModelVersionIds.includes(Number(m.model_version_id)))
        .map(m => ({
          ...m,
          classes: (m.classes || []).filter(cls => (
            !isClientRestrictedUser || allowedClassIds.includes(Number(cls.model_class_id ?? cls.class_config_id))
          )),
        }))
        .filter(m => (m.classes || []).length > 0);

      // Load Monarea / Ignorearea into canvas FIRST
      const loadedShapes = [];
      if (device) {
        parseAreaString(device.Monarea).forEach(poly => {
          if (Array.isArray(poly) && poly.length >= 3)
            loadedShapes.push(polyToShape(poly, "zone"));
        });
        parseAreaString(device.Ignorearea).forEach(poly => {
          if (Array.isArray(poly) && poly.length >= 3)
            loadedShapes.push(polyToShape(poly, "ignore"));
        });
      }

      // Collect all detection_area and track_boundary polys, create shapes for ones not already in loadedShapes
      const addMissingShapes = (areaStr, category) => {
        if (!areaStr) return;
        parseAreaString(areaStr).forEach(poly => {
          if (!Array.isArray(poly) || poly.length < 2) return;
          const alreadyExists = loadedShapes.some(s => s.category !== "ignore" && polyMatchesShape(poly, s));
          if (!alreadyExists) {
            if (poly.length === 2) {
              // Line shape (2 points)
              loadedShapes.push({ id: Date.now() + Math.random(), type: "line", category, x1: poly[0][0], y1: poly[0][1], x2: poly[1][0], y2: poly[1][1] });
            } else {
              loadedShapes.push(polyToShape(poly, category));
            }
          }
        });
      };
      models.forEach(m => {
        (m.classes || []).forEach(cls => {
          addMissingShapes(cls.detection_area, "zone");
          const tEntry = Array.isArray(cls.tracking)
            ? (cls.tracking[0] || {})
            : (cls.tracking?.config || cls.tracking || {});
          const tb = tEntry.track_boundary;
          addMissingShapes(tb, "zone");
        });
      });

      const globalSettingsMap = {};
      const reconstructedZoneConfigs = {};
      const zoneSpecificModelKeys = new Set();
      const allModelKeys = [];
      models.forEach(m => {
        const modelKey = String(m.model_version_id);
        allModelKeys.push(modelKey);
        const comm = m.communication || {};
        const classNames = (m.classes || []).map(c => String(c.model_class_id ?? c.class_config_id));
        const trackingByClass = {};
        const classConfigIdMap = {};

        (m.classes || []).forEach(cls => {
          const classKey = String(cls.model_class_id ?? cls.class_config_id);
          classConfigIdMap[classKey] = cls.class_config_id;
          const trackingDisabled = Array.isArray(cls.tracking) && cls.tracking.length === 0;
          const t = Array.isArray(cls.tracking)
            ? (cls.tracking[0] || {})
            : (cls.tracking?.config || cls.tracking || {});
          const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

          // Merge ob_monitoring_times by start/end time, store per-day IDs
          const merged = [];
          (cls.ob_monitoring_times || []).forEach(mt => {
            const st = (mt.start_time || "00:00").slice(0, 5);
            const et = (mt.end_time || "23:59").slice(0, 5);
            const dayName = DAY_NAMES[mt.day_of_week] || mt.day_of_week;
            const existing = merged.find(r => r.startTime === st && r.endTime === et);
            if (existing) {
              existing.days.push(dayName);
              existing.dayIds[dayName] = mt.id || 0;
            } else {
              merged.push({ startTime: st, endTime: et, days: [dayName], dayIds: { [dayName]: mt.id || 0 } });
            }
          });

          const finalSchedule = merged;

          // Match detection_area polygons to loaded zone shapes (poly/rect only, not lines)
          const loadedZoneShapes = loadedShapes.filter(s => s.category !== "ignore" && s.type !== "line");
          const matchedZoneIds = [];
          if (cls.detection_area) {
            parseAreaString(cls.detection_area).forEach(daPoly => {
              if (!Array.isArray(daPoly) || daPoly.length < 3) return;
              const match = loadedZoneShapes.find(s => polyMatchesShape(daPoly, s));
              if (match) matchedZoneIds.push(String(match.id));
            });
          }

          // Match track_boundary to loaded shapes (lines, polys, rects)
          const matchedCountIds = [];
          if (t.track_boundary) {
            parseAreaString(t.track_boundary).forEach(tbPoly => {
              if (!Array.isArray(tbPoly) || tbPoly.length < 2) return;
              const match = loadedShapes.find(s => s.category !== "ignore" && polyMatchesShape(tbPoly, s));
              if (match) {
                matchedCountIds.push(String(match.id));
                return;
              }
              // Fallback: materialize unmatched boundary so UI can always bind it.
              const newShape = tbPoly.length === 2
                ? { id: Date.now() + Math.random(), type: "line", category: "zone", x1: tbPoly[0][0], y1: tbPoly[0][1], x2: tbPoly[1][0], y2: tbPoly[1][1] }
                : polyToShape(tbPoly, "zone");
              loadedShapes.push(newShape);
              matchedCountIds.push(String(newShape.id));
            });
          }

          trackingByClass[classKey] = {
            enabled: trackingDisabled ? false : (t.enabled ?? trackingEnabledByDefault(modelKey)),
            tracking_config_id: t.tracking_config_id || 0,
            min_distance: t.min_distance ?? minDistanceForClass(classKey),
            min_duration_sec: t.min_duration_sec ?? defaultMinDurationSec,
            max_disappeared: t.max_disappeared ?? defaultMaxDisappeared,
            direction: t.direction || "both",
            fps: t.fps ?? defaultTrackingFps,
            count_objects: t.enable_object_count ?? false,
            ignore_motion_after_tracking: t.ignore_motion_after_tracking ?? defaultIgnoreMotionAfterTracking,
            ignore_motion_after_tracking_time: t.ignore_motion_after_tracking_time ?? defaultIgnoreMotionAfterTrackingTime,
            enable_single_track_video: t.enable_single_track_video ?? false,
            enable_license_plate: t.enable_license_plate_rec ?? false,
            license_plate_model: t.ocr_model || "small",
            license_plate_conf: t.ocr_confidence || 0.5,
            license_plate_ip: t.ocr_service_ip_address || "127.0.0.1",
            license_plate_port: t.ocr_service_port ? String(t.ocr_service_port) : "50051",
            track_direction: t.direction || "TTB",
            track_line: null,
            zoneIds: matchedZoneIds,
            countShapeIds: matchedCountIds,
            schedule: finalSchedule,
          };
        });

        const modelState = {
          model_config_id: m.model_config_id || 0,
          conf: m.conf ?? DEFAULT_CONF,
          iou: m.iou ?? DEFAULT_IOU,
          detect_fps: m.detect_fps ?? DEFAULT_DETECT_FPS,
          motion_sensitivity: m.motion_sensitivity ?? DEFAULT_MOTION_SENSITIVITY,
          augment: m.augment ?? false,
          agnostic_nms: m.agnostic_nms ?? false,
          is_zoom_motion_area: m.zoom_motion_area ?? false,
          enable_detect_always: m.enable_detect_always ?? false,
          enable_motion: m.enable_motion ?? true,
          model_format: m.model_format || modelFormatForDevice(device, modelKey),
          comm_type: comm.type || "grpc",
          comm_ip: comm.grpc?.ip || deviceCommIp,
          comm_port: String(comm.grpc?.port || "50051"),
          comm_url: comm.http?.public_base_url || comm.http?.private_base_url || "",
          comm_path: comm.http?.path || "",
          classNames: classNames.length > 0 ? classNames : [],
          trackingByClass,
          classConfigIdMap,
        };

        const globalClassNames = (modelState.classNames || []).filter(classKey => {
          const tbc = modelState.trackingByClass?.[classKey] || {};
          return (tbc.zoneIds || []).length === 0;
        });
        if (globalClassNames.length > 0) {
          const existingGlobal = globalSettingsMap[modelKey];
          const nextTrackingByClass = {
            ...(existingGlobal?.trackingByClass || {}),
            ...Object.fromEntries(
              Object.entries(modelState.trackingByClass || {}).filter(([k]) => globalClassNames.includes(k))
            ),
          };
          const nextClassConfigIdMap = {
            ...(existingGlobal?.classConfigIdMap || {}),
            ...Object.fromEntries(
              Object.entries(modelState.classConfigIdMap || {}).filter(([k]) => globalClassNames.includes(k))
            ),
          };
          const nextClassNames = [...new Set([...(existingGlobal?.classNames || []), ...globalClassNames])];
          globalSettingsMap[modelKey] = existingGlobal ? {
            ...existingGlobal,
            classNames: nextClassNames,
            trackingByClass: nextTrackingByClass,
            classConfigIdMap: nextClassConfigIdMap,
          } : {
            ...modelState,
            classNames: nextClassNames,
            trackingByClass: nextTrackingByClass,
            classConfigIdMap: nextClassConfigIdMap,
          };
        }

        Object.entries(modelState.trackingByClass || {}).forEach(([classKey, tracking]) => {
          (tracking.zoneIds || []).forEach(shapeId => {
            zoneSpecificModelKeys.add(modelKey);
            if (!reconstructedZoneConfigs[shapeId]) {
              reconstructedZoneConfigs[shapeId] = {
                enabled: true,
                selectedModels: [],
                activeModel: "",
                activeClassName: "",
                modelSettings: {},
              };
            }
            const zc = reconstructedZoneConfigs[shapeId];
            if (!zc.selectedModels.includes(modelKey)) zc.selectedModels.push(modelKey);
            if (!zc.activeModel) zc.activeModel = modelKey;
            if (!zc.activeClassName) zc.activeClassName = classKey;
            if (!zc.modelSettings[modelKey]) {
              zc.modelSettings[modelKey] = {
                ...modelState,
                classNames: [],
                trackingByClass: {},
                classConfigIdMap: {},
              };
            }
            if (!zc.modelSettings[modelKey].classNames.includes(classKey)) {
              zc.modelSettings[modelKey].classNames.push(classKey);
            }
            zc.modelSettings[modelKey].trackingByClass[classKey] = tracking;
            if (modelState.classConfigIdMap?.[classKey] != null) {
              zc.modelSettings[modelKey].classConfigIdMap[classKey] = modelState.classConfigIdMap[classKey];
            }
          });
        });
      });

      const globalModelKeys = Object.keys(globalSettingsMap);
      setSelectedModels(globalModelKeys);
      setModelSettings(globalSettingsMap);

      // Active model/class should only be set from global models (not zone-specific ones)
      let preferredModelKey = globalModelKeys[0] || "";
      let preferredClassKey = "";

      // Prefer first global class that has an existing count boundary assignment
      for (const mk of globalModelKeys) {
        const tbc = globalSettingsMap[mk]?.trackingByClass || {};
        const foundClass = Object.keys(tbc).find(k => (tbc[k]?.countShapeIds || []).length > 0);
        if (foundClass) {
          preferredModelKey = mk;
          preferredClassKey = foundClass;
          break;
        }
      }

      if (!preferredClassKey && preferredModelKey) {
        const firstGlobalClass = globalSettingsMap[preferredModelKey]?.classNames?.[0] || "";
        preferredClassKey = firstGlobalClass;
      }

      setActiveModel(preferredModelKey);
      setActiveClassName(preferredClassKey);

      // ── MODEL LOAD SUMMARY LOG ─────────────────────────────────────────────
      console.group(`[LOAD][MODEL] camera ${deviceId}`);
      console.log('[LOAD][MODEL] all modelKeys from API:', allModelKeys);
      console.log('[LOAD][MODEL] globalModelKeys (classes with no zone detection):', globalModelKeys);
      globalModelKeys.forEach(mk => console.log(`  global model ${mk} classes:`, globalSettingsMap[mk]?.classNames));
      console.log('[LOAD][MODEL] zone-specific modelKeys:', Array.from(zoneSpecificModelKeys));
      console.log('[LOAD][MODEL] reconstructedZoneConfigs zones:', Object.keys(reconstructedZoneConfigs));
      Object.entries(reconstructedZoneConfigs).forEach(([sid, zc]) => {
        zc.selectedModels.forEach(mk => console.log(`  zone ${sid} model ${mk} classes:`, zc.modelSettings[mk]?.classNames));
      });
      console.groupEnd();

      if (Object.keys(reconstructedZoneConfigs).length > 0) {
        if (!isCurrentLoad()) return;
        setZoneConfigsByImage(prev => ({
          ...prev,
          [imgKey]: reconstructedZoneConfigs,
        }));
      } else {
        if (!isCurrentLoad()) return;
        setZoneConfigsByImage(prev => ({ ...prev, [imgKey]: {} }));
      }

      // Auto-select a configured zone only when there is no global model config.
      // Global config = at least one model class has no zone detection area assigned.
      const hasGlobalModelConfig = globalModelKeys.length > 0;
      const autoSelectZoneId = (!hasGlobalModelConfig && Object.keys(reconstructedZoneConfigs).length > 0)
        ? Object.keys(reconstructedZoneConfigs)[0]
        : null;
      setSelectedShapeId(autoSelectZoneId ? Number(autoSelectZoneId) : null);
      tabZoneMemory.current = { model: autoSelectZoneId ? Number(autoSelectZoneId) : null, combo: undefined };
      // Capture loaded shapes for zone combo reconstruction in post-settlement handler
      _loadedShapes = loadedShapes;

      // Set shapes for this device, preserving any combo shapes already added by comboRequest
      if (!isCurrentLoad()) return;
      setShapesByImage(prev => ({
        ...prev,
        [imgKey]: [...loadedShapes, ...(prev[imgKey] || []).filter(s => s._comboShape)],
      }));
    })
    .catch(() => {
      if (!isCurrentLoad()) return;
      setSnack({ open: true, severity: "error", message: "Failed to load device configuration" });
    });

  return Promise.allSettled([comboRequest, detectionRequest])
    .then(() => {
      if (!isCurrentLoad()) return;
      // ---- ZONE COMBO RECONSTRUCTION ----
      if (_comboIds.length === 0) return;

      // Materialize combo boundary polygons that aren't already on canvas.
      // They get excluded from Monarea at save time (marked as assignedZoneIds),
      // so they must be restored from rawComboBoundary here.
      // Use a separate array to avoid mutating _loadedShapes (same reference as
      // the loadedShapes array captured in detectionRequest's setShapesByImage updater).
      const boundaryShapesToAdd = [];
      const shapesForMatching = [..._loadedShapes]; // new array — safe to push into
      _comboIds.forEach(comboId => {
        const cs = _comboSettings[comboId];
        if (!cs?.rawComboBoundary) return;
        const polys = parseAreaString(cs.rawComboBoundary);
        (polys || []).forEach(poly => {
          if (!Array.isArray(poly) || poly.length < 3) return;
          const alreadyExists = shapesForMatching.some(s => s.category !== "ignore" && polyMatchesShape(poly, s));
          if (!alreadyExists) {
            const shape = polyToShape(poly, "zone");
            shapesForMatching.push(shape);
            boundaryShapesToAdd.push(shape);
          }
        });
      });
      if (boundaryShapesToAdd.length > 0) {
        setShapesByImage(prev => ({
          ...prev,
          [imgKey]: [...(prev[imgKey] || []), ...boundaryShapesToAdd],
        }));
      }

      const zoneShapes = shapesForMatching.filter(s => s.category !== "ignore" && s.type !== "line");
      const reconstructedZoneComboConfigs = {};
      const restoredSettings = { ..._comboSettings };

      _comboIds.forEach(comboId => {
        const cs = _comboSettings[comboId];
        if (!cs?.rawComboBoundary) return;
        const boundaryPolys = parseAreaString(cs.rawComboBoundary);
        if (!boundaryPolys || boundaryPolys.length === 0) return;
        for (const poly of boundaryPolys) {
          if (!Array.isArray(poly) || poly.length < 3) continue;
          const match = zoneShapes.find(s => polyMatchesShape(poly, s));
          if (!match) continue;
          const shapeId = String(match.id);
          restoredSettings[comboId] = { ...restoredSettings[comboId], boundaryZoneIds: [shapeId] };
          if (!reconstructedZoneComboConfigs[shapeId]) {
            reconstructedZoneComboConfigs[shapeId] = { selectedCombos: [], activeComboId: "", activeComboClassId: "", comboSettings: {} };
          }
          const zcc = reconstructedZoneComboConfigs[shapeId];
          if (!zcc.selectedCombos.includes(comboId)) zcc.selectedCombos.push(comboId);
          if (!zcc.activeComboId) {
            zcc.activeComboId = comboId;
            // Auto-select first class so combo class panel shows without needing a manual click
            const comboRule = comboList.find(c => getComboId(c) === comboId);
            const firstItem = (comboRule?.items || []).find(item => item.class_id);
            if (firstItem) zcc.activeComboClassId = String(firstItem.class_id);
          }
          zcc.comboSettings[comboId] = cs;
          break;
        }
      });

      // Only include truly global combos (no zone boundary) in global selectedCombos.
      // Zone-specific combos live only in zoneConfigsByImage; including them globally
      // causes zone data to appear when no zone is selected.
      const globalComboIds = _comboIds.filter(id => !_comboSettings[id]?.rawComboBoundary);
      setSelectedCombos(globalComboIds);
      setActiveComboId(globalComboIds[0] || "");
      if (globalComboIds[0]) {
        const firstGlobalCombo = comboList.find(c => getComboId(c) === globalComboIds[0]);
        const firstItem = (firstGlobalCombo?.items || []).find(item => item.class_id);
        setActiveComboClassId(firstItem ? String(firstItem.class_id) : "");
      } else {
        setActiveComboClassId("");
      }
      setComboSettings(restoredSettings); // keep all settings for zone-assign panel boundary lookup

      // Add track-line shapes to canvas
      if (_comboTrackLineShapes.length > 0) {
        setShapesByImage(prev => ({
          ...prev,
          [imgKey]: [
            ...(prev[imgKey] || []).filter(s => !s._comboShape),
            ..._comboTrackLineShapes,
          ],
        }));
      }

      // Merge reconstructed zone combo configs into zoneConfigsByImage
      if (Object.keys(reconstructedZoneComboConfigs).length > 0) {
        setZoneConfigsByImage(prev => {
          const existing = prev[imgKey] || {};
          const merged = { ...existing };
          Object.entries(reconstructedZoneComboConfigs).forEach(([shapeId, zcc]) => {
            merged[shapeId] = { ...(merged[shapeId] || {}), comboConfig: zcc };
          });
          return { ...prev, [imgKey]: merged };
        });
        // Always store combo zone memory so switching to combo tab restores the right zone,
        // regardless of whether global combos also exist.
        const firstComboZoneShapeId = Object.keys(reconstructedZoneComboConfigs)[0];
        if (firstComboZoneShapeId) {
          tabZoneMemory.current = { ...tabZoneMemory.current, combo: Number(firstComboZoneShapeId) };
        }
      }

      // ── LOAD SUMMARY LOG ──────────────────────────────────────────────────
      console.group(`[LOAD] camera ${deviceId}`);
      console.log('[LOAD][COMBO] globalComboIds (no boundary → shown globally when no zone):', globalComboIds);
      console.log('[LOAD][COMBO] zone-specific comboIds (have boundary):', _comboIds.filter(id => _comboSettings[id]?.rawComboBoundary));
      console.log('[LOAD][COMBO] reconstructedZoneComboConfigs zones:', Object.keys(reconstructedZoneComboConfigs));
      Object.entries(reconstructedZoneComboConfigs).forEach(([sid, zcc]) => {
        console.log(`  zone ${sid}: selectedCombos=${JSON.stringify(zcc.selectedCombos)}`);
      });
      console.groupEnd();
    })
    .finally(() => {
      if (isCurrentLoad()) setConfigLoading(false);
    });
};

React.useEffect(() => {
  if (selectedDevice?.deviceid) loadDeviceConfig(selectedDevice.deviceid, selectedDevice, selectedImg);
}, [selectedDevice]);

// Model config state — the api response is kept raw and the lists are derived from
// it, so switching camera re-filters the model versions without another fetch.
const [modelsData, setModelsData] = React.useState([]);
const [modelsLoading, setModelsLoading] = React.useState(true);

React.useEffect(() => {
  setModelsLoading(true);
  fetch(apiUrl('/api/v1/models'))
    .then(r => r.json())
    .then(data => {
      setModelsData(Array.isArray(data) ? data : []);
    })
    .catch(() => {
      setSnack({ open: true, severity: "error", message: "Failed to load models" });
    })
    .finally(() => setModelsLoading(false));
}, []);

const modelCatalog = React.useMemo(() => {
  // restrict === false builds the unfiltered lists used by the combo tab.
  const buildGroups = (restrict) => modelsData.map(m => {
    const modelClasses = (m.model_classes || m.classes || [])
      .filter(c => !restrict || allowedClassIds.includes(Number(c.model_class_id ?? c.class_config_id)))
      .map(c => ({
        key: String(c.model_class_id ?? c.class_config_id),
        label: c.class_display_name,
      }));
    const modelVersions = (m.model_versions || [])
      .filter(v => !restrict || allowedModelVersionIds.includes(Number(v.model_version_id)))
      .map(v => ({
        key: String(v.model_version_id),
        label: v.version_display_name,
        model_id: m.model_id,
        classes: modelClasses,
      }));
    return {
      header: m.model_name,
      model_id: m.model_id,
      classes: modelClasses,
      models: modelVersions,
    };
  }).filter(group => group.models.length > 0);

  // Collect unique classes from all models
  const buildClasses = (restrict) => {
    const classMap = new Map();
    modelsData.forEach(m => {
      (m.model_classes || m.classes || [])
        .filter(c => !restrict || allowedClassIds.includes(Number(c.model_class_id ?? c.class_config_id)))
        .forEach(c => {
        const key = c.model_class_id ?? c.class_config_id;
        if (!classMap.has(key)) {
          classMap.set(key, { key: String(key), label: c.class_display_name });
        }
      });
    });
    return Array.from(classMap.values());
  };

  const allGroups = buildGroups(false);
  const allClasses = buildClasses(false);
  return {
    allGroups,
    allClasses,
    groups: isClientRestrictedUser ? buildGroups(true) : allGroups,
    classes: isClientRestrictedUser ? buildClasses(true) : allClasses,
  };
}, [modelsData, allowedClassIds, allowedModelVersionIds, isClientRestrictedUser]);

const MODEL_GROUPS = modelCatalog.groups;
const MODEL_CLASSES = modelCatalog.classes;
const MODEL_OPTIONS = React.useMemo(() => MODEL_GROUPS.flatMap(g => g.models), [MODEL_GROUPS]);
// Unrestricted copies, used by the combo tab for label lookups only. A combo granted by
// display_combo_ids is atomic, so its classes are never hidden by display_class_ids and a
// model version already saved against one still renders by name even if it later falls
// outside display_model_version_ids. Selecting a version still honours that allowlist.
const ALL_MODEL_GROUPS = modelCatalog.allGroups;
const ALL_MODEL_CLASSES = modelCatalog.allClasses;
const ALL_MODEL_OPTIONS = React.useMemo(() => ALL_MODEL_GROUPS.flatMap(g => g.models), [ALL_MODEL_GROUPS]);

// Fetch combo list
React.useEffect(() => {
  setCombosLoading(true);
  fetch(apiUrl('/api/v1/get-combo-configs'))
    .then(r => r.json())
    .then(data => {
      const list = Array.isArray(data) ? data : [];
      // Only display_combo_ids gates combos. A combo's items are part of the rule —
      // filtering them by display_class_ids left allowed combos with no classes, which
      // hid the class chips and with them the whole model selector.
      setComboList(isClientRestrictedUser
        ? list.filter(combo => allowedComboIds.includes(Number(getComboId(combo))))
        : list
      );
    })
    .catch(() => setComboList([]))
    .finally(() => setCombosLoading(false));
}, [allowedComboIds, getComboId, isClientRestrictedUser]);

const LICENSE_PLATE_MODELS = [
  { key: "tiny", label: "Tiny" },
  { key: "small", label: "Small" },
  { key: "medium", label: "Medium" },
];
const MODEL_FORMATS = [
  { key: "onnx", label: "ONNX" },
  { key: "xml", label: "OpenVINO" },
  { key: "paddle", label: "PaddlePaddle" },
  { key: "engine", label: "TensorRT" },
  { key: "tflite", label: "TFLite" },
  { key: "pt", label: "PyTorch" },
];

const DEFAULT_TRACKING = { enabled: true, tracking_config_id: 0, min_distance: defaultMinDistance, min_duration_sec: defaultMinDurationSec, max_disappeared: defaultMaxDisappeared, direction: "both", fps: defaultTrackingFps, count_objects: false, ignore_motion_after_tracking: defaultIgnoreMotionAfterTracking, ignore_motion_after_tracking_time: defaultIgnoreMotionAfterTrackingTime, enable_single_track_video: false, track_direction: "TTB", track_line: null, zoneIds: [], countShapeIds: [], schedule: [],
  enable_license_plate: false, license_plate_model: "small", license_plate_conf: 0.5, license_plate_ip: "127.0.0.1", license_plate_port: "50051" };

// DEFAULT_TRACKING with the enabled flag this model version starts on and the min
// distance this class starts on.
const defaultTrackingFor = (modelVersionKey, classKey) => ({
  ...DEFAULT_TRACKING,
  enabled: trackingEnabledByDefault(modelVersionKey),
  min_distance: minDistanceForClass(classKey),
});

// Format a model version defaults to. Models listed in the client's
// pytorch_model_ids have no TensorRT build, so they stay on PyTorch; everything
// else takes the client's default. Unknown or unchosen versions take the default.
// A model whose model_id sits in pytorch_model_ids stays "pt" unless the config
// names that exact model version against the camera's server.
const modelFormatForDevice = (device, modelVersionKey) => {
  const { byVersion, fallback } = modelFormatConfigFor(device);
  const explicit = byVersion[String(modelVersionKey)];
  if (explicit) return explicit;
  const option = ALL_MODEL_OPTIONS.find(o => o.key === String(modelVersionKey));
  if (option && pytorchModelIds.includes(Number(option.model_id))) return "pt";
  return fallback;
};
const modelFormatFor = (modelVersionKey) => modelFormatForDevice(selectedDevice, modelVersionKey);

const DEFAULT_MODEL_SETTINGS = {
  conf: DEFAULT_CONF, iou: DEFAULT_IOU, detect_fps: DEFAULT_DETECT_FPS, motion_sensitivity: DEFAULT_MOTION_SENSITIVITY,
  augment: false, agnostic_nms: false, is_zoom_motion_area: false, enable_detect_always: false, enable_motion: true,
  model_format: defaultModelFormat,
  comm_type: "grpc", comm_ip: defaultCommIp, comm_port: "50051", comm_url: "", comm_path: "",
  classNames: [],
  trackingByClass: {},
};


const [selectedModels, setSelectedModels] = React.useState([]);
const [activeModel, setActiveModel] = React.useState("");
const [modelSettings, setModelSettings] = React.useState({});


// Detection classes — MODEL_CLASSES fetched from API above

// Per-boundary (shape) config
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const [activeClassName, setActiveClassName] = React.useState("");

// Popover anchors
const [modelPopoverAnchor, setModelPopoverAnchor] = React.useState(null);
const [classPopoverAnchor, setClassPopoverAnchor] = React.useState(null);
const [filterPopoverAnchor, setFilterPopoverAnchor] = React.useState(null);
const [zoneConfigsByImage, setZoneConfigsByImage] = React.useState({});
const [zonesOpen, setZonesOpen] = React.useState(true);
const [configTab, setConfigTab] = React.useState("model");
const tabZoneMemory = React.useRef({ model: undefined, combo: undefined });
const zonesRef = React.useRef(null);
const rightPanelRef = React.useRef(null);
const [filterSelectAnchor, setFilterSelectAnchor] = React.useState(null);
const [filterListSearch, setFilterListSearch] = React.useState("");

// ---- COMBO CONFIG STATE ----
const [comboList, setComboList] = React.useState([]);           // from /get-combo-configs
const [combosLoading, setCombosLoading] = React.useState(true);

// Re-run device config load once the models, the combo list and the server id
// aliases have finished loading, in case the device was selected before these
// async lists were ready (race condition).
React.useEffect(() => {
  if (!selectedDevice?.deviceid || modelsLoading || combosLoading || !serverAliasesLoaded) return;
  loadDeviceConfig(selectedDevice.deviceid, selectedDevice, selectedImg);
}, [modelsLoading, combosLoading, serverAliasesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

const [selectedCombos, setSelectedCombos] = React.useState([]);  // combo rule IDs (strings)
const [activeComboId, setActiveComboId] = React.useState("");
const [comboSettings, setComboSettings] = React.useState({});    // { comboId: { comboConfigId, classModels, trackingEnabled, tracking, schedule } }
const [comboPopoverAnchor, setComboPopoverAnchor] = React.useState(null);
const [activeComboClassId, setActiveComboClassId] = React.useState("");
const [comboClassModelPopoverAnchor, setComboClassModelPopoverAnchor] = React.useState(null);

// Accordion open state — driven by data, user can still toggle manually
const [modelSettingsAccOpen, setModelSettingsAccOpen] = React.useState(true);
const [detectionClassesAccOpen, setDetectionClassesAccOpen] = React.useState(true);
const [comboClassAccOpen, setComboClassAccOpen] = React.useState(false);
const [comboTrackAccOpen, setComboTrackAccOpen] = React.useState(false);
const [comboMonAccOpen, setComboMonAccOpen] = React.useState(false);
const [modelMonAccOpen, setModelMonAccOpen] = React.useState(false);

React.useEffect(() => {
  const zoneComboCount = selectedShapeId && selectedImg
    ? (zoneConfigsByImage[selectedImg]?.[selectedShapeId]?.comboConfig?.selectedCombos?.length || 0)
    : 0;
  setComboClassAccOpen(selectedCombos.length > 0 || zoneComboCount > 0);
  const effActiveComboId = zoneConfigsByImage[selectedImg]?.[selectedShapeId]?.comboConfig?.activeComboId || activeComboId;
  const effComboSettings = zoneConfigsByImage[selectedImg]?.[selectedShapeId]?.comboConfig?.comboSettings || comboSettings;
  setComboTrackAccOpen(Boolean(effActiveComboId && effComboSettings[effActiveComboId]?.trackingEnabled));
  setComboMonAccOpen(Boolean(effActiveComboId && (effComboSettings[effActiveComboId]?.schedule || []).length > 0));
}, [activeComboId, selectedCombos, comboSettings, zoneConfigsByImage, selectedShapeId, selectedImg]); // eslint-disable-line react-hooks/exhaustive-deps

React.useEffect(() => {
  const sched = (activeModel && activeClassName)
    ? (modelSettings[activeModel]?.trackingByClass?.[activeClassName]?.schedule || [])
    : [];
  setModelMonAccOpen(sched.length > 0);
}, [activeModel, activeClassName, modelSettings]); // eslint-disable-line react-hooks/exhaustive-deps

const DEFAULT_COMBO_CLASS_MODEL = {
  model_config_id: 0, model_version_id: defaultComboModelVersionId,
  conf: DEFAULT_CONF, iou: DEFAULT_IOU, detect_fps: defaultComboDetectFps, motion_sensitivity: DEFAULT_MOTION_SENSITIVITY,
  augment: false, agnostic_nms: false, is_zoom_motion_area: false, enable_detect_always: false, enable_motion: true,
  model_format: defaultComboModelVersionId ? modelFormatFor(defaultComboModelVersionId) : defaultModelFormat,
  zoneIds: [],
  comm_type: "grpc", comm_ip: defaultCommIp, comm_port: "50051", comm_url: "", comm_path: "",
};
const DEFAULT_COMBO_TRACKING_CFG = {
  tracking_config_id: 0, track_iou: 0.3, track_max_disappeared: defaultComboMaxDisappeared,
  tracking_fps: defaultComboTrackingFps, min_track_distance: defaultMinDistance, min_track_duration_sec: defaultComboMinDurationSec,
  enable_object_count: false, track_direction: null, track_line: null,
  comboTrackLineIds: [], rawTrackLine: null,
};
const DEFAULT_COMBO_CFG = { comboConfigId: 0, classModels: {}, trackingEnabled: defaultComboTrackingEnabled, tracking: { ...DEFAULT_COMBO_TRACKING_CFG }, schedule: [], syncDetectionConfig: false, boundaryZoneIds: [] };
const EMPTY_ZONE_COMBO = { selectedCombos: [], activeComboId: "", activeComboClassId: "", comboSettings: {} };

// derived shapes for current image
const shapes = shapesByImage[selectedImg] || [];


const countableShapesWithLabel = React.useMemo(() => {
  return shapes
    .map((s, idx) => ({ ...s, _idx: idx }))
    .filter(s => s.category !== "ignore")
    .map(s => {
      const typeLabel = s.type === "line" ? "Line" : s.type === "rect" ? "Rect" : "Poly";
      return { id: String(s.id), label: s.label || `${typeLabel} ${s._idx + 1}`, type: s.type };
    });
}, [shapes]);

  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/');
  };

  // ---- HISTORY HELPERS ----
  const pruneZoneConfigsForImage = (image, newShapes) => {
    setZoneConfigsByImage(prev => {
      const imgConfigs = prev[image];
      if (!imgConfigs) return prev;
      const keep = new Set(newShapes.map(s => String(s.id)));
      let changed = false;
      const nextImgConfigs = {};
      Object.entries(imgConfigs).forEach(([id, cfg]) => {
        if (keep.has(id)) nextImgConfigs[id] = cfg;
        else changed = true;
      });
      if (!changed) return prev;
      return { ...prev, [image]: nextImgConfigs };
    });
  };

  const pushHistory = (newShapes) => {
    setShapesByImage(prev => ({
      ...prev,
      [selectedImg]: newShapes
    }));
    if (selectedImg) pruneZoneConfigsForImage(selectedImg, newShapes);
    pruneStaleZoneIds(newShapes);
  };
  const undo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setFuture(f => [shapes, ...f]);
    setHistory(h => h.slice(0, -1));
    setShapesByImage(s => ({ ...s, [selectedImg]: prev }));
  };

  const redo = () => {
    if (future.length === 0) return;
    const next = future[0];
    setHistory(h => [...h, shapes]);
    setFuture(f => f.slice(1));
    setShapesByImage(s => ({ ...s, [selectedImg]: next }));
  };

  // ---- CANVAS DRAWING ----
  const TYPE_COLORS = {
    line:   { stroke: "#2563EB", fill: "none",                    selected: "#60A5FA", selectedFill: "none",                       dot: "#2563EB" },
    zone:   { stroke: "#10B981", fill: "rgba(16,185,129,0.12)",   selected: "#34D399", selectedFill: "rgba(52,211,153,0.18)",      dot: "#10B981" },
    ignore: { stroke: "#EF4444", fill: "rgba(239,68,68,0.10)",    selected: "#F87171", selectedFill: "rgba(248,113,113,0.16)",     dot: "#EF4444" },
  };
  const getShapeColor = (s) => {
    if (s.category === "ignore") return TYPE_COLORS.ignore;
    if (s.type === "line") return TYPE_COLORS.line;
    return TYPE_COLORS.zone;
  };

// Per-class colors for assigned zones (cycle through these)
const CLASS_ZONE_COLORS = [
  { stroke: "#8B5CF6", fill: "rgba(139,92,246,0.15)", selected: "#A78BFA", selectedFill: "rgba(167,139,250,0.22)" },
  { stroke: "#EC4899", fill: "rgba(236,72,153,0.15)", selected: "#F472B6", selectedFill: "rgba(244,114,182,0.22)" },
  { stroke: "#F59E0B", fill: "rgba(245,158,11,0.15)", selected: "#FBBF24", selectedFill: "rgba(251,191,36,0.22)" },
  { stroke: "#06B6D4", fill: "rgba(6,182,212,0.15)",  selected: "#22D3EE", selectedFill: "rgba(34,211,238,0.22)" },
  { stroke: "#F97316", fill: "rgba(249,115,22,0.15)", selected: "#FB923C", selectedFill: "rgba(251,146,60,0.22)" },
  { stroke: "#14B8A6", fill: "rgba(20,184,166,0.15)", selected: "#2DD4BF", selectedFill: "rgba(45,212,191,0.22)" },
];

const redrawCanvas = () => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const w = imgSize.width;
  const h = imgSize.height;

  const displayRect = canvas.getBoundingClientRect();
  const scale = displayRect.width > 0 ? canvas.width / displayRect.width : 1;
  const handleR = 4 * scale;

  // Build shape-to-class assignment map: shapeId → { label, colorIdx }
  const shapeClassMap = {};
  let colorCounter = 0;

  // 1. From zone-specific configs (runtime zone assignments)
  const imgZoneConfigs = (selectedImg ? zoneConfigsByImage[selectedImg] : null) || {};
  Object.entries(imgZoneConfigs).forEach(([zoneShapeId, zoneConfig]) => {
    (zoneConfig.selectedModels || []).forEach(mKey => {
      const zoneMs = zoneConfig.modelSettings?.[mKey] || {};
      (zoneMs.classNames || []).forEach(cKey => {
        const modelLabel = MODEL_OPTIONS.find(o => o.key === mKey)?.label || mKey;
        const clsLabel = MODEL_CLASSES.find(c => c.key === cKey)?.label || cKey;
        const colorIdx = (colorCounter++) % CLASS_ZONE_COLORS.length;
        if (!shapeClassMap[zoneShapeId]) {
          shapeClassMap[zoneShapeId] = { label: `${modelLabel} › ${clsLabel}`, colorIdx };
        }
      });
    });
  });

  // 2. From global modelSettings (populated from API detection_area match on load)
  Object.entries(modelSettings).forEach(([mKey, ms]) => {
    const classNames = ms.classNames || [];
    classNames.forEach((cKey, cIdx) => {
      const t = ms.trackingByClass?.[cKey];
      const modelLabel = MODEL_OPTIONS.find(o => o.key === mKey)?.label || mKey;
      const clsLabel = MODEL_CLASSES.find(c => c.key === cKey)?.label || cKey;
      const colorIdx = cIdx % CLASS_ZONE_COLORS.length;
      (t?.zoneIds || []).forEach(zId => {
        if (!shapeClassMap[zId]) {
          shapeClassMap[zId] = { label: `${modelLabel} › ${clsLabel}`, colorIdx };
        }
      });
      (t?.countShapeIds || []).forEach(cId => {
        if (!shapeClassMap[cId]) {
          shapeClassMap[cId] = { label: `${clsLabel}-Count`, colorIdx };
        }
      });
    });
  });

  // 3. Combo boundary assignments — label zones used as combo detection boundaries
  selectedCombos.forEach(comboId => {
    const cs = comboSettings[comboId] || DEFAULT_COMBO_CFG;
    const combo = comboList.find(c => String(c.id) === comboId);
    const comboName = combo?.display_name || combo?.combo_name || `Combo ${comboId}`;
    (cs.boundaryZoneIds || []).forEach(zId => {
      const comboLabel = `⬡ ${comboName}`;
      if (!shapeClassMap[zId]) {
        shapeClassMap[zId] = { label: comboLabel, colorIdx: (colorCounter++) % CLASS_ZONE_COLORS.length };
      } else {
        shapeClassMap[zId].label += `  ${comboLabel}`;
      }
    });
  });

  const drawHandle = (x, y, color) => {
    ctx.beginPath();
    ctx.arc(x, y, handleR, 0, Math.PI * 2);
    ctx.fillStyle = "#fff";
    ctx.fill();
    ctx.lineWidth = 1.5 * scale;
    ctx.strokeStyle = color;
    ctx.stroke();
  };

  shapes.forEach((s, idx) => {
    const isSelected = s.id === selectedShapeId;
    const isHovered = hoveredZoneId != null && String(s.id) === hoveredZoneId;
    const classAssign = s.category !== "ignore" ? shapeClassMap[String(s.id)] : null;
    const assignPalette = classAssign ? CLASS_ZONE_COLORS[classAssign.colorIdx] : null;
    const palette = assignPalette || getShapeColor(s);
    const strokeColor = isHovered ? "#F59E0B" : isSelected ? palette.selected : palette.stroke;
    const fillColor = isHovered ? "rgba(245,158,11,0.25)" : isSelected ? palette.selectedFill : palette.fill;

    ctx.lineWidth = isSelected ? 2.5 * scale : 1.5 * scale;
    ctx.strokeStyle = strokeColor;
    ctx.setLineDash([]);

    if (s.type === "line") {
      ctx.lineWidth = (isSelected || isHovered || classAssign) ? 2.5 * scale : 1.5 * scale;
      if (classAssign) { ctx.setLineDash([6 * scale, 4 * scale]); }
      ctx.beginPath();
      ctx.moveTo(s.x1 * w, s.y1 * h);
      ctx.lineTo(s.x2 * w, s.y2 * h);
      ctx.stroke();
      ctx.setLineDash([]);
      // Label
      const mx = (s.x1 + s.x2) / 2 * w;
      const my = (s.y1 + s.y2) / 2 * h;
      ctx.font = `${Math.max(10, 10 * scale)}px Inter, system-ui, sans-serif`;
      ctx.fillStyle = strokeColor;
      ctx.textAlign = "center";
      ctx.fillText(classAssign ? classAssign.label : (s.label || `Line ${idx + 1}`), mx, my - 6 * scale);
      ctx.textAlign = "start";
      // endpoints
      [{ x: s.x1, y: s.y1 }, { x: s.x2, y: s.y2 }].forEach(pt => {
        ctx.beginPath();
        ctx.arc(pt.x * w, pt.y * h, 4 * scale, 0, Math.PI * 2);
        ctx.fillStyle = strokeColor;
        ctx.fill();
        ctx.lineWidth = 1.5 * scale;
        ctx.strokeStyle = "#fff";
        ctx.stroke();
      });
      ctx.strokeStyle = strokeColor;
      if (isSelected) {
        drawHandle(s.x1 * w, s.y1 * h, strokeColor);
        drawHandle(s.x2 * w, s.y2 * h, strokeColor);
      }
      return;
    }

    if (s.type === "rect") {
      const rx = s.x * w, ry = s.y * h, rw = s.w * w, rh = s.h * h;
      ctx.fillStyle = fillColor;
      ctx.fillRect(rx, ry, rw, rh);
      ctx.strokeRect(rx, ry, rw, rh);
      // Label
      ctx.font = `${Math.max(11, 11 * scale)}px Inter, system-ui, sans-serif`;
      ctx.fillStyle = strokeColor;
      ctx.fillText(classAssign ? classAssign.label : (s.label || (s.category === "ignore" ? `Ignore ${idx + 1}` : `Zone ${idx + 1}`)), rx + 4 * scale, ry - 4 * scale > 14 * scale ? ry - 4 * scale : ry + 14 * scale);
      if (isSelected) {
        drawHandle(rx, ry, strokeColor);
        drawHandle(rx + rw, ry, strokeColor);
        drawHandle(rx, ry + rh, strokeColor);
        drawHandle(rx + rw, ry + rh, strokeColor);
      }
      return;
    }

    if (s.type === "poly" && s.points.length >= 3) {
      ctx.beginPath();
      ctx.moveTo(s.points[0].x * w, s.points[0].y * h);
      s.points.forEach(p => ctx.lineTo(p.x * w, p.y * h));
      ctx.closePath();
      ctx.fillStyle = fillColor;
      ctx.fill();
      ctx.stroke();
      // Label
      const cx = s.points.reduce((a, p) => a + p.x, 0) / s.points.length * w;
      const cy = s.points.reduce((a, p) => a + p.y, 0) / s.points.length * h;
      ctx.font = `${Math.max(11, 11 * scale)}px Inter, system-ui, sans-serif`;
      ctx.fillStyle = strokeColor;
      ctx.textAlign = "center";
      ctx.fillText(classAssign ? classAssign.label : (s.label || (s.category === "ignore" ? `Ignore ${idx + 1}` : `Zone ${idx + 1}`)), cx, cy);
      ctx.textAlign = "start";
      if (isSelected) {
        s.points.forEach(p => drawHandle(p.x * w, p.y * h, strokeColor));
      }
      return;
    }

    ctx.stroke();
  });

};

React.useEffect(() => {
  let mounted = true;

  if (mounted) {
    redrawCanvas();
  }

  return () => {
    mounted = false;   // ✅ prevents updates after unmount
  };
}, [shapesByImage, selectedImg, selectedShapeId, hoveredZoneId, modelSettings, zoneConfigsByImage, selectedCombos, comboSettings, comboList, activeModel, activeClassName, imgSize, zoom, configLoading]);

  const getMousePos = (e) => {
  const canvas = canvasRef.current;
  if (!canvas) return { x: 0, y: 0 };

  const rect = canvas.getBoundingClientRect();
  return {
    x: (e.clientX - rect.left) * (canvas.width / rect.width),
    y: (e.clientY - rect.top) * (canvas.height / rect.height),
  };
};

const handleMouseDown = (e) => {
  // In select/pan mode, don't draw
  if (mode === "select" || mode === "pan" || !mode) return;
  // Don't draw when no image is available
  if (imgBroken || !activeSnapshot) return;

  const p = getMousePos(e);
  const w = imgSize.width;
  const h = imgSize.height;

  // Check if clicking on existing rect to drag (only in non-select modes)
  const hit = shapes.find(s =>
    s.type === "rect" &&
    p.x >= s.x * w &&
    p.x <= (s.x + s.w) * w &&
    p.y >= s.y * h &&
    p.y <= (s.y + s.h) * h
  );

  if (hit) {
    setDraggingShape(hit.id);
    setDragOffset({
      x: p.x - hit.x * w,
      y: p.y - hit.y * h,
    });
    return;
  }

  if (mode === "poly" || mode === "ignore_poly") {
    setPolyPoints(pp => [...pp, p]);
    return;
  }

  setStart(p);
  setDrawing(true);
};

  const handleMouseUp = (e) => {
    if (draggingShape) {
  setDraggingShape(null);
  return;
}
  if (!drawing || !start) return;
  if (imgBroken || !activeSnapshot) return;
  const end = getMousePos(e);

  const w = imgSize.width;
  const h = imgSize.height;

  let newShape = null;

  if (mode === "line") {
    newShape = {
      id: Date.now(),
      type: "line",
      category: "zone",
      x1: start.x / w,
      y1: start.y / h,
      x2: end.x / w,
      y2: end.y / h,
    };
  }

  if (mode === "rect" || mode === "ignore_rect") {
    newShape = {
      id: Date.now(),
      type: "rect",
      category: mode === "ignore_rect" ? "ignore" : "zone",
      x: start.x / w,
      y: start.y / h,
      w: (end.x - start.x) / w,
      h: (end.y - start.y) / h,
    };
  }

  if (newShape) {
    pushHistory([...shapes, newShape]);
  }

  setDrawing(false);
  setStart(null);
};

const handleDoubleClick = () => {
  if (imgBroken || !activeSnapshot) return;
  if ((mode === "poly" || mode === "ignore_poly") && polyPoints.length >= 3) {
    const w = imgSize.width;
    const h = imgSize.height;

    const normalizedPoints = polyPoints.map(p => ({
      x: p.x / w,
      y: p.y / h,
    }));

    const newShape = {
      id: Date.now(),
      type: "poly",
      category: mode === "ignore_poly" ? "ignore" : "zone",
      points: normalizedPoints,
    };

    pushHistory([...shapes, newShape]);
    setPolyPoints([]);
  }
};

// Point-to-line-segment distance
const distToSegment = (px, py, x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
};

// Point-in-polygon (ray casting)
const pointInPolygon = (px, py, points) => {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x, yi = points[i].y;
    const xj = points[j].x, yj = points[j].y;
    if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
};

const handleCanvasClick = (e) => {
  if (mode !== "select") return;

  const p = getMousePos(e);
  const w = imgSize.width;
  const h = imgSize.height;

  const canvas = canvasRef.current;
  const displayRect = canvas ? canvas.getBoundingClientRect() : { width: 1 };
  const scale = canvas && displayRect.width > 0 ? canvas.width / displayRect.width : 1;
  const hitThreshold = 10 * scale;

  let found = null;

  shapes.forEach(s => {
    if (s.type === "rect") {
      if (
        p.x >= s.x * w &&
        p.x <= (s.x + s.w) * w &&
        p.y >= s.y * h &&
        p.y <= (s.y + s.h) * h
      ) {
        found = s.id;
      }
    }

    if (s.type === "line") {
      const dist = distToSegment(p.x, p.y, s.x1 * w, s.y1 * h, s.x2 * w, s.y2 * h);
      if (dist < hitThreshold) {
        found = s.id;
      }
    }

    if (s.type === "poly" && s.points.length >= 3) {
      const absPoints = s.points.map(pt => ({ x: pt.x * w, y: pt.y * h }));
      if (pointInPolygon(p.x, p.y, absPoints)) {
        found = s.id;
      }
    }
  });

  setSelectedShapeId(found);
};

const handleMouseMove = (e) => {
  const p = getMousePos(e);

  // Handle dragging a shape
  if (draggingShape) {
    const w = imgSize.width;
    const h = imgSize.height;

    const updated = shapes.map(s => {
      if (s.id === draggingShape && s.type === "rect") {
        return {
          ...s,
          x: (p.x - dragOffset.x) / w,
          y: (p.y - dragOffset.y) / h,
        };
      }
      return s;
    });

    setShapesByImage(prev => ({
      ...prev,
      [selectedImg]: updated
    }));
    return;
  }

  // Preview for line/rect while actively drawing
  if (drawing && start) {
    drawPreview(start, p);
  }

  // Preview for polygon points
  if ((mode === "poly" || mode === "ignore_poly") && polyPoints.length > 0) {
    redrawCanvas();
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      const displayRect = canvas.getBoundingClientRect();
      const scale = displayRect.width > 0 ? canvas.width / displayRect.width : 1;
      const polyColor = mode === "ignore_poly" ? "#EF4444" : "#10B981";

      ctx.lineWidth = 2 * scale;
      ctx.strokeStyle = polyColor;
      ctx.setLineDash([5 * scale, 5 * scale]);
      ctx.beginPath();
      ctx.moveTo(polyPoints[0].x, polyPoints[0].y);
      polyPoints.slice(1).forEach(pt => ctx.lineTo(pt.x, pt.y));
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw dots at each clicked point
      polyPoints.forEach(pt => {
        ctx.fillStyle = polyColor;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 4 * scale, 0, Math.PI * 2);
        ctx.fill();
      });
    }
  }
};


  const drawPreview = (start, current) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  redrawCanvas(); // clear + redraw existing shapes

  const displayRect = canvas.getBoundingClientRect();
  const scale = displayRect.width > 0 ? canvas.width / displayRect.width : 1;

  const previewColor = mode === "line" ? "#2563EB" : mode === "ignore_rect" ? "#EF4444" : "#10B981";
  ctx.lineWidth = 2 * scale;
  ctx.strokeStyle = previewColor;
  ctx.beginPath();

  if (mode === "line") {
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(current.x, current.y);
  }

  if (mode === "rect" || mode === "ignore_rect") {
    ctx.strokeRect(
      start.x,
      start.y,
      current.x - start.x,
      current.y - start.y
    );
  }

  ctx.stroke();
};

// ---- CONFIG CONTEXT HELPERS ----
const setCurrentSelectedModels = (updater) => {
  if (useZoneConfig) {
    updateZoneConfig(selectedShapeId, (cfg) => ({
      ...cfg,
      selectedModels: typeof updater === "function" ? updater(cfg?.selectedModels || []) : updater,
    }));
  } else {
    setSelectedModels(updater);
  }
};

const setCurrentActiveModel = (value) => {
  if (useZoneConfig) {
    updateZoneConfig(selectedShapeId, (cfg) => ({ ...cfg, activeModel: value }));
  } else {
    setActiveModel(value);
  }
};

const setCurrentActiveClassName = (value) => {
  if (useZoneConfig) {
    updateZoneConfig(selectedShapeId, (cfg) => ({ ...cfg, activeClassName: value }));
  } else {
    setActiveClassName(value);
  }
};

// Switching to a model that carries exactly one class activates that class too, so its
// tracking panel opens without a second click on the lone chip.
const activateModel = (modelKey) => {
  setCurrentActiveModel(modelKey);
  const names = getCurrentModelSettingsMap()[modelKey]?.classNames || [];
  if (names.length === 1) setCurrentActiveClassName(names[0]);
};

const setCurrentModelSettingsMap = (updater) => {
  if (useZoneConfig) {
    updateZoneConfig(selectedShapeId, (cfg) => ({
      ...cfg,
      modelSettings: typeof updater === "function" ? updater(cfg?.modelSettings || {}) : updater,
    }));
  } else {
    setModelSettings(updater);
  }
};

const collectModelReferencedShapeIds = (settingsMap) => {
  const ids = new Set();
  Object.values(settingsMap || {}).forEach((ms) => {
    Object.values(ms?.trackingByClass || {}).forEach((t) => {
      (t?.zoneIds || []).forEach(id => ids.add(String(id)));
      (t?.countShapeIds || []).forEach(id => ids.add(String(id)));
      if (t?.track_line != null) ids.add(String(t.track_line));
    });
  });
  return ids;
};

const collectSelectedComboReferencedShapeIds = (settingsMap, selectedIds) => {
  const ids = new Set();
  (selectedIds || []).forEach((comboId) => {
    const cs = settingsMap?.[comboId];
    if (!cs) return;
    Object.values(cs.classModels || {}).forEach((cm) => {
      (cm?.zoneIds || []).forEach(id => ids.add(String(id)));
    });
    (cs?.tracking?.comboTrackLineIds || []).forEach(id => ids.add(String(id)));
    if (cs?.tracking?.track_line != null) ids.add(String(cs.tracking.track_line));
  });
  return ids;
};

const removeShapeIdsFromCanvas = (candidateIds, referencedIds) => {
  if (!selectedImg || !candidateIds || candidateIds.size === 0) return;
  setShapesByImage(prev => {
    const current = prev[selectedImg] || [];
    const next = current.filter(s => {
      const id = String(s.id);
      if (!candidateIds.has(id)) return true;
      return referencedIds.has(id);
    });
    if (next.length === current.length) return prev;
    return { ...prev, [selectedImg]: next };
  });
};

// ---- MODEL HELPERS ----
// A model version exposing a single detection class leaves nothing to pick, so it is
// selected up front instead of making the user open the class popover for it.
const soleClassKeyFor = (modelKey) => {
  const classes = MODEL_OPTIONS.find(m => m.key === modelKey)?.classes || [];
  return classes.length === 1 ? classes[0].key : "";
};

const toggleModelInPopover = (modelKey) => {
  const currentSelectedModels = getCurrentSelectedModels();
  const currentActiveModel = getCurrentActiveModel();
  if (currentSelectedModels.includes(modelKey)) {
    if (currentSelectedModels.length <= 1) return;
    const next = currentSelectedModels.filter(m => m !== modelKey);
    setCurrentSelectedModels(next);
    if (currentActiveModel === modelKey) setCurrentActiveModel(next[0]);
  } else {
    const soleClassKey = soleClassKeyFor(modelKey);
    const defaultClassNames = soleClassKey ? [soleClassKey] : [];
    const defaultTrackingByClass = Object.fromEntries(
      defaultClassNames.map(k => [k, defaultTrackingFor(modelKey, k)])
    );
    setCurrentSelectedModels(prev => [...prev, modelKey]);
    setCurrentModelSettingsMap(prev => prev[modelKey] ? prev : {
      ...prev,
      [modelKey]: {
        ...DEFAULT_MODEL_SETTINGS,
        model_format: modelFormatFor(modelKey),
        classNames: defaultClassNames,
        trackingByClass: defaultTrackingByClass,
      },
    });
  }
};

const removeModel = (modelKey) => {
  const currentSelectedModels = getCurrentSelectedModels();
  const currentActiveModel = getCurrentActiveModel();
  const currentSettingsMap = getCurrentModelSettingsMap();
  const removedModelSettings = currentSettingsMap?.[modelKey];
  const removedShapeIds = collectModelReferencedShapeIds(removedModelSettings ? { [modelKey]: removedModelSettings } : {});
  const nextSettingsMap = { ...(currentSettingsMap || {}) };
  delete nextSettingsMap[modelKey];
  setCurrentModelSettingsMap(nextSettingsMap);

  // Only clean canvas in global mode. Zone-config mode must not mutate global shapes.
  if (!useZoneConfig && removedShapeIds.size > 0) {
    const stillReferenced = new Set([
      ...collectModelReferencedShapeIds(nextSettingsMap),
      ...collectSelectedComboReferencedShapeIds(comboSettings, selectedCombos),
    ]);
    removeShapeIdsFromCanvas(removedShapeIds, stillReferenced);
  }

  const next = currentSelectedModels.filter(m => m !== modelKey);
  setCurrentSelectedModels(next);
  if (currentActiveModel === modelKey) setCurrentActiveModel(next[0] || "");
  if (next.length === 0) setCurrentActiveClassName("");
};

const updateActiveModelField = (field, value) => {
  const currentActiveModel = getCurrentActiveModel();
  if (!currentActiveModel) return;
  setCurrentModelSettingsMap(prev => ({
    ...prev,
    [currentActiveModel]: { ...(prev[currentActiveModel] || DEFAULT_MODEL_SETTINGS), [field]: value },
  }));
};

const getActiveModelSettings = () => {
  const currentActiveModel = getCurrentActiveModel();
  const ms = getCurrentModelSettingsMap();
  return ms[currentActiveModel] || DEFAULT_MODEL_SETTINGS;
};


// ---- PER-MODEL CLASS HELPERS ----
const getActiveModelClasses = () => {
  const currentActiveModel = getCurrentActiveModel();
  const ms = getCurrentModelSettingsMap()[currentActiveModel];
  return ms?.classNames || [];
};

const toggleClassInPopover = (classKey) => {
  const currentActiveModel = getCurrentActiveModel();
  if (!currentActiveModel) return;
  setCurrentModelSettingsMap(prev => {
    const ms = prev[currentActiveModel] || { ...DEFAULT_MODEL_SETTINGS };
    const names = ms.classNames || [];
    if (names.includes(classKey)) {
      if (names.length <= 1) return prev;
      const next = names.filter(c => c !== classKey);
      if (!next.includes(getCurrentActiveClassName())) setCurrentActiveClassName(next[0]);
      return { ...prev, [currentActiveModel]: { ...ms, classNames: next } };
    } else {
      const trackingByClass = { ...(ms.trackingByClass || {}) };
      if (!trackingByClass[classKey]) {
        // Copy config from first existing class, fallback to this model version's defaults
        const firstKey = names[0];
        const source = (firstKey && trackingByClass[firstKey]) ? trackingByClass[firstKey] : defaultTrackingFor(currentActiveModel, classKey);
        const cloned = JSON.parse(JSON.stringify(source));
        cloned.tracking_config_id = 0;
        // A class the client pins a distance for keeps that distance, not the one copied
        // off the class it was cloned from.
        if (minDistanceByClassId?.[String(classKey)] != null) cloned.min_distance = minDistanceForClass(classKey);
        // Reset all monitoring time IDs so backend creates new records
        (cloned.schedule || []).forEach(range => { range.dayIds = {}; });
        trackingByClass[classKey] = cloned;
      }
      return { ...prev, [currentActiveModel]: { ...ms, classNames: [...names, classKey], trackingByClass } };
    }
  });
};

const removeModelClass = (classKey) => {
  const currentActiveModel = getCurrentActiveModel();
  if (!currentActiveModel) return;
  const currentSettingsMap = getCurrentModelSettingsMap();
  const ms = currentSettingsMap[currentActiveModel] || { ...DEFAULT_MODEL_SETTINGS };
  const names = ms.classNames || [];
  if (names.length <= 1) return;

  const removedTracking = ms.trackingByClass?.[classKey] || {};
  const removedShapeIds = new Set();
  (removedTracking?.zoneIds || []).forEach(id => removedShapeIds.add(String(id)));
  (removedTracking?.countShapeIds || []).forEach(id => removedShapeIds.add(String(id)));
  if (removedTracking?.track_line != null) removedShapeIds.add(String(removedTracking.track_line));

  const nextClassNames = names.filter(c => c !== classKey);
  const nextTrackingByClass = { ...(ms.trackingByClass || {}) };
  delete nextTrackingByClass[classKey];
  const nextClassConfigIdMap = { ...(ms.classConfigIdMap || {}) };
  delete nextClassConfigIdMap[classKey];
  const nextSettingsMap = {
    ...(currentSettingsMap || {}),
    [currentActiveModel]: {
      ...ms,
      classNames: nextClassNames,
      trackingByClass: nextTrackingByClass,
      classConfigIdMap: nextClassConfigIdMap,
    },
  };
  setCurrentModelSettingsMap(nextSettingsMap);

  if (!nextClassNames.includes(getCurrentActiveClassName())) setCurrentActiveClassName(nextClassNames[0]);

  // Only clean canvas in global mode. Zone-config mode must not mutate global shapes.
  if (!useZoneConfig && removedShapeIds.size > 0) {
    const stillReferenced = new Set([
      ...collectModelReferencedShapeIds(nextSettingsMap),
      ...collectSelectedComboReferencedShapeIds(comboSettings, selectedCombos),
    ]);
    removeShapeIdsFromCanvas(removedShapeIds, stillReferenced);
  }
};

const getActiveClassTracking = () => {
  const currentActiveModel = getCurrentActiveModel();
  const ms = getCurrentModelSettingsMap()[currentActiveModel];
  if (!ms) return defaultTrackingFor(currentActiveModel, getCurrentActiveClassName());
  return ms.trackingByClass?.[getCurrentActiveClassName()] || defaultTrackingFor(currentActiveModel, getCurrentActiveClassName());
};

const updateModelClassTracking = (field, value) => {
  const currentActiveModel = getCurrentActiveModel();
  const currentActiveClassName = getCurrentActiveClassName();
  if (!currentActiveModel) return;
  setCurrentModelSettingsMap(prev => {
    const ms = prev[currentActiveModel] || { ...DEFAULT_MODEL_SETTINGS };
    const clsTracking = ms.trackingByClass?.[currentActiveClassName] || defaultTrackingFor(currentActiveModel, currentActiveClassName);
    return {
      ...prev,
      [currentActiveModel]: {
        ...ms,
        trackingByClass: {
          ...(ms.trackingByClass || {}),
          [currentActiveClassName]: { ...clsTracking, [field]: value },
        },
      },
    };
  });
};

// ---- PER-CLASS ZONE ASSIGNMENT HELPERS ----
// Zone assignments always operate on global modelSettings, never per-shape zone config.
// ---- PER-CLASS COUNT BOUNDARY HELPERS ----
const getActiveClassCountShapeIds = () => {
  const currentActiveModel = getCurrentActiveModel();
  const currentActiveClassName = getCurrentActiveClassName();
  const ms = modelSettings[currentActiveModel];
  if (!ms) return [];
  return ms.trackingByClass?.[currentActiveClassName]?.countShapeIds || [];
};

const toggleCountShape = (shapeId) => {
  const currentActiveModel = getCurrentActiveModel();
  const currentActiveClassName = getCurrentActiveClassName();
  const current = getActiveClassCountShapeIds();
  const next = current.includes(shapeId) ? current.filter(id => id !== shapeId) : [...current, shapeId];
  if (!currentActiveModel) return;
  setModelSettings(prev => {
    const ms = prev[currentActiveModel] || { ...DEFAULT_MODEL_SETTINGS };
    const clsTracking = ms.trackingByClass?.[currentActiveClassName] || defaultTrackingFor(currentActiveModel, currentActiveClassName);
    return {
      ...prev,
      [currentActiveModel]: {
        ...ms,
        trackingByClass: {
          ...(ms.trackingByClass || {}),
          [currentActiveClassName]: { ...clsTracking, countShapeIds: next },
        },
      },
    };
  });
};

const pruneStaleZoneIds = (newShapes) => {
  const validIds = new Set(newShapes.filter(s => s.category !== "ignore").map(s => String(s.id)));
  const validZoneIds = new Set(newShapes.filter(s => s.category === "zone" && s.type !== "line").map(s => String(s.id)));
  setModelSettings(prev => {
    let changed = false;
    const next = { ...prev };
    Object.keys(next).forEach(modelKey => {
      const ms = next[modelKey];
      if (!ms?.trackingByClass) return;
      const nextTbc = { ...ms.trackingByClass };
      Object.keys(nextTbc).forEach(classKey => {
        const t = nextTbc[classKey];
        let updated = false;
        // Prune zoneIds
        const zIds = t?.zoneIds;
        const prunedZ = zIds && zIds.length > 0 ? zIds.filter(id => validZoneIds.has(id)) : zIds;
        if (zIds && prunedZ.length !== zIds.length) updated = true;
        // Prune countShapeIds
        const cIds = t?.countShapeIds;
        const prunedC = cIds && cIds.length > 0 ? cIds.filter(id => validIds.has(id)) : cIds;
        if (cIds && prunedC.length !== cIds.length) updated = true;
        if (updated) {
          changed = true;
          nextTbc[classKey] = { ...t, zoneIds: prunedZ || [], countShapeIds: prunedC || [] };
        }
      });
      if (changed) next[modelKey] = { ...ms, trackingByClass: nextTbc };
    });
    return changed ? next : prev;
  });
  setComboSettings(prev => {
    let changed = false;
    const next = { ...prev };
    Object.keys(next).forEach(comboId => {
      const cs = next[comboId];
      if (!cs?.classModels) return;
      const nextClassModels = { ...cs.classModels };
      let comboChanged = false;
      Object.keys(nextClassModels).forEach(classId => {
        const cm = nextClassModels[classId];
        const zIds = cm?.zoneIds;
        const prunedZ = zIds && zIds.length > 0 ? zIds.filter(id => validZoneIds.has(id)) : zIds;
        if (zIds && prunedZ.length !== zIds.length) {
          changed = true;
          comboChanged = true;
          nextClassModels[classId] = { ...cm, zoneIds: prunedZ };
        }
      });
      if (comboChanged) next[comboId] = { ...cs, classModels: nextClassModels };
    });
    return changed ? next : prev;
  });
};

// ---- PER-CLASS SCHEDULE HELPERS (inside active model's active class) ----
const getActiveClassSchedule = () => {
  const tracking = getActiveClassTracking();
  return tracking?.schedule || [];
};

const updateClassSchedule = (newSchedule) => {
  const currentActiveModel = getCurrentActiveModel();
  const currentActiveClassName = getCurrentActiveClassName();
  if (!currentActiveModel) return;
  setCurrentModelSettingsMap(prev => {
    const ms = prev[currentActiveModel] || { ...DEFAULT_MODEL_SETTINGS };
    const clsTracking = ms.trackingByClass?.[currentActiveClassName] || defaultTrackingFor(currentActiveModel, currentActiveClassName);
    return {
      ...prev,
      [currentActiveModel]: {
        ...ms,
        trackingByClass: {
          ...(ms.trackingByClass || {}),
          [currentActiveClassName]: { ...clsTracking, schedule: newSchedule },
        },
      },
    };
  });
};

const addClassTimeRange = () => {
  const current = getActiveClassSchedule();
  updateClassSchedule([
    ...current,
    { startTime: "08:00", endTime: "18:00", days: ["Mon", "Tue", "Wed", "Thu", "Fri"], dayIds: {} },
  ]);
};

const updateClassTimeRange = (rangeIdx, field, value) => {
  const current = getActiveClassSchedule();
  updateClassSchedule(current.map((r, i) => i === rangeIdx ? { ...r, [field]: value } : r));
};

const toggleClassDay = (rangeIdx, day) => {
  const current = getActiveClassSchedule();
  updateClassSchedule(current.map((r, i) => {
    if (i !== rangeIdx) return r;
    const days = r.days.includes(day) ? r.days.filter(d => d !== day) : [...r.days, day];
    return { ...r, days };
  }));
};

const removeClassTimeRange = (rangeIdx) => {
  const current = getActiveClassSchedule();
  updateClassSchedule(current.filter((_, i) => i !== rangeIdx));
};

// ---- COMBO HELPERS ----
const getActiveCombo = () => comboList.find(c => getComboId(c) === getCurrentActiveComboId());
const getActiveComboSettings = () => getCurrentComboSettings(getCurrentActiveComboId());
const getActiveComboClassModel = () => getActiveComboSettings().classModels?.[getCurrentActiveComboClassId()] || DEFAULT_COMBO_CLASS_MODEL;
const getComboClassIds = (comboId) => {
  const combo = comboList.find(c => getComboId(c) === String(comboId));
  return (combo?.items || []).filter(item => item.class_id).map(item => String(item.class_id));
};
const getFirstComboClassId = (comboId) => getComboClassIds(comboId)[0] || "";

const toggleComboInPopover = (comboId) => {
  comboId = String(comboId);
  const combo = comboList.find(c => getComboId(c) === comboId);
  const firstClassId = getFirstComboClassId(comboId);
  if (selectedCombos.includes(comboId)) {
    const next = selectedCombos.filter(id => id !== comboId);
    setSelectedCombos(next);
    if (activeComboId === comboId) { setActiveComboId(next[0] || ""); setActiveComboClassId(""); }
  } else {
    setSelectedCombos(prev => [...prev, comboId]);
    if (!activeComboId) {
      setActiveComboId(comboId);
      setActiveComboClassId(firstClassId);
    }
    if (combo && !comboSettings[comboId]) {
      const classModels = {};
      (combo.items || []).forEach(item => { classModels[String(item.class_id)] = { ...DEFAULT_COMBO_CLASS_MODEL }; });
      setComboSettings(prev => ({ ...prev, [comboId]: { ...DEFAULT_COMBO_CFG, classModels } }));
    }
  }
};
const removeCombo = (comboId) => {
  comboId = String(comboId);
  const removedCombo = comboSettings?.[comboId];
  const removedShapeIds = new Set();
  if (removedCombo) {
    Object.values(removedCombo.classModels || {}).forEach((cm) => {
      (cm?.zoneIds || []).forEach(id => removedShapeIds.add(String(id)));
    });
    (removedCombo?.tracking?.comboTrackLineIds || []).forEach(id => removedShapeIds.add(String(id)));
    if (removedCombo?.tracking?.track_line != null) removedShapeIds.add(String(removedCombo.tracking.track_line));
  }
  const next = selectedCombos.filter(id => id !== comboId);
  setSelectedCombos(next);
  if (activeComboId === comboId) { setActiveComboId(next[0] || ""); setActiveComboClassId(""); }

  if (removedShapeIds.size > 0) {
    const stillReferenced = new Set([
      ...collectModelReferencedShapeIds(modelSettings),
      ...collectSelectedComboReferencedShapeIds(comboSettings, next),
    ]);
    removeShapeIdsFromCanvas(removedShapeIds, stillReferenced);
  }
};
const updateComboClassModelField = (classId, field, value) => {
  if (!activeComboId) return;
  setComboSettings(prev => {
    const cs = prev[activeComboId] || { ...DEFAULT_COMBO_CFG };
    const cm = cs.classModels?.[classId] || { ...DEFAULT_COMBO_CLASS_MODEL };
    return { ...prev, [activeComboId]: { ...cs, classModels: { ...cs.classModels, [classId]: { ...cm, [field]: value } } } };
  });
};
const updateComboTracking = (field, value) => {
  if (!activeComboId) return;
  setComboSettings(prev => {
    const cs = prev[activeComboId] || { ...DEFAULT_COMBO_CFG };
    return { ...prev, [activeComboId]: { ...cs, tracking: { ...(cs.tracking || DEFAULT_COMBO_TRACKING_CFG), [field]: value } } };
  });
};
const toggleComboTrackLine = (shapeId) => {
  if (!activeComboId) return;
  setComboSettings(prev => {
    const cs = prev[activeComboId] || { ...DEFAULT_COMBO_CFG };
    const tracking = cs.tracking || DEFAULT_COMBO_TRACKING_CFG;
    const current = tracking.comboTrackLineIds || [];
    const next = current.includes(shapeId) ? current.filter(id => id !== shapeId) : [...current, shapeId];
    return { ...prev, [activeComboId]: { ...cs, tracking: { ...tracking, comboTrackLineIds: next } } };
  });
};

const setComboTrackingEnabled = (enabled) => {
  if (!activeComboId) return;
  setComboSettings(prev => {
    const cs = prev[activeComboId] || { ...DEFAULT_COMBO_CFG };
    return { ...prev, [activeComboId]: { ...cs, trackingEnabled: enabled } };
  });
};
const updateComboSchedule = (sched) => {
  if (!activeComboId) return;
  setComboSettings(prev => ({ ...prev, [activeComboId]: { ...(prev[activeComboId] || DEFAULT_COMBO_CFG), schedule: sched } }));
};

React.useEffect(() => {
  if (!activeComboId) {
    setActiveComboClassId("");
    return;
  }
  const classIds = getComboClassIds(activeComboId);
  if (classIds.length === 0) {
    setActiveComboClassId("");
    return;
  }
  if (!classIds.includes(activeComboClassId)) {
    setActiveComboClassId(classIds[0]);
  }
}, [activeComboId, activeComboClassId, comboList, getComboId]); // eslint-disable-line react-hooks/exhaustive-deps

const getMergedComboStateForSave = () => {
  const imgZoneConfigs = zoneConfigsByImage[selectedImg] || {};
  const mergedComboSettings = JSON.parse(JSON.stringify(comboSettings));
  Object.entries(imgZoneConfigs).forEach(([zoneShapeId, zoneConfig]) => {
    const zoneComboCfg = zoneConfig.comboConfig || {};
    const zoneSelectedCombos = zoneComboCfg.selectedCombos || [];
    zoneSelectedCombos.forEach(comboId => {
      // If this combo is already in global selectedCombos, global config takes precedence.
      // Don't let zone settings override it or add a zone boundary — the combo stays global.
      if (selectedCombos.includes(comboId)) return;
      const zoneCs = zoneComboCfg.comboSettings?.[comboId] || {};
      if (!mergedComboSettings[comboId]) {
        mergedComboSettings[comboId] = { ...DEFAULT_COMBO_CFG, ...zoneCs };
      } else {
        const existing = mergedComboSettings[comboId];
        // Merge class models
        const mergedClassModels = { ...(existing.classModels || {}) };
        Object.entries(zoneCs.classModels || {}).forEach(([classId, zoneCm]) => {
          mergedClassModels[classId] = { ...(mergedClassModels[classId] || DEFAULT_COMBO_CLASS_MODEL), ...zoneCm };
        });
        // Zone settings are authoritative: spread zoneCs over existing so tracking,
        // schedule, syncDetectionConfig etc. are all saved. Keep boundaryZoneIds from
        // global (zone-assign panel) if already set there.
        const existingBoundary = existing.boundaryZoneIds?.length ? existing.boundaryZoneIds : null;
        mergedComboSettings[comboId] = {
          ...existing,
          ...zoneCs,
          classModels: mergedClassModels,
          ...(existingBoundary ? { boundaryZoneIds: existingBoundary } : {}),
        };
      }
      if (!(mergedComboSettings[comboId].boundaryZoneIds || []).length) {
        mergedComboSettings[comboId] = { ...mergedComboSettings[comboId], boundaryZoneIds: [zoneShapeId] };
      }
    });
  });

  // Collect all combos that should be included: global selected + zone-selected
  const allSelectedCombos = [...new Set([
    ...selectedCombos,
    ...Object.values(imgZoneConfigs).flatMap(zc => zc.comboConfig?.selectedCombos || []),
  ])];

  return { allSelectedCombos, mergedComboSettings };
};

const buildComboSavePayloads = () => {
  const deviceShapes = shapesByImage[selectedImg] || [];
  const zoneShapes = deviceShapes.filter(s => s.category === "zone" && s.type !== "line");
  const { allSelectedCombos, mergedComboSettings } = getMergedComboStateForSave();

  const selectedPayloads = allSelectedCombos.map(comboId => {
    const cs = mergedComboSettings[comboId] || DEFAULT_COMBO_CFG;
    const modelsPayload = Object.entries(cs.classModels || {}).filter(([, m]) => m.model_version_id).map(([classId, m]) => ({
      detection_area: m.rawDetectionArea || null,
      class_id: parseInt(classId) || 0,
      model_config_id: m.model_config_id || 0,
      model_version_id: parseInt(m.model_version_id) || 0,
      conf: m.conf, iou: m.iou, detect_fps: m.detect_fps,
      motion_sensitivity: m.motion_sensitivity ?? DEFAULT_MOTION_SENSITIVITY,
      model_format: m.model_format || modelFormatFor(m.model_version_id),
      augment: m.augment || false,
      agnostic_nms: m.agnostic_nms || false,
      is_zoom_motion_area: m.is_zoom_motion_area || false,
      enable_detect_always: m.enable_detect_always || false,
      enable_motion: m.enable_motion ?? true,
      communication_type: m.comm_type || "grpc",
      grpc_connect_ip: m.comm_ip || null,
      grpc_connect_port: parseInt(m.comm_port) || null,
      http_private_base_url: m.comm_url || null,
      http_public_base_url: m.comm_url || null,
      http_path: m.comm_path || null,
    }));
    const monTimes = [];
    (cs.schedule || []).forEach(range => {
      (range.days || []).forEach(day => {
        monTimes.push({ id: (range.dayIds && range.dayIds[day]) || 0, day_of_week: DAY_NAME_TO_NUM[day] ?? day, start_time: `${range.startTime || "00:00"}:00`, end_time: `${range.endTime || "23:59"}:00` });
      });
    });
    return {
      camera_id: selectedDevice?.deviceid || 0,
      combo_rule_id: cs.combo_rule_id || parseInt(comboId) || 0,
      sync_detection_config: cs.syncDetectionConfig ?? false,
      combo_boundary: (() => {
        const ids = cs.boundaryZoneIds || [];
        if (ids.length > 0) {
          const assigned = zoneShapes.filter(s => ids.includes(String(s.id)));
          const result = shapesToAreaString(assigned);
          if (result) return result;
        }
        // Fallback: rawComboBoundary may be array (from API) or string — normalize to string
        if (cs.rawComboBoundary) {
          const polys = parseAreaString(cs.rawComboBoundary);
          const fakeShapes = polys.map(poly => ({ type: "poly", points: poly.map(([x, y]) => ({ x, y })) }));
          return shapesToAreaString(fakeShapes) || null;
        }
        return null;
      })(),
      models: modelsPayload,
      tracking: cs.trackingEnabled ? {
        tracking_config_id: cs.tracking?.tracking_config_id || 0,
        track_iou: cs.tracking?.track_iou ?? 0.3,
        track_max_disappeared: cs.tracking?.track_max_disappeared ?? defaultComboMaxDisappeared,
        tracking_fps: cs.tracking?.tracking_fps ?? defaultComboTrackingFps,
        min_track_distance: cs.tracking?.min_track_distance ?? defaultMinDistance,
        min_track_duration_sec: cs.tracking?.min_track_duration_sec ?? defaultComboMinDurationSec,
        enable_object_count: cs.tracking?.enable_object_count ?? false,
        track_direction: cs.tracking?.track_direction || null,
        track_line: (() => {
          const ids = cs.tracking?.comboTrackLineIds || [];
          if (ids.length === 0) return cs.tracking?.rawTrackLine || null;
          const assigned = deviceShapes.filter(s => ids.includes(String(s.id)));
          return shapesToAreaString(assigned) || cs.tracking?.rawTrackLine || null;
        })(),
      } : null,
      monitoring_times: monTimes.length > 0 ? monTimes.sort((a, b) => a.day_of_week - b.day_of_week) : null,
    };
  });
  const deselectedPayloads = Object.keys(mergedComboSettings)
    .filter(comboId => !allSelectedCombos.includes(comboId))
    .map(comboId => {
      const cs = comboSettings[comboId] || DEFAULT_COMBO_CFG;
      return {
        camera_id: selectedDevice?.deviceid || 0,
        combo_rule_id: cs.combo_rule_id || parseInt(comboId) || 0,
        sync_detection_config: false,
        combo_boundary: null,
        models: [],
        tracking: null,
        monitoring_times: null,
      };
    });
  // ── COMBO SAVE SUMMARY LOG ─────────────────────────────────────────────────
  console.group('[SAVE][COMBO]');
  console.log('[SAVE][COMBO] globalSelectedCombos (no boundary):', selectedCombos);
  console.log('[SAVE][COMBO] allSelectedCombos (global + zone):', allSelectedCombos);
  allSelectedCombos.forEach(id => {
    const cs = mergedComboSettings[id] || DEFAULT_COMBO_CFG;
    const isGlobal = selectedCombos.includes(id);
    const boundary = cs.boundaryZoneIds?.length ? cs.boundaryZoneIds : null;
    console.log(`  combo ${id}: ${isGlobal ? 'GLOBAL' : 'ZONE'} | boundary zone IDs: ${JSON.stringify(boundary)} | trackingEnabled: ${cs.trackingEnabled} | schedule entries: ${(cs.schedule || []).length}`);
  });
  if (deselectedPayloads.length > 0) {
    console.log('[SAVE][COMBO] deselected (will be cleared):', deselectedPayloads.map(p => p.combo_rule_id));
  }
  console.groupEnd();

  return {
    payloads: [...selectedPayloads, ...deselectedPayloads],
    allSelectedCombos,
    mergedComboSettings,
  };
};

// Convert shapes back to area string format "[[(x1,y1),(x2,y2),...]]"
const shapesToAreaString = (shapeList) => {
  if (!shapeList || shapeList.length === 0) return null;
  const polys = shapeList.map(s => {
    if (s.type === "line" && s.x1 != null) {
      return [[s.x1, s.y1], [s.x2, s.y2]];
    }
    if (s.type === "rect") {
      return [[s.x, s.y], [s.x + s.w, s.y], [s.x + s.w, s.y + s.h], [s.x, s.y + s.h]];
    }
    if (s.type === "poly" && s.points) {
      return s.points.map(p => [p.x, p.y]);
    }
    return null;
  }).filter(Boolean);
  if (polys.length === 0) return null;
  // Format as Python-style string: "[[(x,y),(x,y),...]]"
  const inner = polys.map(poly =>
    "[" + poly.map(p => `(${p[0]}, ${p[1]})`).join(", ") + "]"
  ).join(", ");
  return `[${inner}]`;
};

const DAY_NAME_TO_NUM = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const getSeparatedModelStateForSave = () => {
  const imgZoneConfigs = zoneConfigsByImage[selectedImg] || {};
  const globalEntries = selectedModels.map(mKey => ({
    scope: "global",
    zoneShapeId: null,
    modelKey: mKey,
    settings: JSON.parse(JSON.stringify(modelSettings[mKey] || DEFAULT_MODEL_SETTINGS)),
  }));
  const zoneEntries = [];
  Object.entries(imgZoneConfigs).forEach(([zoneShapeId, zoneConfig]) => {
    (zoneConfig.selectedModels || []).forEach((mKey) => {
      const zoneSettings = zoneConfig.modelSettings?.[mKey];
      if (!zoneSettings) return;
      zoneEntries.push({
        scope: "zone",
        zoneShapeId: String(zoneShapeId),
        modelKey: mKey,
        settings: JSON.parse(JSON.stringify(zoneSettings)),
      });
    });
  });
  return {
    globalEntries,
    zoneEntries,
    allEntries: [...globalEntries, ...zoneEntries],
  };
};

const buildConfigJSON = () => {
  const deviceShapes = shapesByImage[selectedImg] || [];
  const zoneShapes = deviceShapes.filter(s => s.category === "zone");
  const ignoreShapes = deviceShapes.filter(s => s.category === "ignore");

  const { globalEntries, zoneEntries, allEntries } = getSeparatedModelStateForSave();
  const { allSelectedCombos, mergedComboSettings } = getMergedComboStateForSave();

  // Collect all zone IDs assigned to model/combo classes — these are excluded from Monarea
  const assignedZoneIds = new Set();
  zoneEntries.forEach(({ zoneShapeId, settings }) => {
    if (zoneShapeId) assignedZoneIds.add(String(zoneShapeId));
    Object.values(settings?.trackingByClass || {}).forEach(t => {
      (t.zoneIds || []).forEach(id => assignedZoneIds.add(String(id)));
    });
  });
  globalEntries.forEach(({ settings }) => {
    Object.values(settings?.trackingByClass || {}).forEach(t => {
      (t.zoneIds || []).forEach(id => assignedZoneIds.add(String(id)));
    });
  });
  // Zones used as combo boundaries
  allSelectedCombos.forEach(comboId => {
    const cs = mergedComboSettings[comboId] || DEFAULT_COMBO_CFG;
    (cs.boundaryZoneIds || []).forEach(id => assignedZoneIds.add(String(id)));
    Object.values(cs.classModels || {}).forEach(cm => {
      (cm.zoneIds || []).forEach(id => assignedZoneIds.add(String(id)));
    });
  });
  const monAreaShapes = zoneShapes.filter(s => !assignedZoneIds.has(String(s.id)));

  // ── MODEL SAVE SUMMARY LOG ─────────────────────────────────────────────────
  console.group('[SAVE][MODEL]');
  console.log('[SAVE][MODEL] entries:', allEntries.map(entry => ({
    scope: entry.scope,
    zoneShapeId: entry.zoneShapeId,
    modelKey: entry.modelKey,
    classes: entry.settings?.classNames || [],
  })));
  allEntries.forEach(({ scope, zoneShapeId, modelKey, settings }) => {
    console.log(`  ${scope.toUpperCase()} model ${modelKey}${zoneShapeId ? ` @ zone ${zoneShapeId}` : ''} classes:`, settings?.classNames || []);
    (settings?.classNames || []).forEach(cKey => {
      const ids = scope === "zone" ? [zoneShapeId] : (settings?.trackingByClass?.[cKey]?.zoneIds || []);
      console.log(`    class ${cKey} detection_area zone IDs:`, ids, ids.length > 0 ? '→ ZONE' : '→ GLOBAL');
    });
  });
  console.groupEnd();

  const modelsPayload = allEntries.map(({ scope, zoneShapeId, modelKey, settings: ms }) => {
    const zoneShape = zoneShapeId ? zoneShapes.find(s => String(s.id) === String(zoneShapeId)) : null;

    const classesPayload = (ms.classNames || []).map(cKey => {
      const tracking = ms.trackingByClass?.[cKey] || defaultTrackingFor(modelKey, cKey);

      const trackBoundary = (() => {
        const cIds = tracking.countShapeIds || [];
        if (cIds.length > 0) {
          const assigned = deviceShapes.filter(s => cIds.includes(String(s.id)));
          return shapesToAreaString(assigned);
        }
        // Backward compat: fall back to legacy track_line
        const tl = tracking.track_line;
        if (tl && typeof tl === "object" && tl.x1 != null) {
          return `[[(${tl.x1}, ${tl.y1}), (${tl.x2}, ${tl.y2})]]`;
        }
        return null;
      })();

      const obMonitoringTimes = [];
      (tracking.schedule || []).forEach(range => {
        (range.days || []).forEach(day => {
          const dayNum = DAY_NAME_TO_NUM[day] ?? day;
          obMonitoringTimes.push({
            id: (range.dayIds && range.dayIds[day]) || 0,
            day_of_week: dayNum,
            start_time: `${range.startTime || "00:00"}:00`,
            end_time: `${range.endTime || "23:59"}:00`,
          });
        });
      });

      return {
        class_config_id: ms.classConfigIdMap?.[cKey] ?? 0,
        model_class_id: parseInt(cKey) || 0,
        detection_area: (() => {
          if (scope === "zone") {
            return zoneShape ? shapesToAreaString([zoneShape]) : null;
          }
          const ids = tracking.zoneIds || [];
          if (ids.length === 0) return null;
          const assigned = zoneShapes.filter(s => ids.includes(String(s.id)));
          return shapesToAreaString(assigned);
        })(),
        tracking: tracking.enabled === false ? [] : [{
          tracking_config_id: tracking.tracking_config_id || 0,
          min_distance: tracking.min_distance,
          min_duration_sec: tracking.min_duration_sec,
          max_disappeared: tracking.max_disappeared,
          enable_object_count: tracking.count_objects,
          track_boundary: trackBoundary,
          direction: tracking.track_direction || "TTB",
          fps: tracking.fps,
          ignore_motion_after_tracking: tracking.ignore_motion_after_tracking,
          ignore_motion_after_tracking_time: tracking.ignore_motion_after_tracking_time,
          enable_single_track_video: tracking.enable_single_track_video,
          enable_license_plate_rec: tracking.enable_license_plate ?? false,
          ocr_model: tracking.enable_license_plate ? (tracking.license_plate_model || "small") : "",
          ocr_confidence: tracking.enable_license_plate ? (tracking.license_plate_conf ?? 0.5) : 0,
          ocr_service_ip_address: tracking.enable_license_plate ? (tracking.license_plate_ip || "") : "",
          ocr_service_port: tracking.enable_license_plate ? (parseInt(tracking.license_plate_port) || 0) : 0,
        }],
        ob_monitoring_times: obMonitoringTimes.sort((a, b) => a.day_of_week - b.day_of_week),
      };
    });

    return {
      model_config_id: ms.model_config_id || 0,
      model_version_id: parseInt(modelKey) || 0,
      conf: ms.conf,
      iou: ms.iou,
      detect_fps: ms.detect_fps,
      model_format: ms.model_format,
      augment: ms.augment,
      motion_sensitivity: ms.motion_sensitivity,
      enable_detect_always: ms.enable_detect_always,
      enable_motion: ms.enable_motion,
      agnostic_nms: ms.agnostic_nms,
      zoom_motion_area: ms.is_zoom_motion_area,
      communication: {
        type: ms.comm_type,
        grpc: {
          ip: ms.comm_ip || defaultCommIp,
          port: parseInt(ms.comm_port) || 50051,
        },
        http: {
          private_base_url: ms.comm_url || null,
          public_base_url: ms.comm_url || null,
          path: ms.comm_path || null,
        },
      },
      classes: classesPayload,
    };
  });

  return {
    camera_id: selectedDevice?.deviceid || 0,
    Monarea: shapesToAreaString(monAreaShapes),
    Ignorearea: shapesToAreaString(ignoreShapes),
    models: modelsPayload,
  };
};

// ---- VALIDATION ----
const validateConfig = () => {
  if (!selectedDevice?.deviceid) return "No camera selected";
  const { allEntries } = getSeparatedModelStateForSave();
  for (const { scope, zoneShapeId, modelKey, settings: ms } of allEntries) {
    const mLabel = MODEL_OPTIONS.find(o => o.key === modelKey)?.label || modelKey;
    const scopeLabel = scope === "zone" ? `Zone ${zoneShapeId} / ${mLabel}` : mLabel;
    if (!ms.classNames || ms.classNames.length === 0) return `Model "${scopeLabel}" has no detection classes`;
    if (ms.comm_type === "grpc") {
      if (!isValidIp(ms.comm_ip)) return `Model "${scopeLabel}": invalid gRPC IP address`;
      if (!isValidPort(ms.comm_port)) return `Model "${scopeLabel}": invalid gRPC port (1–65535)`;
    }
    if (ms.comm_type === "http" && !ms.comm_url) return `Model "${scopeLabel}": HTTP base URL is required`;
    for (const cKey of ms.classNames) {
      const t = ms.trackingByClass?.[cKey] || {};
      const cLabel = MODEL_CLASSES.find(c => c.key === cKey)?.label || cKey;
      if (t.count_objects) {
        const cIds = t.countShapeIds || [];
        if (cIds.length === 0) return `Class "${cLabel}" in "${scopeLabel}" has Count enabled but no count boundary assigned`;
      }
      if (t.enable_license_plate) {
        if (!isValidIp(t.license_plate_ip)) return `Class "${cLabel}" in "${scopeLabel}": invalid license plate service IP address`;
        if (!isValidPort(t.license_plate_port)) return `Class "${cLabel}" in "${scopeLabel}": invalid license plate service port (1–65535)`;
      }
      const sched = t.schedule || [];
      for (let i = 0; i < sched.length; i++) {
        const r = sched[i];
        if (!r.startTime || !r.endTime) return `Class "${cLabel}" in "${scopeLabel}" schedule range ${i + 1}: missing time`;
        if (r.startTime >= r.endTime) return `Class "${cLabel}" in "${scopeLabel}" schedule range ${i + 1}: start must be before end`;
        if (!r.days || r.days.length === 0) return `Class "${cLabel}" in "${scopeLabel}" schedule range ${i + 1}: select at least one day`;
      }
    }
  }
  return null;
};

const validateComboConfig = () => {
  const { allSelectedCombos, mergedComboSettings } = getMergedComboStateForSave();
  for (const comboId of allSelectedCombos) {
    const combo = comboList.find(c => getComboId(c) === comboId);
    if (!combo) continue;
    const comboName = combo.display_name || combo.combo_name || `Combo ${comboId}`;
    const classIds = (combo.items || []).filter(it => it.class_id).map(it => String(it.class_id));
    const cs = mergedComboSettings[comboId] || DEFAULT_COMBO_CFG;
    for (const classId of classIds) {
      const classCfg = cs.classModels?.[classId];
      if (!classCfg?.model_version_id) continue; // no model assigned yet — skipped in payload, not a blocker
      const classLabel = ALL_MODEL_CLASSES.find(c => c.key === classId)?.label || `Class ${classId}`;
      if (classCfg.comm_type === "grpc") {
        if (!isValidIp(classCfg.comm_ip)) return `${comboName}: invalid gRPC IP for "${classLabel}"`;
        if (!isValidPort(classCfg.comm_port)) return `${comboName}: invalid gRPC port for "${classLabel}"`;
      }
      if (classCfg.comm_type === "http" && !classCfg.comm_url) {
        return `${comboName}: HTTP URL required for "${classLabel}"`;
      }
    }
  }
  return null;
};

const hasActiveFilter = (!isClientRestrictedUser && filterBy !== "") || searchText.trim() !== "";

const clearFilters = () => {
  setFilterBy("");
  setSelectedServer("");
  setSelectedClient("");
  setSearchText("");
};

  const activeZoneConfig = selectedImg && selectedShapeId
    ? zoneConfigsByImage[selectedImg]?.[selectedShapeId]
    : null;
  const _shapes = shapesByImage[selectedImg] || [];
  const _selectedZoneIndex = selectedShapeId ? _shapes.findIndex(s => s.id === selectedShapeId) : -1;
  const selectedShape = _selectedZoneIndex >= 0 ? _shapes[_selectedZoneIndex] : null;
  const useZoneConfig = !!(selectedShape && selectedShape.category === "zone" && selectedShape.type !== "line");

  // When a zone is selected, show only that zone's own config — never fall back to global.
  const getCurrentSelectedModels = () => (useZoneConfig ? (activeZoneConfig?.selectedModels || []) : selectedModels);
  const getCurrentActiveModel = () => (useZoneConfig ? (activeZoneConfig?.activeModel || "") : activeModel);
  const getCurrentActiveClassName = () => (useZoneConfig ? (activeZoneConfig?.activeClassName || "") : activeClassName);
  const getCurrentModelSettingsMap = () => (useZoneConfig ? (activeZoneConfig?.modelSettings || {}) : modelSettings);

  // ---- ZONE-AWARE COMBO GETTERS ----
  const zoneComboConfig = useZoneConfig ? (activeZoneConfig?.comboConfig || EMPTY_ZONE_COMBO) : null;
  // Zone selected → show only that zone's combo config; no global fallback.
  const getCurrentSelectedCombos = () => useZoneConfig ? (zoneComboConfig?.selectedCombos || []) : selectedCombos;
  const getCurrentActiveComboId = () => useZoneConfig ? (zoneComboConfig?.activeComboId || "") : activeComboId;
  const getCurrentActiveComboClassId = () => useZoneConfig ? (zoneComboConfig?.activeComboClassId || "") : activeComboClassId;
  const getCurrentComboSettings = (cid) => {
    const zcs = zoneComboConfig?.comboSettings?.[cid];
    if (zcs && Object.keys(zcs.classModels || {}).length > 0) return zcs;
    return useZoneConfig ? DEFAULT_COMBO_CFG : (comboSettings[cid] || DEFAULT_COMBO_CFG);
  };

  // New zone configs start empty — zone config is independent from global config.
  const getBaseZoneConfig = () => ({
    enabled: true,
    selectedModels: [],
    activeModel: "",
    activeClassName: "",
    modelSettings: {},
  });

  const updateZoneConfig = (shapeId, updater) => {
    if (!selectedImg || !shapeId) return;
    setZoneConfigsByImage(prev => {
      const imgConfigs = prev[selectedImg] || {};
      const current = imgConfigs[shapeId] || getBaseZoneConfig();
      const next = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [selectedImg]: { ...imgConfigs, [shapeId]: next } };
    });
  };

  const updateZoneComboConfig = (updater) => {
    if (!selectedShapeId) return;
    updateZoneConfig(selectedShapeId, (cfg) => ({
      ...cfg,
      comboConfig: updater(cfg.comboConfig || { ...EMPTY_ZONE_COMBO }),
    }));
  };

  const removeZoneConfig = (shapeId) => {
    if (!selectedImg || !shapeId) return;
    setZoneConfigsByImage(prev => {
      const imgConfigs = prev[selectedImg];
      if (!imgConfigs) return prev;
      const { [shapeId]: _removed, ...rest } = imgConfigs;
      return { ...prev, [selectedImg]: rest };
    });
  };

  const isTypingTarget = (el) => {
    if (!el) return false;
    const tag = (el.tagName || "").toLowerCase();
    return tag === "input" || tag === "textarea" || el.isContentEditable;
  };

  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) {
        if (e.key.toLowerCase() === "z") {
          e.preventDefault();
          undo();
        }
        if (e.key.toLowerCase() === "y") {
          e.preventDefault();
          redo();
        }
        return;
      }
      const k = e.key.toLowerCase();
      if (k === "v") setMode("select");
      if (k === "h") setMode("pan");
      if (k === "r") setMode("rect");
      if (k === "p") setMode("poly");
      if (k === "l") setMode("line");
      if (k === "i") setMode("ignore_rect");
      if (k === "o") setMode("ignore_poly");
      if (k === "escape") {
        setSelectedShapeId(null);
      }
      if (k === "delete" || k === "backspace") {
        if (selectedShapeId) {
          const filtered = shapes.filter(s => s.id !== selectedShapeId);
          pushHistory(filtered);
          removeZoneConfig(selectedShapeId);
          setSelectedShapeId(null);
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedShapeId, shapes, mode]);

  React.useEffect(() => {
    if (!selectedShapeId) return;
    setZonesOpen(true);
    if (zonesRef.current) {
      zonesRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedShapeId]);

  // Zone combo config mirrors the global first-class effect above: an active combo whose
  // class is missing or stale falls back to its first class, so a combo carrying a single
  // class never needs a click on its lone chip before the model version picker appears.
  React.useEffect(() => {
    if (!useZoneConfig || !selectedShapeId) return;
    const cfg = activeZoneConfig?.comboConfig;
    if (!cfg?.activeComboId) return;
    const classIds = getComboClassIds(cfg.activeComboId);
    if (classIds.length === 0 || classIds.includes(String(cfg.activeComboClassId || ""))) return;
    updateZoneComboConfig(c => ({ ...c, activeComboClassId: classIds[0] }));
  }, [useZoneConfig, selectedShapeId, activeZoneConfig, comboList]); // eslint-disable-line react-hooks/exhaustive-deps

  const isValidIp = (value) => {
    if (!value) return false;
    const ipv4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
    return ipv4.test(value);
  };

  const isValidPort = (value) => {
    const n = Number(value);
    return Number.isInteger(n) && n >= 1 && n <= 65535;
  };

  // ---- RENDER ----
  const currentSelectedModels = getCurrentSelectedModels();
  const currentActiveModel = getCurrentActiveModel();
  const currentActiveClassName = getCurrentActiveClassName();

  // Zone-aware combo state
  const currentSelectedCombos = getCurrentSelectedCombos();
  const currentActiveComboId = getCurrentActiveComboId();
  const currentActiveComboClassId = getCurrentActiveComboClassId();

  // Zone-aware combo setters — write to zone config or global based on useZoneConfig
  const setCurrentActiveComboId = (id) => {
    const firstClassId = getFirstComboClassId(id);
    if (useZoneConfig) updateZoneComboConfig(cfg => ({ ...cfg, activeComboId: id, activeComboClassId: firstClassId }));
    else {
      setActiveComboId(id);
      setActiveComboClassId(firstClassId);
    }
  };
  const setCurrentActiveComboClassId = (id) => {
    if (useZoneConfig) updateZoneComboConfig(cfg => ({ ...cfg, activeComboClassId: id }));
    else setActiveComboClassId(id);
  };
  const toggleCurrentComboInPopover = (comboId) => {
    comboId = String(comboId);
    if (!useZoneConfig) { toggleComboInPopover(comboId); return; }
    updateZoneComboConfig(cfg => {
      const combo = comboList.find(c => getComboId(c) === comboId);
      const firstClassId = getFirstComboClassId(comboId);
      const scs = cfg.selectedCombos || [];
      const isSelected = scs.includes(comboId);
      const next = isSelected ? scs.filter(id => id !== comboId) : [...scs, comboId];
      const nextActive = isSelected && cfg.activeComboId === comboId ? (next[0] || "") : (cfg.activeComboId || (!isSelected ? comboId : ""));
      const nextActiveClassId = isSelected && cfg.activeComboId === comboId
        ? getFirstComboClassId(nextActive)
        : (cfg.activeComboClassId || (!isSelected ? firstClassId : ""));
      const newSettings = { ...(cfg.comboSettings || {}) };
      if (!isSelected && !newSettings[comboId]) {
        const classModels = {};
        (combo?.items || []).forEach(item => { classModels[String(item.class_id)] = { ...DEFAULT_COMBO_CLASS_MODEL }; });
        newSettings[comboId] = { ...DEFAULT_COMBO_CFG, classModels };
      }
      return { ...cfg, selectedCombos: next, activeComboId: nextActive, activeComboClassId: nextActiveClassId, comboSettings: newSettings };
    });
  };
  const updateCurrentComboClassModelField = (classId, field, value) => {
    if (!useZoneConfig) { updateComboClassModelField(classId, field, value); return; }
    const cid = currentActiveComboId;
    updateZoneComboConfig(cfg => {
      const cs = cfg.comboSettings?.[cid] || DEFAULT_COMBO_CFG;
      const cm = cs.classModels?.[classId] || DEFAULT_COMBO_CLASS_MODEL;
      return { ...cfg, comboSettings: { ...(cfg.comboSettings || {}), [cid]: { ...cs, classModels: { ...(cs.classModels || {}), [classId]: { ...cm, [field]: value } } } } };
    });
  };

  // Zone-aware wrapper for updating the active combo's settings object
  const updateCurrentComboSettingsObj = (fn) => {
    if (!useZoneConfig) return false; // signal caller to use global fn
    const cid = currentActiveComboId;
    if (!cid) return true;
    updateZoneComboConfig(cfg => {
      const cs = cfg.comboSettings?.[cid] || DEFAULT_COMBO_CFG;
      return { ...cfg, comboSettings: { ...(cfg.comboSettings || {}), [cid]: fn(cs) } };
    });
    return true;
  };

  const setCurrentComboTrackingEnabled = (enabled) => {
    if (!updateCurrentComboSettingsObj(cs => ({ ...cs, trackingEnabled: enabled })))
      setComboTrackingEnabled(enabled);
  };
  const updateCurrentComboTracking = (field, value) => {
    if (!updateCurrentComboSettingsObj(cs => ({ ...cs, tracking: { ...(cs.tracking || DEFAULT_COMBO_TRACKING_CFG), [field]: value } })))
      updateComboTracking(field, value);
  };
  const toggleCurrentComboTrackLine = (shapeId) => {
    if (!updateCurrentComboSettingsObj(cs => {
      const t = cs.tracking || DEFAULT_COMBO_TRACKING_CFG;
      const cur = t.comboTrackLineIds || [];
      return { ...cs, tracking: { ...t, comboTrackLineIds: cur.includes(shapeId) ? cur.filter(id => id !== shapeId) : [...cur, shapeId] } };
    })) toggleComboTrackLine(shapeId);
  };
  const getCurrentComboSchedule = () => getActiveComboSettings().schedule || [];
  const updateCurrentComboSchedule = (sched) => {
    if (!updateCurrentComboSettingsObj(cs => ({ ...cs, schedule: sched })))
      updateComboSchedule(sched);
  };
  const addCurrentComboTimeRange = () => updateCurrentComboSchedule([...getCurrentComboSchedule(), { startTime: "08:00", endTime: "18:00", days: ["Mon","Tue","Wed","Thu","Fri"], dayIds: {} }]);
  const updateCurrentComboTimeRange = (idx, field, value) => updateCurrentComboSchedule(getCurrentComboSchedule().map((r, i) => i === idx ? { ...r, [field]: value } : r));
  const toggleCurrentComboDay = (idx, day) => updateCurrentComboSchedule(getCurrentComboSchedule().map((r, i) => i !== idx ? r : { ...r, days: r.days.includes(day) ? r.days.filter(d => d !== day) : [...r.days, day] }));
  const removeCurrentComboTimeRange = (idx) => updateCurrentComboSchedule(getCurrentComboSchedule().filter((_, i) => i !== idx));

  const selectedZoneIndex = selectedShapeId ? shapes.findIndex(s => s.id === selectedShapeId) : -1;
  const isIgnoreSelected = selectedShape?.category === "ignore";
  const selectedZoneLabel = selectedZoneIndex >= 0 ? (isIgnoreSelected ? `Ignore ${selectedZoneIndex + 1}` : `Zone ${selectedZoneIndex + 1}`) : "Zone";
  const POP_LIST_SX = {
    bgcolor: UI.surface,
    color: UI.textMain,
    borderRadius: 2,
    mt: 0.5,
    boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
    px: 0.5,
    py: 0.5,
    minWidth: 160,
  };

  return (
    <>
        <Box
          sx={{
            flexGrow: 1,
            height: "100vh",
            background: UI.bg,
            fontFamily: '"Manrope", "Segoe UI", Tahoma, sans-serif',
            color: UI.textMain,
            "& .MuiButton-root": { textTransform: "none", fontWeight: 600, borderRadius: 2 },
            "& .MuiButton-containedPrimary": { boxShadow: "none" },
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              backgroundColor: UI.panel,
            },
            "& *": {
              scrollbarWidth: "thin",
              scrollbarColor: `${UI.scrollThumb} transparent`,
            },
            "& *::-webkit-scrollbar": { width: 8, height: 8 },
            "& *::-webkit-scrollbar-track": { background: "transparent" },
            "& *::-webkit-scrollbar-thumb": {
              backgroundColor: UI.scrollThumb,
              borderRadius: 8,
            },
            "& *::-webkit-scrollbar-thumb:hover": { backgroundColor: UI.scrollThumbHover },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: UI.border },
            "& .MuiInputLabel-root": { color: UI.textMuted },
            "& .MuiChip-root": { borderRadius: 2, fontWeight: 600 },
            "& .MuiSlider-root": {
              color: UI.accent,
              "& .MuiSlider-thumb": { boxShadow: "none" },
            },
            "& .MuiTypography-caption": { fontSize: 12 },
            "& .MuiTypography-body2": { fontSize: 13 },
            "& .MuiTypography-subtitle2": { fontSize: 13 },
            "& .MuiTypography-subtitle1": { fontSize: 14 },
            "& .rp": {
              "& .MuiAccordionSummary-root": {
                px: 2,
                minHeight: 42,
                backgroundColor: UI.bg,
              },
              "& .MuiAccordionSummary-content": { my: 0.75 },
              "& .MuiAccordionSummary-expandIconWrapper": { color: UI.textMuted },
              "& .MuiAccordionDetails-root": { px: 2, pt: 1.5, pb: 2 },
              "& .MuiTextField-root .MuiOutlinedInput-root": {
                height: 26,
                borderRadius: 1,
                backgroundColor: UI.inputBg,
                fontSize: 11,
              },
              "& .MuiTextField-root .MuiOutlinedInput-input": {
                fontSize: 11,
                padding: "4px 8px",
              },
              "& .MuiTextField-root .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(145,158,171,0.16)",
              },
              "& .MuiTextField-root .MuiInputAdornment-root svg": {
                fontSize: 12,
              },
              "& .MuiTextField-root .MuiInputLabel-root": {
                fontSize: 11,
              },
              "& .MuiButton-root": {
                height: 26,
                borderRadius: 1,
                fontSize: 11,
              },
              "& .MuiFormControl-root .MuiOutlinedInput-root": {
                height: 26,
                borderRadius: 1,
                backgroundColor: UI.inputBg,
                fontSize: 11,
              },
              "& .MuiSlider-root": {
                mt: 0.5,
                mb: 0,
              },
            },
            "& .MuiSwitch-switchBase.Mui-checked": { color: UI.dark },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: UI.dark },
            "& .MuiAccordion-root": {
              borderRadius: 0,
              border: "none",
              boxShadow: "none",
              backgroundColor: "transparent",
              "&:before": { display: "none" },
              mb: 0,
            },
            "& .MuiAccordionSummary-root": {
              minHeight: 36,
              px: 2,
              borderBottom: `1px solid ${UI.border}`,
              background: "transparent",
              "& .MuiAccordionSummary-content": { my: 0.5 },
            },
            "& .MuiAccordionDetails-root": { px: 2, pb: 1.5 },
          }}
        >
          <Grid container spacing={0} columns={16} height="100vh">

            {/* LEFT PANEL */}
            <Grid item xs={4} md={3} lg={2}>
              <Box sx={{ height: "100vh" }}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 0,
                    backgroundColor: UI.panel,
                    borderRight: `1px solid ${UI.border}`,
                    boxShadow: UI.shadowSm,
                  }}
                >
                <Box sx={{ position: "sticky", top: 0, zIndex: 10, borderBottom: `1px solid ${UI.border}`, backgroundColor: UI.panel }}>
                  {/* Title row */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} pt={1.5} pb={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Cameras
                      <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>
                        ({devices.length}{hasMoreDevices ? "+" : ""})
                      </Typography>
                    </Typography>
                    {hasActiveFilter && (
                      <Button size="small" variant="text" onClick={clearFilters} sx={{ color: UI.textMuted }}>
                        Clear
                      </Button>
                    )}
                  </Stack>

                  {/* Search + Filter */}
                  <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <Box sx={{
                      display: "flex", alignItems: "center", gap: 0.75,
                      backgroundColor: UI.inputBg, borderRadius: 2, px: 1.25, height: 32,
                    }}>
                      <SearchIcon sx={{ fontSize: 15, color: UI.textMuted, flexShrink: 0 }} />
                      <input
                        placeholder="Search cameras (min 3 chars)"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{
                          border: "none", outline: "none", background: "transparent",
                          fontSize: 12, color: UI.textMain, width: "100%", padding: 0,
                        }}
                      />
                      {!isClientRestrictedUser && (
                        <IconButton
                          size="small"
                          onClick={(e) => setFilterPopoverAnchor(e.currentTarget)}
                          sx={{ color: UI.textMuted, p: 0.25, flexShrink: 0 }}
                        >
                          <FilterListIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {!isClientRestrictedUser && <Popover
                    open={Boolean(filterPopoverAnchor)}
                    anchorEl={filterPopoverAnchor}
                    onClose={() => setFilterPopoverAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  >
                    <Box sx={{ p: 1.5, width: 220, backgroundColor: UI.panel, borderRadius: 2, boxShadow: "0 12px 28px rgba(0,0,0,0.12)" }}>
                      <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: "block", color: UI.dark }}>
                        Filter Cameras
                      </Typography>
                      <Stack direction="row" spacing={0.5} mb={1}>
                        {["server", "client"].map(type => (
                          <Chip
                            key={type}
                            label={type.charAt(0).toUpperCase() + type.slice(1)}
                            size="small"
                            onClick={() => {
                              setFilterBy(prev => prev === type ? "" : type);
                              setSelectedServer("");
                              setSelectedClient("");
                            }}
                            sx={{
                              height: 24, fontSize: 11, fontWeight: 600, cursor: "pointer",
                              backgroundColor: filterBy === type ? UI.dark : UI.inputBg,
                              color: filterBy === type ? UI.darkContrast : UI.textMuted,
                            }}
                          />
                        ))}
                      </Stack>
                      <Box>
                        <Button
                          size="small"
                          fullWidth
                          disabled={!filterBy}
                          onClick={(e) => setFilterSelectAnchor(e.currentTarget)}
                          sx={{
                            justifyContent: "space-between", textTransform: "none", fontSize: 11,
                            color: UI.textMain, border: `1px solid ${UI.border}`, backgroundColor: UI.surface,
                            "&:hover": { border: `1px solid ${UI.textMuted}` },
                            "&.Mui-disabled": { color: UI.border, border: `1px solid ${UI.border}` },
                          }}
                        >
                          {filterBy === "server"
                            ? (servers.find(s => s.ServerId === selectedServer)?.ServerName || "Select server")
                            : filterBy === "client"
                            ? (clients.find(c => String(c.ID) === selectedClient)?.displayname || clients.find(c => String(c.ID) === selectedClient)?.Name || "Select client")
                            : "Select filter type"}
                        </Button>
                        <Popover
                          open={Boolean(filterSelectAnchor)}
                          anchorEl={filterSelectAnchor}
                          onClose={() => { setFilterSelectAnchor(null); setFilterListSearch(""); }}
                          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        >
                          <Box sx={{ width: 200, bgcolor: UI.panel, borderRadius: 2, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden" }}>
                            {/* Search */}
                            <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
                              <Box sx={{
                                display: "flex", alignItems: "center", gap: 0.5,
                                backgroundColor: UI.inputBg, borderRadius: 1.5, px: 1, height: 30,
                              }}>
                                <SearchIcon sx={{ fontSize: 14, color: UI.textMuted }} />
                                <input
                                  value={filterListSearch}
                                  onChange={e => setFilterListSearch(e.target.value)}
                                  placeholder={`Search ${filterBy}...`}
                                  style={{
                                    border: "none", outline: "none", background: "transparent",
                                    fontSize: 11, color: UI.textMain, width: "100%",
                                  }}
                                />
                              </Box>
                            </Box>
                            {/* List */}
                            <Box sx={{
                              maxHeight: 180, overflowY: "auto", px: 0.5, pb: 0.5,
                              "&::-webkit-scrollbar": { width: 4 },
                              "&::-webkit-scrollbar-track": { background: "transparent" },
                              "&::-webkit-scrollbar-thumb": { background: UI.border, borderRadius: 2 },
                              "&::-webkit-scrollbar-thumb:hover": { background: UI.textMuted },
                            }}>
                              {(filterBy === "server" ? servers : clients)
                                .filter(item => {
                                  if (!filterListSearch.trim()) return true;
                                  const label = filterBy === "server" ? item.ServerName : (item.displayname || item.Name);
                                  return label?.toLowerCase().includes(filterListSearch.toLowerCase());
                                })
                                .map(item => {
                                  const id = filterBy === "server" ? item.ServerId : String(item.ID);
                                  const label = filterBy === "server" ? item.ServerName : (item.displayname || item.Name);
                                  const isSelected = filterBy === "server" ? selectedServer === id : selectedClient === id;
                                  return (
                                    <ListItemButton
                                      key={id}
                                      dense
                                      onClick={() => {
                                        if (filterBy === "server") setSelectedServer(id);
                                        if (filterBy === "client") setSelectedClient(id);
                                        setFilterSelectAnchor(null);
                                        setFilterListSearch("");
                                      }}
                                      sx={{
                                        borderRadius: 1, px: 1, py: 0.4,
                                        backgroundColor: isSelected ? UI.accentSoft : "transparent",
                                        "&:hover": { backgroundColor: isSelected ? UI.accentSoft : UI.inputBg },
                                      }}
                                    >
                                      <ListItemText primary={label} primaryTypographyProps={{ fontSize: 11, color: UI.textMain }} />
                                      {isSelected && <CheckIcon sx={{ fontSize: 13, color: UI.accent }} />}
                                    </ListItemButton>
                                  );
                                })}
                              {(filterBy === "server" ? servers : clients).filter(item => {
                                if (!filterListSearch.trim()) return true;
                                const label = filterBy === "server" ? item.ServerName : (item.displayname || item.Name);
                                return label?.toLowerCase().includes(filterListSearch.toLowerCase());
                              }).length === 0 && (
                                <Typography sx={{ fontSize: 11, color: UI.textMuted, textAlign: "center", py: 2 }}>
                                  No results
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Popover>
                      </Box>
                      {hasActiveFilter && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={clearFilters}
                          sx={{ mt: 1, color: UI.textMuted, textTransform: "none", fontSize: 11 }}
                        >
                          Clear filters
                        </Button>
                      )}
                    </Box>
                  </Popover>}
                </Box>

                <Box
                  ref={deviceListRef}
                  onScroll={handleDeviceScroll}
                  sx={{
                    flex: 1, overflowY: "auto", overflowX: "hidden", px: 1.5, pt: 1, pb: 1.5,
                    "&::-webkit-scrollbar": { width: 4 },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                    "&::-webkit-scrollbar-thumb": { background: UI.border, borderRadius: 2 },
                    "&::-webkit-scrollbar-thumb:hover": { background: UI.textMuted },
                  }}
                >
                  <Stack spacing={1.5}>
                    {devices.map((item) => (
                      <Box
                        key={item.uid}
                        onClick={() => { setSelectedImg(item.uid); setSelectedDevice(item); setImgBroken(false); setSnapshotCandidateIndex(0); setDeviceSnapshotNonce(Date.now()); }}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 2,
                          overflow: "hidden",
                          backgroundColor: selectedImg === item.uid ? UI.accentSoft : "transparent",
                          border: "none",
                          transition: "all 0.15s",
                        }}
                      >
                        <Box sx={{ width: "100%", aspectRatio: "16/10", overflow: "hidden", borderRadius: 1.5, backgroundColor: UI.inputBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {preferredSnapshotFor(item, listSnapshotNonce) ? (
                            <img
                              src={preferredSnapshotFor(item, listSnapshotNonce)}
                              alt={item.devicename}
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.2s" }}
                              onError={(e) => {
                                const candidates = snapshotCandidatesFor(item, listSnapshotNonce);
                                // The rendered src is the truth; the marker only covers a
                                // src the browser rewrote (a relative api snapshot).
                                const currentIndex = candidates.indexOf(e.target.src);
                                const nextIndex = (currentIndex >= 0 ? currentIndex : Number(e.target.dataset.snapshotIndex || 0)) + 1;
                                if (nextIndex < candidates.length) {
                                  e.target.dataset.snapshotIndex = String(nextIndex);
                                  e.target.src = candidates[nextIndex];
                                  return;
                                }
                                e.target.style.display = "none";
                                e.target.nextSibling.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <Box sx={{ display: preferredSnapshotFor(item, listSnapshotNonce) ? "none" : "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                            <VideocamOffIcon sx={{ fontSize: 20, color: UI.subtleIcon, mb: 0.25 }} />
                            <Typography sx={{ fontSize: 9, color: UI.textMuted }}>No Image</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ px: 0.5, pt: 0.75, pb: 0.25 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={0.5}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: UI.textMain, lineHeight: 1.3 }}>{item.devicename}</Typography>
                            <Typography sx={{ fontSize: 9, color: UI.textMuted, flexShrink: 0 }}>#{item.deviceid}</Typography>
                          </Stack>
                          <Typography sx={{ fontSize: 10, color: UI.textMuted, mt: 0.25 }}>
                            {item.displayname || "—"}{item.ServerName ? ` · ${item.ServerName}` : ""}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    {devicesLoading && (
                      <Box sx={{ px: 1, py: 0.5 }}>
                        {[1, 2, 3].map(i => (
                          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75 }}>
                            <Skeleton variant="circular" width={28} height={28} />
                            <Box sx={{ flex: 1 }}>
                              <Skeleton variant="text" width="60%" height={14} />
                              <Skeleton variant="text" width="40%" height={12} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                    {!devicesLoading && devices.length === 0 && (
                      <Typography sx={{ fontSize: 11, color: UI.textMuted, textAlign: "center", py: 3 }}>No devices found</Typography>
                    )}
                  </Stack>
                </Box>
                </Paper>
              </Box>
            </Grid>

            {/* MIDDLE PANEL */}
            <Grid item xs={8} md={10} lg={12}>
              <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", position: "relative" }}>
                {/* Header with Save/Cancel */}
                <Box sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: UI.panel,
                  borderBottom: `1px solid ${UI.border}`,
                  borderRadius: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minHeight: 48,
                  boxShadow: UI.shadowSm,
                }}>
                  {selectedImg ? (
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {selectedDevice?.devicename || "Untitled"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: UI.textMuted }}>
                        Shapes: {shapes.length}
                      </Typography>
                      <Typography variant="caption" sx={{ color: UI.textMuted }}>
                        {imgSize.width} x {imgSize.height}px
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: UI.textMuted }}>
                      Select a camera to start
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} alignItems="center">
                    {/* <Button
                      size="small"
                      onClick={handleClose}
                      sx={{
                        textTransform: "none", fontWeight: 600, fontSize: 12, px: 2, height: 32,
                        borderRadius: 1.5, color: UI.textMuted, backgroundColor: UI.inputBg,
                        border: "none", boxShadow: "none",
                        "&:hover": { backgroundColor: UI.hoverBg },
                      }}
                    >
                      Cancel
                    </Button> */}
                    <Button
                      size="small"
                      disabled={saving || configLoading || !selectedDevice?.deviceid}
                      onClick={async () => {
                        if (saving) return;
                        const deviceId = selectedDevice?.deviceid;
                        const imageKey = selectedImg;
                        const deviceSnapshot = selectedDevice;
                        const error = validateConfig();
                        if (error) {
                          setSnack({ open: true, severity: "warning", message: error });
                          return;
                        }
                        const comboError = validateComboConfig();
                        if (comboError) {
                          setSnack({ open: true, severity: "warning", message: comboError });
                          return;
                        }
                        const payload = buildConfigJSON();
                        const { payloads: comboPayloads } = buildComboSavePayloads();
                        setSaving(true);
                        try {
                          const postJson = async (path, body, label) => {
                            const response = await fetch(apiUrl(path), {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify(body),
                            });
                            if (!response.ok) {
                              // Surface the server's validation body — a 422 names the offending field.
                              let detail = "";
                              try { detail = await response.text(); } catch (e) { /* body already consumed */ }
                              console.error(`[SAVE] ${label} → HTTP ${response.status}`, detail);
                              console.error(`[SAVE] ${label} → request payload`, body);
                              throw new Error(`${label}: HTTP ${response.status}${detail ? ` — ${detail.slice(0, 300)}` : ""}`);
                            }
                            return response.json();
                          };

                          await postJson('/api/v1/update-camera-detection-config', payload, 'Detection config');
                          if (comboPayloads.length > 0) {
                            // Batch endpoint: every combo goes in one request under `combos`.
                            await postJson(
                              '/api/v1/update-camera-combo-config',
                              { camera_id: deviceId || 0, combos: comboPayloads },
                              `Combos ${comboPayloads.map(p => p.combo_rule_id).join(", ")}`
                            );
                          }
                          if (deviceId && imageKey) {
                            await loadDeviceConfig(deviceId, deviceSnapshot, imageKey);
                          }
                          setSnack({ open: true, severity: "success", message: "Configuration saved successfully" });
                        } catch (err) {
                          console.error("Save failed:", err);
                          if (deviceId && imageKey) {
                            await loadDeviceConfig(deviceId, deviceSnapshot, imageKey).catch(() => {});
                          }
                          const msg = err?.message?.includes("HTTP")
                            ? `Save did not complete cleanly; reloaded latest server state (${err.message})`
                            : "Save did not complete cleanly; reloaded latest server state";
                          setSnack({ open: true, severity: "error", message: msg });
                        } finally {
                          setSaving(false);
                        }
                      }}
                      sx={{
                        textTransform: "none", fontWeight: 600, fontSize: 12, px: 2.5, height: 32,
                        borderRadius: 1.5, color: UI.darkContrast, backgroundColor: UI.accent,
                        border: "none", boxShadow: "none",
                        "&:hover": { backgroundColor: theme.palette.primary.dark },
                        "&.Mui-disabled": { backgroundColor: UI.border, color: UI.textMuted },
                      }}
                    >
                      {saving ? <CircularProgress size={16} sx={{ color: UI.darkContrast, mr: 0.75 }} /> : null}
                      {saving ? "Saving..." : "Save"}
                    </Button>
                  </Stack>
                </Box>

                {/* Canvas area */}
                <Box
  ref={scrollContainerRef}
  sx={{
    backgroundColor: UI.gridBg,
    backgroundImage:
      `linear-gradient(${UI.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${UI.gridLine} 1px, transparent 1px)`,
    backgroundSize: "24px 24px",
    flex: 1,
    overflow: "auto",
    position: "relative",
    borderRadius: 0,
    border: `1px solid ${UI.border}`,
    boxShadow: UI.shadowMd,
    mt: 0,
    transition: "none",
    cursor:
      mode === "pan"
        ? (isPanning ? "grabbing" : "grab")
        : mode === "line" || mode === "rect" || mode === "poly" || mode === "ignore_rect" || mode === "ignore_poly"
        ? "crosshair"
        : "default",
  }}
  onMouseDown={(e) => {
    if (mode === "pan") {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    }
  }}
  onMouseMove={(e) => {
    if (mode === "pan" && isPanning && panStart && scrollContainerRef.current) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      scrollContainerRef.current.scrollLeft -= dx;
      scrollContainerRef.current.scrollTop -= dy;
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  }}
  onMouseUp={() => {
    if (isPanning) {
      setIsPanning(false);
      setPanStart(null);
    }
  }}
  onMouseLeave={() => {
    if (isPanning) {
      setIsPanning(false);
      setPanStart(null);
    }
  }}
>

                {!selectedImg ? (
                  <Fade in timeout={250}>
                    <Box
                      sx={{
                        minHeight: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        px: 4,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: UI.textMuted }}>
                        Select a camera from the left panel to begin.
                      </Typography>
                    </Box>
                  </Fade>
                ) : (
                  <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minWidth: "100%",
                    minHeight: "100%",
                  }}>
                  <Box sx={{
                    position: "relative",
                    flexShrink: 0,
                    width: imgSize.width ? imgSize.width * zoom : "auto",
                    height: imgSize.height ? imgSize.height * zoom : "auto",
                    transition: "width 0.2s, height 0.2s",
                  }}>
                    {/* Loading / Saving overlay */}
                    {(configLoading || saving) && (
                      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: UI.overlayBg, borderRadius: 1 }}>
                        <CircularProgress size={28} sx={{ color: UI.accent, mb: 1 }} />
                        <Typography sx={{ fontSize: 11, color: UI.dark, fontWeight: 600 }}>{saving ? "Saving..." : "Loading..."}</Typography>
                      </Box>
                    )}
                    {!imgBroken && activeSnapshot ? (
                      <img
                        src={activeSnapshot}
                        alt=""
                        style={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                        }}
                        onLoad={(e) => {
                          const natW = e.target.naturalWidth;
                          const natH = e.target.naturalHeight;
                          setImgSize({ width: natW, height: natH });
                          const container = scrollContainerRef.current;
                          if (container) {
                            const fitZoom = Math.min(
                              container.clientWidth / natW,
                              container.clientHeight / natH,
                              1
                            );
                            setZoom(Math.round(fitZoom * 100) / 100);
                          }
                        }}
                        onError={() => {
                          // This url had no frame for the camera — try the next one, and
                          // only give up once every candidate has been through.
                          if (snapshotCandidateIndex + 1 < activeSnapshotCandidates.length) {
                            setSnapshotCandidateIndex(snapshotCandidateIndex + 1);
                            return;
                          }
                          setImgBroken(true);
                          setImgSize({ width: 1200, height: 700 });
                        }}
                      />
                    ) : (
                      <Box sx={{ width: "100%", height: "100%", minHeight: 400, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: UI.inputBg, borderRadius: 1 }}>
                        <VideocamOffIcon sx={{ fontSize: 48, color: UI.subtleIcon, mb: 1 }} />
                        <Typography sx={{ fontSize: 13, color: UI.textMuted, fontWeight: 500 }}>No Image Available</Typography>
                        <Typography sx={{ fontSize: 11, color: UI.subtleIcon, mt: 0.5 }}>Camera snapshot could not be loaded</Typography>
                      </Box>
                    )}

                    {!imgBroken && activeSnapshot && (
                    <canvas
                      ref={canvasRef}
                      width={imgSize.width || 1200}
                      height={imgSize.height || 700}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                      onMouseDown={saving || configLoading ? undefined : handleMouseDown}
                      onMouseUp={saving || configLoading ? undefined : handleMouseUp}
                      onMouseMove={saving || configLoading ? undefined : handleMouseMove}
                      onDoubleClick={saving || configLoading ? undefined : handleDoubleClick}
                      onClick={saving || configLoading ? undefined : handleCanvasClick}
                    />
                    )}
                  </Box>
                  </Box>
                )}

              </Box>

              {/* Bottom Toolbar — Figma style */}
              {selectedImg && (
              <Paper
                elevation={0}
                sx={{
                  position: "absolute",
                  left: "50%",
                  bottom: 16,
                  transform: "translateX(-50%)",
                  zIndex: 1400,
                  borderRadius: 3,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  py: 0.25,
                  px: 0.5,
                  gap: 0.25,
                  backgroundColor: UI.panel,
                  border: `1px solid ${UI.border}`,
                }}
              >
                {[
                  { key: "select", icon: <NearMeIcon sx={{ fontSize: 18 }} />, tip: "Select (V)" },
                  { key: "pan", icon: <PanToolIcon sx={{ fontSize: 16 }} />, tip: "Pan (H)" },
                ].map(t => (
                  <Tooltip key={t.key} title={t.tip} placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={() => setMode(t.key)}
                      sx={{
                        color: mode === t.key ? "#fff" : UI.textMuted,
                        backgroundColor: mode === t.key ? UI.accent : "transparent",
                        borderRadius: 2,
                        width: 32,
                        height: 32,
                        transition: "all 0.15s",
                        "&:hover": { backgroundColor: mode === t.key ? UI.accent : "rgba(0,0,0,0.04)" },
                      }}
                    >
                      {t.icon}
                    </IconButton>
                  </Tooltip>
                ))}

                <Divider orientation="vertical" flexItem sx={{ borderColor: UI.border, mx: 0.25, my: 0.5 }} />

                {/* Ignore Area tools — dark block */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, backgroundColor: UI.dark, borderRadius: 2, px: 0.5, py: 0.25 }}>
                  {[
                    { key: "ignore_rect", icon: <CropSquareIcon sx={{ fontSize: 18 }} />, tip: "Ignore Rect (I)" },
                    { key: "ignore_poly", icon: <GestureIcon sx={{ fontSize: 18 }} />, tip: "Ignore Poly (O)" },
                  ].map(t => (
                    <Tooltip key={t.key} title={t.tip} placement="top" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setMode(t.key)}
                        sx={{
                          color: mode === t.key ? "#EF4444" : UI.darkIconMuted,
                          backgroundColor: mode === t.key ? "rgba(239,68,68,0.15)" : "transparent",
                          borderRadius: 1.5,
                          width: 30,
                          height: 30,
                          transition: "all 0.15s",
                          "&:hover": { backgroundColor: mode === t.key ? "rgba(239,68,68,0.15)" : UI.darkHover },
                        }}
                      >
                        {t.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                </Box>

                <Divider orientation="vertical" flexItem sx={{ borderColor: UI.border, mx: 0.25, my: 0.5 }} />

                {/* Zone drawing tools — green active */}
                {[
                  { key: "line", icon: <TimelineIcon sx={{ fontSize: 18 }} />, tip: "Line (L)" },
                  { key: "rect", icon: <CropSquareIcon sx={{ fontSize: 18 }} />, tip: "Rectangle (R)" },
                  { key: "poly", icon: <GestureIcon sx={{ fontSize: 18 }} />, tip: "Polygon (P)" },
                ].map(t => {
                  const activeColor = t.key === "line" ? "#2563EB" : "#10B981";
                  return (
                  <Tooltip key={t.key} title={t.tip} placement="top" arrow>
                    <span>
                    <IconButton
                      size="small"
                      onClick={() => setMode(t.key)}
                      sx={{
                        color: mode === t.key ? "#fff" : UI.textMuted,
                        backgroundColor: mode === t.key ? activeColor : "transparent",
                        borderRadius: 2,
                        width: 32,
                        height: 32,
                        transition: "all 0.15s",
                        "&:hover": { backgroundColor: mode === t.key ? activeColor : "rgba(0,0,0,0.04)" },
                        "&.Mui-disabled": { color: "rgba(0,0,0,0.15)" },
                      }}
                    >
                      {t.icon}
                    </IconButton>
                    </span>
                  </Tooltip>
                  );
                })}

                <Divider orientation="vertical" flexItem sx={{ borderColor: UI.border, mx: 0.25, my: 0.5 }} />

                <Tooltip title="Undo (Ctrl+Z)" placement="top" arrow>
                  <IconButton size="small" onClick={undo} sx={{ color: UI.textMuted, borderRadius: 2, width: 32, height: 32, "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" } }}>
                    <UndoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Redo (Ctrl+Y)" placement="top" arrow>
                  <IconButton size="small" onClick={redo} sx={{ color: UI.textMuted, borderRadius: 2, width: 32, height: 32, "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" } }}>
                    <RedoIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ borderColor: UI.border, mx: 0.25, my: 0.5 }} />

                <Tooltip title="Deselect (Esc)" placement="top" arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!selectedShapeId}
                      onClick={() => setSelectedShapeId(null)}
                      sx={{
                        color: selectedShapeId ? UI.textMuted : "rgba(15,23,42,0.2)",
                        borderRadius: 2,
                        width: 32,
                        height: 32,
                        "&:hover": { backgroundColor: selectedShapeId ? "rgba(0,0,0,0.06)" : "transparent" },
                      }}
                    >
                      <HighlightOffIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title="Delete (Del)" placement="top" arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!selectedShapeId}
                      onClick={() => {
                        const filtered = shapes.filter(s => s.id !== selectedShapeId);
                        pushHistory(filtered);
                        removeZoneConfig(selectedShapeId);
                        setSelectedShapeId(null);
                      }}
                      sx={{
                        color: selectedShapeId ? "#EF4444" : "rgba(15,23,42,0.2)",
                        borderRadius: 2,
                        width: 32,
                        height: 32,
                        "&:hover": { backgroundColor: selectedShapeId ? "rgba(239,68,68,0.08)" : "transparent" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Paper>
              )}

              {/* Zoom Control — compact horizontal bar, bottom-right */}
              {selectedImg && (
                <Paper
                  elevation={0}
                  sx={{
                    position: "absolute",
                    right: 16,
                    bottom: 16,
                    zIndex: 1400,
                    borderRadius: 3,
                    px: 0.5,
                    py: 0.25,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 0.25,
                    backgroundColor: UI.panel,
                    border: `1px solid ${UI.border}`,
                  }}
                >
                  <Tooltip title="Zoom out" placement="top">
                    <IconButton size="small" sx={{ p: 0.5, color: UI.textMuted }} onClick={() => setZoom(z => Math.max(0.1, +(z - 0.1).toFixed(2)))}>
                      <ZoomOutIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 38,
                      textAlign: "center",
                      fontSize: 11,
                      fontWeight: 600,
                      color: UI.textMain,
                      cursor: "pointer",
                      userSelect: "none",
                      lineHeight: 1,
                    }}
                    onClick={() => {
                      const container = scrollContainerRef.current;
                      if (container && imgSize.width && imgSize.height) {
                        const fitZoom = Math.min(
                          container.clientWidth / imgSize.width,
                          container.clientHeight / imgSize.height,
                          1
                        );
                        setZoom(Math.round(fitZoom * 100) / 100);
                      }
                    }}
                  >
                    {Math.round(zoom * 100)}%
                  </Typography>
                  <Tooltip title="Zoom in" placement="top">
                    <IconButton size="small" sx={{ p: 0.5, color: UI.textMuted }} onClick={() => setZoom(z => Math.min(3, +(z + 0.1).toFixed(2)))}>
                      <ZoomInIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ borderColor: UI.border, mx: 0.25 }} />
                  <Tooltip title="Fit to view" placement="top">
                    <IconButton size="small" sx={{ p: 0.5, color: UI.textMuted }} onClick={() => {
                      const container = scrollContainerRef.current;
                      if (container && imgSize.width && imgSize.height) {
                        const fitZoom = Math.min(
                          container.clientWidth / imgSize.width,
                          container.clientHeight / imgSize.height,
                          1
                        );
                        setZoom(Math.round(fitZoom * 100) / 100);
                      }
                    }}>
                      <ZoomOutMapIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Paper>
              )}
              </Box>
            </Grid>

            {/* RIGHT PANEL */}
            <Grid item xs={4} md={3} lg={2}>
              <Box sx={{ height: "100vh" }}>
                <Paper
                  ref={rightPanelRef}
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflowY: "auto",
                    borderRadius: 0,
                    backgroundColor: UI.bg,
                    borderLeft: `1px solid ${UI.border}`,
                    boxShadow: UI.shadowSm,
                  }}
                  className="rp"
                >

                {!selectedImg ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", px: 2 }}>
                    <Typography variant="body2" sx={{ color: UI.textMuted }} textAlign="center">
                      Select a camera to configure
                    </Typography>
                  </Box>
                ) : configLoading ? (
                  <Box sx={{ px: 2, py: 2 }}>
                    <LinearProgress sx={{ mb: 2, borderRadius: 1, "& .MuiLinearProgress-bar": { backgroundColor: UI.accent } }} />
                    {[1, 2, 3].map(i => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Skeleton variant="text" width="40%" height={18} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1.5 }} />
                      </Box>
                    ))}
                  </Box>
                ) : (<>

                {/* Zones */}
                <Box sx={{ px: 1.5, pt: 1, pb: 0.5, borderBottom: `1px solid ${UI.border}`, backgroundColor: UI.panel }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {[
                      { key: "model", label: "Model" },
                      ...(showComboTab ? [{ key: "combo", label: "Combo" }] : []),
                    ].map(tab => {
                      const active = configTab === tab.key;
                      return (
                        <Box
                          key={tab.key}
                          onClick={() => {
                            tabZoneMemory.current[configTab] = selectedShapeId;
                            const remembered = tabZoneMemory.current[tab.key];
                            // undefined = never visited this tab → carry zone over; null = user explicitly deselected
                            setSelectedShapeId(remembered !== undefined ? remembered : selectedShapeId);
                            setConfigTab(tab.key);
                          }}
                          sx={{
                            position: "relative",
                            height: 24,
                            pb: 0.25,
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            userSelect: "none",
                            fontSize: 12,
                            lineHeight: "16px",
                            fontWeight: active ? 600 : 500,
                            color: active ? UI.textMain : UI.textMuted,
                            "&::after": active ? {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              right: 0,
                              bottom: -4,
                              height: 2,
                              borderRadius: 2,
                              backgroundColor: "#0D99FF",
                            } : {},
                          }}
                        >
                          {tab.label}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Zones list — click to select for deletion */}
                {shapes.length > 0 && (
                  <Accordion expanded={zonesOpen} onChange={(_, v) => setZonesOpen(v)} ref={zonesRef}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                      <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>ZONES</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                      <Stack spacing={0.75}>
                        {shapes.map((s, idx) => (
                          <Box
                            key={s.id}
                            onClick={() => setSelectedShapeId(prev => prev === s.id ? null : s.id)}
                            sx={{
                              px: 1.25,
                              py: 0.75,
                              borderRadius: 1.5,
                              border: `1px solid ${s.id === selectedShapeId ? UI.accent : UI.border}`,
                              backgroundColor: s.id === selectedShapeId ? UI.accentSoft : UI.panel,
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              transition: "all 0.12s",
                              "&:hover": { borderColor: UI.accent },
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: getShapeColor(s).dot }} />
                              <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
                                {s.label || (s.category === "ignore" ? `Ignore ${idx + 1}` : `Zone ${idx + 1}`)}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" sx={{ color: UI.textMuted, fontSize: 10 }}>
                              {s.category === "ignore" ? "IGNORE" : s.type.toUpperCase()}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

{!isIgnoreSelected && (configTab === "model" || !showComboTab) && <>
                {/* Model Settings */}
                <Accordion expanded={modelSettingsAccOpen} onChange={(_, v) => setModelSettingsAccOpen(v)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                      <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>MODEL SETTINGS</Typography>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setModelSettingsAccOpen(true); setModelPopoverAnchor(e.currentTarget); }} sx={{ p: 0.25, mr: 0.5 }}>
                        <AddIcon sx={{ fontSize: 14, color: UI.accent }} />
                      </IconButton>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                    {useZoneConfig && (
                      <Typography variant="caption" sx={{ color: UI.accent, fontSize: 10, display: "block", mb: 0.5 }}>
                        Editing: {selectedZoneLabel}
                      </Typography>
                    )}

                    {/* Model picker popover */}
                    <Popover
                      open={Boolean(modelPopoverAnchor)}
                      anchorEl={modelPopoverAnchor}
                      onClose={() => setModelPopoverAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    >
                      <Box sx={{
                        ...POP_LIST_SX,
                        width: 200, maxHeight: 300, overflowY: "auto", px: 0.5, py: 0.5,
                        scrollbarWidth: "thin", scrollbarColor: `${UI.scrollThumb} transparent`,
                        "&::-webkit-scrollbar": { width: 4 },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": { backgroundColor: UI.scrollThumb, borderRadius: 4 },
                      }}>
                        {modelsLoading ? (
                          <Box sx={{ p: 1 }}>
                            {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={24} sx={{ mb: 0.5, borderRadius: 1, bgcolor: UI.softOverlay }} />)}
                          </Box>
                        ) : MODEL_GROUPS.length === 0 ? (
                          <Typography sx={{ fontSize: 11, color: UI.popTextMuted, textAlign: "center", py: 2 }}>No models available</Typography>
                        ) : MODEL_GROUPS.map((group, gi) => (
                          <Box key={group.header}>
                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.popTextSubtle, textTransform: "uppercase", letterSpacing: 0.5, px: 1, pt: gi === 0 ? 0.75 : 0.75, pb: 0.25 }}>{group.header}</Typography>
                            {group.models.map(m => (
                              <ListItemButton key={m.key} dense onClick={() => toggleModelInPopover(m.key)} sx={{ borderRadius: 1, px: 1, py: 0.25 }}>
                                <Checkbox size="small" checked={currentSelectedModels.includes(m.key)} sx={{ p: 0.25, mr: 0.5, color: UI.popIconMuted, "&.Mui-checked": { color: "#fff" } }} />
                                <ListItemText primary={m.label} primaryTypographyProps={{ fontSize: 11, color: UI.textMain }} />
                              </ListItemButton>
                            ))}
                            {gi < MODEL_GROUPS.length - 1 && <Divider sx={{ my: 0.5, borderColor: UI.softOverlay }} />}
                          </Box>
                        ))}
                      </Box>
                    </Popover>

                    {/* Added models list */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                      {currentSelectedModels.map(mKey => {
                        const m = MODEL_OPTIONS.find(o => o.key === mKey);
                        return (
                          <Chip
                            key={mKey}
                            label={m?.label || mKey}
                            size="small"
                            sx={{
                              height: 22, fontSize: 10, fontWeight: 600,
                              backgroundColor: currentActiveModel === mKey ? UI.accent : "rgba(145,158,171,0.16)",
                              color: currentActiveModel === mKey ? "#fff" : "#919EAB",
                              cursor: "pointer",
                              "& .MuiChip-deleteIcon": { fontSize: 12, color: currentActiveModel === mKey ? "rgba(255,255,255,0.7)" : UI.textMuted },
                            }}
                            onClick={() => activateModel(mKey)}
                            onDelete={() => removeModel(mKey)}
                          />
                        );
                      })}
                    </Box>

                    {currentActiveModel && currentSelectedModels.includes(currentActiveModel) && (
                      <>
                        {/* Sliders — 2-column grid */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1, mb: 1 }}>
                          {[
                            { label: "Confidence", field: "conf", min: 0.1, max: 1, step: 0.05 },
                            { label: "IoU", field: "iou", min: 0.1, max: 1, step: 0.05 },
                            { label: "Detect FPS", field: "detect_fps", min: 1, max: 30, step: 1 },
                            { label: "Sensitivity", field: "motion_sensitivity", min: 0, max: 1, step: 0.01 },
                          ].map(s => (
                            <Box key={s.field}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography sx={{ fontSize: 10, color: UI.textMuted }}>{s.label}</Typography>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.textMain }}>{getActiveModelSettings()[s.field]}</Typography>
                              </Stack>
                              <Slider size="small" value={getActiveModelSettings()[s.field]} min={s.min} max={s.max} step={s.step} onChange={(_, v) => updateActiveModelField(s.field, v)} sx={{ py: 0.5 }} />
                            </Box>
                          ))}
                        </Box>

                        {/* Switches — compact 2-col */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5, mb: 1 }}>
                          {[
                            { key: "augment", label: "Augment" },
                            { key: "agnostic_nms", label: "Agnostic NMS" },
                            { key: "is_zoom_motion_area", label: "Zoom Motion" },
                            { key: "enable_detect_always", label: "Always Detect" },
                            { key: "enable_motion", label: "Motion" },
                          ].map(opt => (
                            <FormControlLabel
                              key={opt.key}
                              control={<Switch size="small" checked={getActiveModelSettings()[opt.key]} onChange={(e) => updateActiveModelField(opt.key, e.target.checked)} />}
                              label={<Typography sx={{ fontSize: 10 }}>{opt.label}</Typography>}
                              sx={{ ml: 0, mr: 0 }}
                              labelPlacement="start"
                            />
                          ))}
                        </Box>

                        {/* Model Format */}
                        <Divider sx={{ my: 1 }} />
                        <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Model Format</Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                          {MODEL_FORMATS.map(f => (
                            <Chip
                              key={f.key}
                              label={f.label}
                              size="small"
                              onClick={() => updateActiveModelField("model_format", f.key)}
                              sx={{
                                height: 22, fontSize: 10, fontWeight: 600, cursor: "pointer",
                                backgroundColor: getActiveModelSettings().model_format === f.key ? UI.dark : "rgba(145,158,171,0.16)",
                                color: getActiveModelSettings().model_format === f.key ? "#fff" : "#919EAB",
                              }}
                            />
                          ))}
                        </Box>

                        {/* Communication — inline Figma style */}
                        <Divider sx={{ my: 1 }} />
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>Communication</Typography>
                          <WifiIcon sx={{ fontSize: 13, color: UI.textMuted }} />
                        </Stack>
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Protocol</Typography>
                          <Box sx={{ display: "flex", borderRadius: 1, border: `1px solid ${UI.border}`, overflow: "hidden", height: 28 }}>
                            {["grpc", "http"].map(type => (
                              <Box
                                key={type}
                                onClick={() => updateActiveModelField("comm_type", type)}
                                sx={{
                                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 10, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                                  backgroundColor: getActiveModelSettings().comm_type === type ? UI.dark : "transparent",
                                  color: getActiveModelSettings().comm_type === type ? "#fff" : UI.textMuted,
                                  "&:hover": { backgroundColor: getActiveModelSettings().comm_type === type ? UI.dark : "rgba(0,0,0,0.03)" },
                                }}
                              >
                                {type.toUpperCase()}
                              </Box>
                            ))}
                          </Box>
                        </Box>

                        {getActiveModelSettings().comm_type === "grpc" ? (
                          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1 }}>
                            <Box>
                              <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>IP Address</Typography>
                              <TextField size="small" fullWidth placeholder={defaultCommIp} value={getActiveModelSettings().comm_ip} onChange={(e) => updateActiveModelField("comm_ip", e.target.value)} error={!isValidIp(getActiveModelSettings().comm_ip)} InputProps={{ startAdornment: <InputAdornment position="start"><SettingsEthernetIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Port</Typography>
                              <TextField size="small" fullWidth placeholder="50051" value={getActiveModelSettings().comm_port} onChange={(e) => updateActiveModelField("comm_port", e.target.value)} error={!isValidPort(getActiveModelSettings().comm_port)} InputProps={{ startAdornment: <InputAdornment position="start"><NumbersIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }} />
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1 }}>
                            <Box>
                              <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>URL</Typography>
                              <TextField size="small" fullWidth placeholder="https://api.example.com" value={getActiveModelSettings().comm_url} onChange={(e) => updateActiveModelField("comm_url", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SettingsEthernetIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Path</Typography>
                              <TextField size="small" fullWidth placeholder="/v1/detect" value={getActiveModelSettings().comm_path} onChange={(e) => updateActiveModelField("comm_path", e.target.value)} />
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Detection Classes */}
                <Accordion expanded={detectionClassesAccOpen} onChange={(_, v) => setDetectionClassesAccOpen(v)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                      <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>DETECTION CLASSES</Typography>
                      {currentActiveModel && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDetectionClassesAccOpen(true); setClassPopoverAnchor(e.currentTarget); }} sx={{ p: 0.25, mr: 0.5 }}>
                          <AddIcon sx={{ fontSize: 14, color: UI.dark }} />
                        </IconButton>
                      )}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                    {!currentActiveModel ? (
                      <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", textAlign: "center", py: 1 }}>Select a model first</Typography>
                    ) : (
                      <>
                        {/* Class chips */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                          {getActiveModelClasses().map(cKey => {
                            const cls = MODEL_CLASSES.find(c => c.key === cKey);
                            return (
                              <Chip
                                key={cKey}
                                label={cls?.label || cKey}
                                size="small"
                                sx={{
                                  height: 22, fontSize: 10, fontWeight: 600,
                                  backgroundColor: currentActiveClassName === cKey ? UI.dark : "rgba(145,158,171,0.16)",
                                  color: currentActiveClassName === cKey ? "#fff" : "#919EAB",
                                  cursor: "pointer",
                                  "& .MuiChip-deleteIcon": { fontSize: 12, color: currentActiveClassName === cKey ? "rgba(255,255,255,0.7)" : UI.textMuted },
                                }}
                                onClick={() => setCurrentActiveClassName(cKey)}
                                onDelete={getActiveModelClasses().length > 1 ? () => removeModelClass(cKey) : undefined}
                              />
                            );
                          })}
                        </Box>

                        {/* Class picker popover */}
                        <Popover open={Boolean(classPopoverAnchor)} anchorEl={classPopoverAnchor} onClose={() => setClassPopoverAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
                          <Box sx={{
                            ...POP_LIST_SX,
                            width: 200, maxHeight: 300, overflowY: "auto", px: 0.5, py: 0.5,
                            scrollbarWidth: "thin", scrollbarColor: `${UI.scrollThumb} transparent`,
                            "&::-webkit-scrollbar": { width: 4 },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": { backgroundColor: UI.scrollThumb, borderRadius: 4 },
                          }}>
                            {modelsLoading ? (
                              <Box sx={{ p: 1 }}>
                                {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={24} sx={{ mb: 0.5, borderRadius: 1, bgcolor: UI.softOverlay }} />)}
                              </Box>
                            ) : (() => {
                              const activeModelClasses = MODEL_OPTIONS.find(m => m.key === currentActiveModel)?.classes || [];
                              return activeModelClasses.length === 0 ? (
                                <Typography sx={{ fontSize: 11, color: UI.popTextMuted, textAlign: "center", py: 2 }}>No classes available</Typography>
                              ) : activeModelClasses.map(cls => (
                                <ListItemButton key={cls.key} dense onClick={() => toggleClassInPopover(cls.key)} sx={{ borderRadius: 1, px: 1, py: 0.25 }}>
                                  <Checkbox size="small" checked={getActiveModelClasses().includes(cls.key)} sx={{ p: 0.25, mr: 0.5, color: UI.popIconMuted, "&.Mui-checked": { color: "#fff" } }} />
                                  <ListItemText primary={cls.label} primaryTypographyProps={{ fontSize: 11, color: UI.textMain }} />
                                </ListItemButton>
                              ));
                            })()}
                          </Box>
                        </Popover>


                        {currentActiveClassName && getActiveModelClasses().includes(currentActiveClassName) && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>
                                {MODEL_CLASSES.find(c => c.key === currentActiveClassName)?.label} Tracking
                              </Typography>
                              <FormControlLabel
                                control={<Switch size="small" checked={getActiveClassTracking().enabled !== false} onChange={(e) => updateModelClassTracking("enabled", e.target.checked)} />}
                                label={<Typography sx={{ fontSize: 10, color: UI.textMuted }}>Enable</Typography>}
                                sx={{ ml: 0, mr: 0 }}
                                labelPlacement="start"
                              />
                            </Stack>

                            {getActiveClassTracking().enabled !== false && (<>
                            {/* Tracking sliders — 2-col */}
                            {/* Text inputs row — label on top, input below */}
                            <Stack direction="row" gap={1} mb={1}>
                              {[
                                { label: "Max Disappear (sec)", field: "max_disappeared" },
                                { label: "Min Duration(sec)", field: "min_duration_sec" },
                              ].map(s => (
                                <Stack key={s.field} direction="column" flex={1}>
                                  <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.3 }}>{s.label}</Typography>
                                  <input
                                    type="number"
                                    value={getActiveClassTracking()[s.field]}
                                    onChange={(e) => updateModelClassTracking(s.field, Number(e.target.value))}
                                    style={{ width: "100%", fontSize: 10, textAlign: "center", border: `1px solid ${UI.border ?? "#ccc"}`, borderRadius: 4, padding: "4px 6px", background: "transparent", color: UI.textMain, boxSizing: "border-box" }}
                                  />
                                </Stack>
                              ))}
                            </Stack>

                            {/* Slider grid */}
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1, mb: 1 }}>
                              {[
                                { label: "Min Distance", field: "min_distance", min: 0, max: 1, step: 0.01 },
                                { label: "FPS", field: "fps", min: 1, max: 30, step: 1 },
                              ].map(s => (
                                <Box key={s.field}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: 10, color: UI.textMuted }}>{s.label}</Typography>
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.textMain }}>{getActiveClassTracking()[s.field]}</Typography>
                                  </Stack>
                                  <Slider size="small" value={getActiveClassTracking()[s.field]} min={s.min} max={s.max} step={s.step} onChange={(_, v) => updateModelClassTracking(s.field, v)} sx={{ py: 0.5, color: UI.dark }} />
                                </Box>
                              ))}
                            </Box>

                            {/* Tracking switches */}
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5, mb: 0.5 }}>
                              {[
                                { key: "ignore_motion_after_tracking", label: "Ignore Motion" },
                                { key: "enable_single_track_video", label: "Single Track" },
                              ].map(opt => (
                                <FormControlLabel
                                  key={opt.key}
                                  control={<Switch size="small" checked={getActiveClassTracking()[opt.key]} onChange={(e) => updateModelClassTracking(opt.key, e.target.checked)} />}
                                  label={<Typography sx={{ fontSize: 10 }}>{opt.label}</Typography>}
                                  sx={{ ml: 0, mr: 0 }}
                                  labelPlacement="start"
                                />
                              ))}
                            </Box>

                            {/* Ignore motion time — visible when ignore_motion_after_tracking enabled */}
                            {getActiveClassTracking().ignore_motion_after_tracking && (
                              <Box sx={{ mb: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography sx={{ fontSize: 10, color: UI.textMuted }}>Ignore Time (sec)</Typography>
                                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.textMain }}>{getActiveClassTracking().ignore_motion_after_tracking_time}</Typography>
                                </Stack>
                                <Slider size="small" value={getActiveClassTracking().ignore_motion_after_tracking_time} min={1} max={60} step={1} onChange={(_, v) => updateModelClassTracking("ignore_motion_after_tracking_time", v)} sx={{ py: 0.5, color: UI.dark }} />
                              </Box>
                            )}

                            {/* Count toggle */}
                            <FormControlLabel
                              control={<Switch size="small" checked={getActiveClassTracking().count_objects} onChange={(e) => updateModelClassTracking("count_objects", e.target.checked)} />}
                              label={<Typography sx={{ fontSize: 10 }}>Count</Typography>}
                              sx={{ ml: 0, mr: 0, mb: 0.5 }}
                              labelPlacement="start"
                            />

                            {/* Count options — visible when count enabled */}
                            {getActiveClassTracking().count_objects && (
                              <Box sx={{ p: 1, borderRadius: 1.5, border: `1px solid ${UI.border}`, backgroundColor: UI.surface }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 600, color: UI.dark, mb: 0.75 }}>Count Settings</Typography>
                                <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Track Direction</Typography>
                                <Box sx={{ display: "flex", borderRadius: 1, border: `1px solid ${UI.border}`, overflow: "hidden", height: 28, mb: 1 }}>
                                  {[
                                    { key: "TTB", icon: <ArrowDownwardIcon sx={{ fontSize: 14 }} />, tip: "Top to Bottom" },
                                    { key: "BTT", icon: <ArrowUpwardIcon sx={{ fontSize: 14 }} />, tip: "Bottom to Top" },
                                    { key: "LTR", icon: <ArrowForwardIcon sx={{ fontSize: 14 }} />, tip: "Left to Right" },
                                    { key: "RTL", icon: <ArrowBackIcon sx={{ fontSize: 14 }} />, tip: "Right to Left" },
                                  ].map(opt => (
                                    <Tooltip key={opt.key} title={opt.tip} placement="top" arrow>
                                      <Box
                                        onClick={() => updateModelClassTracking("track_direction", opt.key)}
                                        sx={{
                                          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                          cursor: "pointer", transition: "all 0.15s",
                                          backgroundColor: getActiveClassTracking().track_direction === opt.key ? UI.dark : "transparent",
                                          color: getActiveClassTracking().track_direction === opt.key ? "#fff" : UI.textMuted,
                                          "&:hover": { backgroundColor: getActiveClassTracking().track_direction === opt.key ? UI.dark : "rgba(0,0,0,0.03)" },
                                        }}
                                      >
                                        {opt.icon}
                                      </Box>
                                    </Tooltip>
                                  ))}
                                </Box>

                                <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Count Boundary</Typography>
                                {countableShapesWithLabel.length > 0 ? (
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 240 }}>
                                    {countableShapesWithLabel.map((cs, csIdx) => {
                                      const active = getActiveClassCountShapeIds().includes(cs.id);
                                      const clsLabel = MODEL_CLASSES.find(c => c.key === currentActiveClassName)?.label || currentActiveClassName;
                                      const chipLabel = active ? `${clsLabel}-Count ${csIdx + 1}` : cs.label;
                                      return (
                                        <Chip
                                          key={cs.id}
                                          label={chipLabel}
                                          size="small"
                                          onClick={() => toggleCountShape(cs.id)}
                                          onMouseEnter={() => setHoveredZoneId(cs.id)}
                                          onMouseLeave={() => setHoveredZoneId(null)}
                                          sx={{
                                            height: 20, fontSize: 9, fontWeight: 600, cursor: "pointer",
                                            backgroundColor: active ? "#2563EB" : "rgba(145,158,171,0.16)",
                                            color: active ? "#fff" : "#919EAB",
                                            "&:hover": { backgroundColor: active ? "#3B82F6" : "#D1D5DB" },
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                ) : (
                                  <Typography sx={{ fontSize: 10, color: UI.textMuted, fontStyle: "italic" }}>
                                    Draw shapes on canvas to assign as count boundary.
                                  </Typography>
                                )}
                                {getActiveClassCountShapeIds().length === 0 && countableShapesWithLabel.length > 0 && (
                                  <Typography sx={{ fontSize: 9, color: UI.textMuted, fontStyle: "italic", mt: 0.5 }}>
                                    No boundary assigned
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {/* License plate hidden for clients whose config sets hide_license_plate */}
                            {!hideLicensePlate && (<>
                              <Divider sx={{ my: 1 }} />

                              {/* License plate toggle */}
                              <FormControlLabel
                                control={<Switch size="small" checked={Boolean(getActiveClassTracking().enable_license_plate)} onChange={(e) => updateModelClassTracking("enable_license_plate", e.target.checked)} />}
                                label={<Typography sx={{ fontSize: 10 }}>License Plate Recognition</Typography>}
                                sx={{ ml: 0, mr: 0, mb: 0.5 }}
                                labelPlacement="start"
                              />

                              {/* License plate options — visible when license plate enabled */}
                              {getActiveClassTracking().enable_license_plate && (
                                <Box sx={{ p: 1, borderRadius: 1.5, border: `1px solid ${UI.border}`, backgroundColor: UI.surface }}>
                                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: UI.dark, mb: 0.75 }}>License Plate Settings</Typography>

                                  <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Model</Typography>
                                  <Box sx={{ display: "flex", borderRadius: 1, border: `1px solid ${UI.border}`, overflow: "hidden", height: 28, mb: 1 }}>
                                    {LICENSE_PLATE_MODELS.map(opt => {
                                      const active = (getActiveClassTracking().license_plate_model || "small") === opt.key;
                                      return (
                                        <Box
                                          key={opt.key}
                                          onClick={() => updateModelClassTracking("license_plate_model", opt.key)}
                                          sx={{
                                            flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                            fontSize: 10, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                                            backgroundColor: active ? UI.dark : "transparent",
                                            color: active ? "#fff" : UI.textMuted,
                                            "&:hover": { backgroundColor: active ? UI.dark : "rgba(0,0,0,0.03)" },
                                          }}
                                        >
                                          {opt.label}
                                        </Box>
                                      );
                                    })}
                                  </Box>

                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography sx={{ fontSize: 10, color: UI.textMuted }}>Confidence</Typography>
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.textMain }}>{getActiveClassTracking().license_plate_conf ?? 0.5}</Typography>
                                  </Stack>
                                  <Slider
                                    size="small"
                                    value={getActiveClassTracking().license_plate_conf ?? 0.5}
                                    min={0.1}
                                    max={1}
                                    step={0.05}
                                    onChange={(_, v) => updateModelClassTracking("license_plate_conf", v)}
                                    sx={{ py: 0.5, color: UI.dark }}
                                  />

                                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 1, rowGap: 1, mt: 0.5 }}>
                                    <Box>
                                      <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.5 }}>IP Address</Typography>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="127.0.0.1"
                                        value={getActiveClassTracking().license_plate_ip ?? ""}
                                        onChange={(e) => updateModelClassTracking("license_plate_ip", e.target.value)}
                                        error={!isValidIp(getActiveClassTracking().license_plate_ip)}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><SettingsEthernetIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }}
                                      />
                                    </Box>
                                    <Box>
                                      <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.5 }}>Port</Typography>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="50051"
                                        value={getActiveClassTracking().license_plate_port ?? ""}
                                        onChange={(e) => updateModelClassTracking("license_plate_port", e.target.value)}
                                        error={!isValidPort(getActiveClassTracking().license_plate_port)}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><NumbersIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }}
                                      />
                                    </Box>
                                  </Box>
                                </Box>
                              )}
                            </>)}
                            </>)}
                          </>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Monitoring Times */}
                <Accordion expanded={modelMonAccOpen} onChange={(_, v) => setModelMonAccOpen(v)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                      <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>MONITORING TIMES</Typography>
                      {currentActiveModel && currentActiveClassName && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setModelMonAccOpen(true); addClassTimeRange(); setTimeout(() => rightPanelRef.current?.scrollTo({ top: rightPanelRef.current.scrollHeight, behavior: "smooth" }), 50); }} sx={{ p: 0.25, mr: 0.5 }}>
                          <AddIcon sx={{ fontSize: 14, color: UI.accent }} />
                        </IconButton>
                      )}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                    {!currentActiveModel || !currentActiveClassName || !getActiveModelClasses().includes(currentActiveClassName) ? (
                      <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", textAlign: "center", py: 1 }}>Select a model and class first</Typography>
                    ) : (
                      <>
                        <Typography variant="caption" sx={{ color: UI.textMuted, fontSize: 10, display: "block", mb: 0.75 }}>
                          {MODEL_OPTIONS.find(m => m.key === currentActiveModel)?.label} &gt; {MODEL_CLASSES.find(c => c.key === currentActiveClassName)?.label}
                        </Typography>

                        {getActiveClassSchedule().length === 0 && (
                          <Stack alignItems="center" spacing={0.5} sx={{ py: 2 }}>
                            <AccessTimeIcon sx={{ fontSize: 22, color: UI.textMuted, opacity: 0.5 }} />
                            <Typography variant="caption" sx={{ color: UI.textMain, fontWeight: 600, textAlign: "center" }}>Monitoring 24/7</Typography>
                            <Typography variant="caption" sx={{ color: UI.textMuted, fontSize: 10, textAlign: "center" }}>No times configured — add a range to limit when this runs.</Typography>
                          </Stack>
                        )}

                        {getActiveClassSchedule().map((range, idx) => (
                          <Box key={idx} sx={{ mb: 1.5, p: 1.5, border: `1px solid ${UI.border}`, borderRadius: 2, backgroundColor: UI.panel }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                              <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Range {idx + 1}</Typography>
                              <IconButton size="small" onClick={() => removeClassTimeRange(idx)} sx={{ p: 0.25 }}>
                                <DeleteIcon sx={{ fontSize: 13, color: UI.textMuted }} />
                              </IconButton>
                            </Stack>

                            <Stack direction="row" spacing={1} mb={1}>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.25 }}>From</Typography>
                                <TimeInput24 value={range.startTime} onChange={(v) => updateClassTimeRange(idx, "startTime", v)} borderColor={UI.border} bgColor={UI.surface} textColor={UI.textMain} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.25 }}>To</Typography>
                                <TimeInput24 value={range.endTime} onChange={(v) => updateClassTimeRange(idx, "endTime", v)} borderColor={UI.border} bgColor={UI.surface} textColor={UI.textMain} />
                              </Box>
                            </Stack>

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {DAYS.map(day => (
                                <Chip
                                  key={day}
                                  label={day}
                                  size="small"
                                  onClick={() => toggleClassDay(idx, day)}
                                  sx={{
                                    cursor: "pointer", fontSize: 10, height: 22, fontWeight: 600,
                                    backgroundColor: range.days.includes(day) ? UI.accent : "transparent",
                                    color: range.days.includes(day) ? "#fff" : UI.textMuted,
                                    border: `1px solid ${range.days.includes(day) ? UI.accent : UI.border}`,
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
                </>}

                {!isIgnoreSelected && showComboTab && configTab === "combo" && <>

                {/* Zone → Combo assignment (shown only when a zone is selected) */}
                {useZoneConfig && selectedShapeId && (
                  <Box sx={{ px: 1.5, py: 1.25, mb: 0.5, background: `${UI.accent}12`, borderRadius: 1.5, border: `1px solid ${UI.accent}30` }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.accent, mb: 0.75, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {selectedZoneLabel} — Assign to Combos
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.75 }}>
                      Toggle which combos use this zone as their detection boundary.
                    </Typography>
                    {comboList.length === 0 ? (
                      <Typography sx={{ fontSize: 10, color: UI.textMuted, fontStyle: "italic" }}>No combos available.</Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {comboList.map(combo => {
                          const comboId = getComboId(combo);
                          const cs = comboSettings[comboId] || DEFAULT_COMBO_CFG;
                          const active = (cs.boundaryZoneIds || []).includes(String(selectedShapeId));
                          return (
                            <Chip
                              key={comboId}
                              label={combo?.display_name || combo?.combo_name || comboId}
                              size="small"
                              onClick={() => {
                                setComboSettings(prev => {
                                  const c = prev[comboId] || { ...DEFAULT_COMBO_CFG };
                                  const current = c.boundaryZoneIds || [];
                                  const zid = String(selectedShapeId);
                                  const next = current.includes(zid) ? current.filter(id => id !== zid) : [...current, zid];
                                  return { ...prev, [comboId]: { ...c, boundaryZoneIds: next } };
                                });
                              }}
                              sx={{
                                height: 22, fontSize: 10, fontWeight: 600, cursor: "pointer",
                                backgroundColor: active ? UI.accent : "rgba(145,158,171,0.12)",
                                color: active ? "#fff" : UI.textMuted,
                                border: `1px solid ${active ? UI.accent : UI.border}`,
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Combo Class Config */}
                <Accordion expanded={comboClassAccOpen} onChange={(_, v) => setComboClassAccOpen(v)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                      <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>COMBO CLASS CONFIG</Typography>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setComboClassAccOpen(true); setComboPopoverAnchor(e.currentTarget); }} sx={{ p: 0.25, mr: 0.5 }}>
                        <AddIcon sx={{ fontSize: 14, color: UI.accent }} />
                      </IconButton>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                    <Popover
                      open={Boolean(comboPopoverAnchor)}
                      anchorEl={comboPopoverAnchor}
                      onClose={() => setComboPopoverAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    >
                      <Box sx={{
                        ...POP_LIST_SX,
                        width: 240, maxHeight: 300, overflowY: "auto", px: 0.5, py: 0.5,
                        scrollbarWidth: "thin", scrollbarColor: `${UI.scrollThumb} transparent`,
                        "&::-webkit-scrollbar": { width: 4 },
                        "&::-webkit-scrollbar-track": { background: "transparent" },
                        "&::-webkit-scrollbar-thumb": { backgroundColor: UI.scrollThumb, borderRadius: 4 },
                      }}>
                        {combosLoading ? (
                          <Box sx={{ p: 1 }}>
                            {[1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={24} sx={{ mb: 0.5, borderRadius: 1, bgcolor: UI.softOverlay }} />)}
                          </Box>
                        ) : comboList.length === 0 ? (
                          <Typography sx={{ fontSize: 11, color: UI.popTextMuted, textAlign: "center", py: 2 }}>No combos available</Typography>
                        ) : (
                          comboList.map(combo => {
                            const comboId = getComboId(combo);
                            return (
                              <ListItemButton key={comboId} dense onClick={() => toggleCurrentComboInPopover(comboId)} sx={{ borderRadius: 1, px: 1, py: 0.25 }}>
                                <Checkbox size="small" checked={currentSelectedCombos.includes(comboId)} sx={{ p: 0.25, mr: 0.5, color: UI.popIconMuted, "&.Mui-checked": { color: "#fff" } }} />
                                <ListItemText primary={combo.display_name || combo.combo_name || comboId} primaryTypographyProps={{ fontSize: 11, color: UI.textMain }} />
                              </ListItemButton>
                            );
                          })
                        )}
                      </Box>
                    </Popover>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                      {currentSelectedCombos.map(comboId => {
                        const combo = comboList.find(c => getComboId(c) === comboId);
                        return (
                          <Chip
                            key={comboId}
                            label={combo?.display_name || combo?.combo_name || comboId}
                            size="small"
                            sx={{
                              height: 22, fontSize: 10, fontWeight: 600,
                              backgroundColor: currentActiveComboId === comboId ? UI.accent : "rgba(145,158,171,0.16)",
                              color: currentActiveComboId === comboId ? "#fff" : "#919EAB",
                              cursor: "pointer",
                              "& .MuiChip-deleteIcon": { fontSize: 12, color: currentActiveComboId === comboId ? "rgba(255,255,255,0.7)" : UI.textMuted },
                            }}
                            onClick={() => setCurrentActiveComboId(comboId)}
                            onDelete={() => useZoneConfig ? toggleCurrentComboInPopover(comboId) : removeCombo(comboId)}
                          />
                        );
                      })}
                    </Box>

                    {!currentActiveComboId && !useZoneConfig ? (
                      <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", textAlign: "center", py: 1 }}>Select a combo first</Typography>
                    ) : (
                      <>
                        <Typography variant="caption" sx={{ color: UI.accent, fontSize: 10, display: "block", mb: 0.5 }}>
                          {getActiveCombo()?.display_name || getActiveCombo()?.combo_name || currentActiveComboId}
                        </Typography>
                        <Typography variant="caption" sx={{ color: UI.textMuted, fontSize: 10, display: "block", mb: 0.75 }}>
                          Combo classes are fixed. Select class to configure model.
                        </Typography>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                          {(getActiveCombo()?.items || []).filter(it => it.class_id).map((item) => {
                            const classId = String(item.class_id);
                            const classLabel = item.class_display_name || item.class_name || ALL_MODEL_CLASSES.find(c => c.key === classId)?.label || `Class ${classId}`;
                            const active = currentActiveComboClassId === classId;
                            return (
                              <Chip
                                key={classId}
                                label={classLabel}
                                size="small"
                                onClick={() => setCurrentActiveComboClassId(classId)}
                                sx={{
                                  height: 22, fontSize: 10, fontWeight: 600, cursor: "pointer",
                                  backgroundColor: active ? UI.dark : "rgba(145,158,171,0.16)",
                                  color: active ? "#fff" : "#919EAB",
                                }}
                              />
                            );
                          })}
                        </Box>

                        {currentActiveComboClassId && (
                          <>
                            <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.5 }}>Model Version</Typography>
                            <Box sx={{ mb: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => setComboClassModelPopoverAnchor(e.currentTarget)}
                                sx={{
                                  textTransform: "none", fontSize: 11, justifyContent: "space-between", width: "100%",
                                  color: getActiveComboClassModel().model_version_id ? UI.dark : UI.textMuted,
                                  borderColor: UI.border,
                                }}
                              >
                                {getActiveComboClassModel().model_version_id
                                  ? (ALL_MODEL_OPTIONS.find(o => o.key === String(getActiveComboClassModel().model_version_id))?.label || `ID: ${getActiveComboClassModel().model_version_id}`)
                                  : "Select model..."}
                              </Button>
                            </Box>

                            <Popover
                              open={Boolean(comboClassModelPopoverAnchor)}
                              anchorEl={comboClassModelPopoverAnchor}
                              onClose={() => setComboClassModelPopoverAnchor(null)}
                              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            >
                              <Box sx={{
                                ...POP_LIST_SX,
                                width: 220, maxHeight: 300, overflowY: "auto", px: 0.5, py: 0.5,
                                "&::-webkit-scrollbar": { width: 4 },
                                "&::-webkit-scrollbar-track": { background: "transparent" },
                                "&::-webkit-scrollbar-thumb": { backgroundColor: UI.scrollThumb, borderRadius: 4 },
                              }}>
                                {MODEL_GROUPS.length === 0 ? (
                                  <Typography sx={{ fontSize: 11, color: UI.popTextMuted, textAlign: "center", py: 2 }}>No models available</Typography>
                                ) : MODEL_GROUPS.map((group, gi) => (
                                  <Box key={group.header}>
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.popTextSubtle, textTransform: "uppercase", letterSpacing: 0.5, px: 1, pt: 0.75, pb: 0.25 }}>{group.header}</Typography>
                                    {group.models.map(m => {
                                      const isSelected = Number(getActiveComboClassModel().model_version_id) === Number(m.key);
                                      return (
                                        <ListItemButton
                                          key={m.key}
                                          dense
                                          onClick={() => {
                                            updateCurrentComboClassModelField(currentActiveComboClassId, "model_version_id", m.key);
                                            updateCurrentComboClassModelField(currentActiveComboClassId, "model_format", modelFormatFor(m.key));
                                            setComboClassModelPopoverAnchor(null);
                                          }}
                                          sx={{ borderRadius: 1, px: 1, py: 0.25, backgroundColor: isSelected ? UI.popSelectedBg : "transparent" }}
                                        >
                                          <ListItemText primary={m.label} primaryTypographyProps={{ fontSize: 11, color: UI.textMain, fontWeight: isSelected ? 700 : 400 }} />
                                        </ListItemButton>
                                      );
                                    })}
                                    {gi < MODEL_GROUPS.length - 1 && <Divider sx={{ my: 0.5, borderColor: UI.softOverlay }} />}
                                  </Box>
                                ))}
                              </Box>
                            </Popover>

                            {getActiveComboClassModel().model_version_id && (
                              <>
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1, mb: 1 }}>
                                  {[
                                    { label: "Confidence", field: "conf", min: 0.1, max: 1, step: 0.05 },
                                    { label: "IoU", field: "iou", min: 0.1, max: 1, step: 0.05 },
                                    { label: "Detect FPS", field: "detect_fps", min: 1, max: 30, step: 1 },
                                    { label: "Sensitivity", field: "motion_sensitivity", min: 0, max: 1, step: 0.01 },
                                  ].map(s => (
                                    <Box key={s.field}>
                                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography sx={{ fontSize: 10, color: UI.textMuted }}>{s.label}</Typography>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.textMain }}>{getActiveComboClassModel()[s.field]}</Typography>
                                      </Stack>
                                      <Slider size="small" value={getActiveComboClassModel()[s.field]} min={s.min} max={s.max} step={s.step} onChange={(_, v) => updateCurrentComboClassModelField(currentActiveComboClassId, s.field, v)} sx={{ py: 0.5 }} />
                                    </Box>
                                  ))}
                                </Box>

                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.5, mb: 1 }}>
                                  {[
                                    { key: "augment", label: "Augment" },
                                    { key: "agnostic_nms", label: "Agnostic NMS" },
                                    { key: "is_zoom_motion_area", label: "Zoom Motion" },
                                    { key: "enable_detect_always", label: "Always Detect" },
                                    { key: "enable_motion", label: "Motion" },
                                  ].map(opt => (
                                    <FormControlLabel
                                      key={opt.key}
                                      control={<Switch size="small" checked={Boolean(getActiveComboClassModel()[opt.key])} onChange={(e) => updateCurrentComboClassModelField(currentActiveComboClassId, opt.key, e.target.checked)} />}
                                      label={<Typography sx={{ fontSize: 10 }}>{opt.label}</Typography>}
                                      sx={{ ml: 0, mr: 0 }}
                                      labelPlacement="start"
                                    />
                                  ))}
                                </Box>

                                <Divider sx={{ my: 1 }} />
                                <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Model Format</Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                                  {MODEL_FORMATS.map(f => (
                                    <Chip
                                      key={f.key}
                                      label={f.label}
                                      size="small"
                                      onClick={() => updateCurrentComboClassModelField(currentActiveComboClassId, "model_format", f.key)}
                                      sx={{
                                        height: 22, fontSize: 10, fontWeight: 600, cursor: "pointer",
                                        backgroundColor: getActiveComboClassModel().model_format === f.key ? UI.dark : "rgba(145,158,171,0.16)",
                                        color: getActiveComboClassModel().model_format === f.key ? "#fff" : "#919EAB",
                                      }}
                                    />
                                  ))}
                                </Box>

                                <Divider sx={{ my: 1 }} />
                                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>Communication</Typography>
                                  <WifiIcon sx={{ fontSize: 13, color: UI.textMuted }} />
                                </Stack>
                                <Box sx={{ mb: 1 }}>
                                  <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Protocol</Typography>
                                  <Box sx={{ display: "flex", borderRadius: 1, border: `1px solid ${UI.border}`, overflow: "hidden", height: 28 }}>
                                    {["grpc", "http"].map(type => (
                                      <Box
                                        key={type}
                                        onClick={() => updateCurrentComboClassModelField(currentActiveComboClassId, "comm_type", type)}
                                        sx={{
                                          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                          fontSize: 10, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
                                          backgroundColor: getActiveComboClassModel().comm_type === type ? UI.dark : "transparent",
                                          color: getActiveComboClassModel().comm_type === type ? "#fff" : UI.textMuted,
                                          "&:hover": { backgroundColor: getActiveComboClassModel().comm_type === type ? UI.dark : "rgba(0,0,0,0.03)" },
                                        }}
                                      >
                                        {type.toUpperCase()}
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>

                                {getActiveComboClassModel().comm_type === "grpc" ? (
                                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1 }}>
                                    <Box>
                                      <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>IP Address</Typography>
                                      <TextField size="small" fullWidth placeholder={defaultCommIp} value={getActiveComboClassModel().comm_ip} onChange={(e) => updateCurrentComboClassModelField(currentActiveComboClassId, "comm_ip", e.target.value)} error={!isValidIp(getActiveComboClassModel().comm_ip)} InputProps={{ startAdornment: <InputAdornment position="start"><SettingsEthernetIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }} />
                                    </Box>
                                    <Box>
                                      <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Port</Typography>
                                      <TextField size="small" fullWidth placeholder="50051" value={getActiveComboClassModel().comm_port} onChange={(e) => updateCurrentComboClassModelField(currentActiveComboClassId, "comm_port", e.target.value)} error={!isValidPort(getActiveComboClassModel().comm_port)} InputProps={{ startAdornment: <InputAdornment position="start"><NumbersIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }} />
                                    </Box>
                                  </Box>
                                ) : (
                                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1 }}>
                                    <Box>
                                      <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>URL</Typography>
                                      <TextField size="small" fullWidth placeholder="https://api.example.com" value={getActiveComboClassModel().comm_url} onChange={(e) => updateCurrentComboClassModelField(currentActiveComboClassId, "comm_url", e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SettingsEthernetIcon sx={{ fontSize: 12, color: UI.textMuted }} /></InputAdornment> }} />
                                    </Box>
                                    <Box>
                                      <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Path</Typography>
                                      <TextField size="small" fullWidth placeholder="/v1/detect" value={getActiveComboClassModel().comm_path} onChange={(e) => updateCurrentComboClassModelField(currentActiveComboClassId, "comm_path", e.target.value)} />
                                    </Box>
                                  </Box>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Combo Tracking */}
                <Accordion expanded={comboTrackAccOpen} onChange={(_, v) => setComboTrackAccOpen(v)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                      <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>TRACKING</Typography>
                      <Switch
                        size="small"
                        checked={Boolean(getActiveComboSettings().trackingEnabled)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setCurrentComboTrackingEnabled(e.target.checked)}
                        disabled={!currentActiveComboId}
                        sx={{ mr: 0.5 }}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                    {!currentActiveComboId ? (
                      <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", textAlign: "center", py: 1 }}>Select a combo first</Typography>
                    ) : !getActiveComboSettings().trackingEnabled ? (
                      <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", textAlign: "center", py: 1 }}>Tracking disabled</Typography>
                    ) : (
                      <>
                        {/* Text inputs row — label on top, input below */}
                        <Stack direction="row" gap={1} mb={1}>
                          {[
                            { label: "Max Disappear (sec)", field: "track_max_disappeared" },
                            { label: "Min Duration(sec)", field: "min_track_duration_sec" },
                          ].map(s => (
                            <Stack key={s.field} direction="column" flex={1}>
                              <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.3 }}>{s.label}</Typography>
                              <input
                                type="number"
                                value={getActiveComboSettings().tracking?.[s.field]}
                                onChange={(e) => updateCurrentComboTracking(s.field, Number(e.target.value))}
                                style={{ width: "100%", fontSize: 10, textAlign: "center", border: `1px solid ${UI.border ?? "#ccc"}`, borderRadius: 4, padding: "4px 6px", background: "transparent", color: UI.textMain, boxSizing: "border-box" }}
                              />
                            </Stack>
                          ))}
                        </Stack>

                        {/* Slider grid */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, rowGap: 1, mb: 1 }}>
                          {[
                            { label: "Track IoU", field: "track_iou", min: 0.1, max: 1, step: 0.05 },
                            { label: "Tracking FPS", field: "tracking_fps", min: 1, max: 30, step: 1 },
                            { label: "Min Distance", field: "min_track_distance", min: 0, max: 1, step: 0.01 },
                          ].map(s => (
                            <Box key={s.field}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography sx={{ fontSize: 10, color: UI.textMuted }}>{s.label}</Typography>
                                <Typography sx={{ fontSize: 10, fontWeight: 700, color: UI.textMain }}>{getActiveComboSettings().tracking?.[s.field]}</Typography>
                              </Stack>
                              <Slider size="small" value={getActiveComboSettings().tracking?.[s.field]} min={s.min} max={s.max} step={s.step} onChange={(_, v) => updateCurrentComboTracking(s.field, v)} sx={{ py: 0.5 }} />
                            </Box>
                          ))}
                        </Box>

                        <FormControlLabel
                          control={<Switch size="small" checked={Boolean(getActiveComboSettings().tracking?.enable_object_count)} onChange={(e) => updateCurrentComboTracking("enable_object_count", e.target.checked)} />}
                          label={<Typography sx={{ fontSize: 10 }}>Count</Typography>}
                          sx={{ ml: 0, mr: 0, mb: 0.5 }}
                          labelPlacement="start"
                        />

                        {getActiveComboSettings().tracking?.enable_object_count && (
                          <Box sx={{ p: 1, borderRadius: 1.5, border: `1px solid ${UI.border}`, backgroundColor: UI.surface }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 600, color: UI.dark, mb: 0.75 }}>Count Settings</Typography>

                            <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Track Direction</Typography>
                            <Box sx={{ display: "flex", borderRadius: 1, border: `1px solid ${UI.border}`, overflow: "hidden", height: 28, mb: 1 }}>
                              {[
                                { key: "TTB", icon: <ArrowDownwardIcon sx={{ fontSize: 14 }} />, tip: "Top to Bottom" },
                                { key: "BTT", icon: <ArrowUpwardIcon sx={{ fontSize: 14 }} />, tip: "Bottom to Top" },
                                { key: "LTR", icon: <ArrowForwardIcon sx={{ fontSize: 14 }} />, tip: "Left to Right" },
                                { key: "RTL", icon: <ArrowBackIcon sx={{ fontSize: 14 }} />, tip: "Right to Left" },
                              ].map(opt => (
                                <Tooltip key={opt.key} title={opt.tip} placement="top" arrow>
                                  <Box
                                    onClick={() => updateCurrentComboTracking("track_direction", opt.key)}
                                    sx={{
                                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                                      cursor: "pointer", transition: "all 0.15s",
                                      backgroundColor: getActiveComboSettings().tracking?.track_direction === opt.key ? UI.dark : "transparent",
                                      color: getActiveComboSettings().tracking?.track_direction === opt.key ? "#fff" : UI.textMuted,
                                      "&:hover": { backgroundColor: getActiveComboSettings().tracking?.track_direction === opt.key ? UI.dark : "rgba(0,0,0,0.03)" },
                                    }}
                                  >
                                    {opt.icon}
                                  </Box>
                                </Tooltip>
                              ))}
                            </Box>

                            <Typography sx={{ fontSize: 11, color: UI.textMuted, mb: 0.5 }}>Count Boundary</Typography>
                            {(() => {
                              const trackLineIds = getActiveComboSettings().tracking?.comboTrackLineIds || [];
                              const rawLine = getActiveComboSettings().tracking?.rawTrackLine;
                              return (
                                <>
                                  {countableShapesWithLabel.length > 0 ? (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 240 }}>
                                      {countableShapesWithLabel.map((s, sIdx) => {
                                        const active = trackLineIds.includes(s.id);
                                        const chipLabel = active ? `Count Boundary ${sIdx + 1}` : s.label;
                                        return (
                                          <Chip
                                            key={s.id}
                                            label={chipLabel}
                                            size="small"
                                            onClick={() => toggleCurrentComboTrackLine(s.id)}
                                            onMouseEnter={() => setHoveredZoneId(s.id)}
                                            onMouseLeave={() => setHoveredZoneId(null)}
                                            sx={{
                                              height: 20, fontSize: 9, fontWeight: 600, cursor: "pointer",
                                              backgroundColor: active ? "#2563EB" : "rgba(145,158,171,0.16)",
                                              color: active ? "#fff" : "#919EAB",
                                              "&:hover": { backgroundColor: active ? "#3B82F6" : "#D1D5DB" },
                                            }}
                                          />
                                        );
                                      })}
                                    </Box>
                                  ) : (
                                    <Typography sx={{ fontSize: 10, color: UI.textMuted, fontStyle: "italic" }}>
                                      Draw shapes on canvas to assign as count boundary.
                                    </Typography>
                                  )}
                                  {rawLine && trackLineIds.length === 0 && (
                                    <Chip
                                      size="small"
                                      label="Applied (from API)"
                                      sx={{ height: 20, fontSize: 9, fontWeight: 600, mt: 0.5, backgroundColor: "#2563EB", color: "#fff" }}
                                    />
                                  )}
                                  {trackLineIds.length === 0 && !rawLine && countableShapesWithLabel.length > 0 && (
                                    <Typography sx={{ fontSize: 9, color: UI.textMuted, fontStyle: "italic", mt: 0.5 }}>
                                      No boundary assigned
                                    </Typography>
                                  )}
                                </>
                              );
                            })()}
                          </Box>
                        )}

                      </>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Combo Monitoring */}
                <Accordion expanded={comboMonAccOpen} onChange={(_, v) => setComboMonAccOpen(v)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                      <Typography variant="caption" fontWeight={600} sx={{ letterSpacing: 0.5 }}>MONITORING TIMES</Typography>
                      {currentActiveComboId && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setComboMonAccOpen(true); addCurrentComboTimeRange(); setTimeout(() => rightPanelRef.current?.scrollTo({ top: rightPanelRef.current.scrollHeight, behavior: "smooth" }), 50); }} sx={{ p: 0.25, mr: 0.5 }}>
                          <AddIcon sx={{ fontSize: 14, color: UI.accent }} />
                        </IconButton>
                      )}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 1.5 }}>
                    {!currentActiveComboId ? (
                      <Typography variant="caption" sx={{ color: UI.textMuted, display: "block", textAlign: "center", py: 1 }}>Select a combo first</Typography>
                    ) : (
                      <>
                        {getCurrentComboSchedule().length === 0 && (
                          <Stack alignItems="center" spacing={0.5} sx={{ py: 2 }}>
                            <AccessTimeIcon sx={{ fontSize: 22, color: UI.textMuted, opacity: 0.5 }} />
                            <Typography variant="caption" sx={{ color: UI.textMain, fontWeight: 600, textAlign: "center" }}>Monitoring 24/7</Typography>
                            <Typography variant="caption" sx={{ color: UI.textMuted, fontSize: 10, textAlign: "center" }}>No times configured — add a range to limit when this runs.</Typography>
                          </Stack>
                        )}

                        {getCurrentComboSchedule().map((range, idx) => (
                          <Box key={idx} sx={{ mb: 1.5, p: 1.5, border: `1px solid ${UI.border}`, borderRadius: 2, backgroundColor: UI.panel }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                              <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Range {idx + 1}</Typography>
                              <IconButton size="small" onClick={() => removeCurrentComboTimeRange(idx)} sx={{ p: 0.25 }}>
                                <DeleteIcon sx={{ fontSize: 13, color: UI.textMuted }} />
                              </IconButton>
                            </Stack>

                            <Stack direction="row" spacing={1} mb={1}>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.25 }}>From</Typography>
                                <TimeInput24 value={range.startTime} onChange={(v) => updateCurrentComboTimeRange(idx, "startTime", v)} borderColor={UI.border} bgColor={UI.surface} textColor={UI.textMain} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: 10, color: UI.textMuted, mb: 0.25 }}>To</Typography>
                                <TimeInput24 value={range.endTime} onChange={(v) => updateCurrentComboTimeRange(idx, "endTime", v)} borderColor={UI.border} bgColor={UI.surface} textColor={UI.textMain} />
                              </Box>
                            </Stack>

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {DAYS.map(day => (
                                <Chip
                                  key={day}
                                  label={day}
                                  size="small"
                                  onClick={() => toggleCurrentComboDay(idx, day)}
                                  sx={{
                                    cursor: "pointer", fontSize: 10, height: 22, fontWeight: 600,
                                    backgroundColor: range.days.includes(day) ? UI.accent : "transparent",
                                    color: range.days.includes(day) ? "#fff" : UI.textMuted,
                                    border: `1px solid ${range.days.includes(day) ? UI.accent : UI.border}`,
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>
                </>}

                </>)}
                <Box sx={{ pb: 6 }} />
                </Paper>
              </Box>
            </Grid>

          </Grid>
        </Box>
      <Snackbar open={snack.open} autoHideDuration={snack.severity === "warning" ? 5000 : 3000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ fontSize: 12, fontWeight: 500, ...(snack.severity === "success" && { backgroundColor: "#81C784", color: "#1b5e20" }), ...(snack.severity === "warning" && { backgroundColor: "#FFB74D", color: "#4E342E" }) }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
